import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Slider,
  Card,
  Grid,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useProfile } from '../../context/ProfileContext';
import type { CalculatorProfile } from '../../services/profileService';
import { calculateAssessableIncome } from '../../utils/haircutFormulas';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { exportAffordabilityToPDF, type AffordabilityPdfTypeReport } from '../../utils/affordabilityPdfExport';
import {
  HDB_EXERCISE_FEE,
  HDB_OPTION_FEE,
  OTP_STAGE_PCT,
  SP_STAGE_PCT,
  getClosingFees,
  getDownPaymentPlan,
} from '../../utils/affordabilityDownPayment';
import { logPdfExport, logCalculatorInteraction } from '../../services/analytics';
import {
  SHARED_CALCULATOR_EXAMPLES,
  buildLoadedProfileHistoryLabel,
  type SharedCalculatorActiveSelection,
  type SharedCalculatorProfile,
} from '../../hooks/useSharedProfileHistory';
import {
  useLinkedCalculator,
  hasPendingHistoryLoad,
  beginProfileSaveBack,
  endProfileSaveBack,
  isProfileSaveBackInProgress,
} from '../../context/LinkedCalculatorContext';
import HistoryToolbar, { type NewProfileDraft } from '../../components/HistoryToolbar';
import TextSizeControl from '../../components/TextSizeControl';
import ProfileBanner from '../../components/ProfileBanner';
import CurrencyTextField from '../../components/CurrencyTextField';
import {
  calculateBsd,
  getBsdBreakdown,
  getAbsdRate,
  getJointAbsdRate,
  getEffectiveCitizenshipForAbsd,
  ABSD_RATES,
  CITIZENSHIP_LABELS,
  PROPERTY_COUNT_OPTIONS,
  FTA_ABSD_INFO_TOOLTIP,
  type CitizenshipType,
  type PropertyCount,
} from '../../../utils/stampDutyRates';
import { useGlobalCalculatorSettings } from '../../context/GlobalCalculatorSettingsContext';
import {
  EC_INCOME_CEILING,
  HDB_CONCESSIONARY_INTEREST_RATE,
  HDB_LOAN_INCOME_CEILING_FAMILY,
  HDB_LOAN_INCOME_CEILING_SINGLE,
  HDB_LOAN_LTV,
  HDB_LOAN_MAX_AGE,
  HDB_LOAN_MAX_TENURE,
  assessEcEligibility,
  assessHdbFlatEligibility,
  assessHdbLoanEligibility,
  type EligibilityInput,
} from '../../utils/housingEligibility';
import {
  CalculatorTutorial,
  TutorialLaunchButton,
  useCalculatorTutorial,
  type TutorialStepDef,
} from '../../components/CalculatorTutorial';
import WhatsNewDialog, {
  WhatsNewLaunchButton,
  useWhatsNew,
} from '../../components/WhatsNewDialog';
import { MAXIMIZER_WHATS_NEW } from '../../utils/maximizerWhatsNew';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BorrowerState {
  /** Borrower's name — seeded from the loaded profile, and the name used when
   *  saving these inputs as a new profile. */
  name?: string;
  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
  /** This borrower's own cash savings — summed into Available Funds → Cash. */
  cash: number;
  /** This borrower's own CPF OA balance — summed into Available Funds → CPF. */
  cpf: number;
}

const EMPTY_BORROWER_FUNDS = { cash: 0, cpf: 0 };

/** Tolerates borrowers restored from snapshots saved before per-borrower funds existed. */
const normalizeBorrower = (raw: unknown): BorrowerState => {
  const b = (raw ?? {}) as Partial<BorrowerState>;
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0);
  return {
    age: num(b.age) || 30,
    monthlyIncome: num(b.monthlyIncome),
    otherAnnualIncome: num(b.otherAnnualIncome),
    isSelfEmployed: !!b.isSelfEmployed,
    existingMonthlyDebt: num(b.existingMonthlyDebt),
    name: typeof b.name === 'string' && b.name.trim() ? b.name : undefined,
    cash: num(b.cash),
    cpf: num(b.cpf),
  };
};

/** Cash/CPF the buyer can put on the table today — drives "Affordable Today".
 *  `cash` and `cpf` are derived: they are the sum of what each borrower declared
 *  on their own card. Only the two "additional" rows are entered here. */
interface AvailableFundsState {
  /** Cash savings on hand — sum of the borrowers' own Cash / Saving. */
  cash: number;
  /** Extra cash expected before completion (e.g. net sale proceeds). */
  additionalCash: number;
  /** CPF OA balance usable for the purchase — sum of the borrowers' own CPF OA. */
  cpf: number;
  /** Extra CPF expected (CPF refund from a sale, withdrawals). */
  additionalCpf: number;
}

interface InputState {
  primary: BorrowerState;
  includeAdditionalBorrower: boolean;
  additional: BorrowerState;
  tenure: number;
  bankInterestRate: number;
  ltv: 75 | 55;
  proposedPropertyPrice: number;
  availableFunds: AvailableFundsState;
  /** HDB column: HDB concessionary loan or a bank loan. */
  hdbLoanSource: HdbLoanSource;
}

type HdbLoanSource = 'hdb' | 'bank';

/** Whether the buyer may buy a property type, and why not. */
interface TypeEligibility {
  eligible: boolean;
  /** Blocking reasons — any one makes the type ineligible. */
  reasons: string[];
  /** Advisories the inputs can't verify. */
  notes: string[];
}

const createDefaultAvailableFunds = (): AvailableFundsState => ({
  cash: 0,
  additionalCash: 0,
  cpf: 0,
  additionalCpf: 0,
});

/** Tolerates snapshots saved before Available Funds existed (all fields → 0). */
const normalizeAvailableFunds = (raw: unknown): AvailableFundsState => {
  const candidate = (raw ?? {}) as Partial<AvailableFundsState>;
  const num = (v: unknown): number =>
    typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0;
  return {
    cash: num(candidate.cash),
    additionalCash: num(candidate.additionalCash),
    cpf: num(candidate.cpf),
    additionalCpf: num(candidate.additionalCpf),
  };
};

const createDefaultInputs = (): InputState => ({
  primary: { age: 30, monthlyIncome: 8000, otherAnnualIncome: 0, isSelfEmployed: false, existingMonthlyDebt: 0, ...EMPTY_BORROWER_FUNDS },
  includeAdditionalBorrower: false,
  additional: { age: 30, monthlyIncome: 0, otherAnnualIncome: 0, isSelfEmployed: false, existingMonthlyDebt: 0, ...EMPTY_BORROWER_FUNDS },
  tenure: 30,
  bankInterestRate: 2.0,
  ltv: 75,
  proposedPropertyPrice: 1500000,
  availableFunds: createDefaultAvailableFunds(),
  hdbLoanSource: 'hdb',
});

/**
 * One MSR column (EC / HDB) — MSR on full recognised income, then the lower of MSR and TDSR.
 * The income ceiling is an eligibility test (new EC / HDB loan), not a cap on the income MSR counts.
 */
interface MsrColumn {
  /** Gross monthly household income ceiling for this property type. */
  incomeCeiling: number;
  /** Gross household income is above the ceiling: EC → not eligible for a new EC; HDB → bank loan only. */
  aboveCeiling: boolean;
  grossMSR: number;
  paymentCap: number;
  msrBinding: boolean;
  loanTenure: number;
  maxLoan: number;
  maxPropertyPrice: number;
  affordableToday: number;
}

/**
 * How one property type's loan is sized. Private and EC follow the inputs (bank loan);
 * HDB follows them too on a bank loan, or HDB's own terms on an HDB concessionary loan.
 */
interface LoanBasis {
  /** Applied LTV, %. */
  ltv: number;
  /** Rate the max loan and the income support are assessed at. */
  stressRate: number;
  stressRateLabel: string;
  /** Rate the actual instalment accrues at. */
  interestRate: number;
  /** Cash-only minimum share of the price (0 on an HDB loan — CPF can pay it all). */
  cashPercent: number;
  /** Age the loan must be repaid by. */
  maxAge: number;
  /** Max tenure this type allows at its LTV. */
  maxTenure: number;
  hdbLoan: boolean;
}

/** Selected-property figures for one property type — drives every section below the overview. */
interface TypeOutcome {
  /** Loan the selected property needs at this type's LTV. */
  requiredLoan: number;
  maxLoan: number;
  maxPropertyPrice: number;
  loanTenure: number;
  /** Monthly instalment the max loan is sized on (Available TDSR, or the lower of MSR / TDSR). */
  instalmentCap: number;
  loanShortfall: number;
  /** Instalment on the required loan at the bank rate. */
  monthlyMortgageRequired: number;
  /** Instalment on the required loan at the stress-test rate — what TDSR / MSR assess. */
  stressInstalment: number;
  /** Extra recognised income needed to pass each ratio (MSR only for EC / HDB). */
  tdsrExtraIncome: number;
  msrExtraIncome: number;
  extraMonthlyIncomeNeeded: number;
  /** Ratio that needs the larger income top-up — it sizes the income support. */
  supportRule: 'TDSR' | 'MSR';
  pledgedFundsNeeded: number;
  unpledgedFundsNeeded: number;
  minimumCashNeeded: number;
}

interface Results {
  maxPropertyPrice: number;
  maxLoan: number;
  appliedLtv: number;
  grossTDSR: number;
  availableTDSR: number;
  /** EC: MSR on full income, same tenure as private property; flagged above the EC ceiling. */
  ec: MsrColumn;
  /** HDB: MSR on full income, shorter HDB tenure; sized on an HDB loan or a bank loan. */
  hdb: MsrColumn;
  /** LTV, stress rate, cash minimum and tenure caps per property type. */
  basis: Record<OverviewType, LoanBasis>;
  /** Selected-property outcome per property type; the top-level loan / support fields mirror `byType.private`. */
  byType: Record<OverviewType, TypeOutcome>;
  totalRecognisedIncome: number;
  totalRawIncome: number;
  totalExistingDebt: number;
  effectiveAgeUsed: number;
  borrowerCount: number;
  iwaa: number;
  propertyPrice: number;
  requiredLoan: number;
  loanShortfall: number;
  pledgedFundsNeeded: number;
  unpledgedFundsNeeded: number;
  minimumCashNeeded: number;
  monthlyMortgageRequired: number;
  effectiveLoanTenure: number;
  ageBasedCap: number;
  cashAvailable: number;
  cpfAvailable: number;
  totalFundsAvailable: number;
  affordableToday: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TDSR_RATIO = 0.55;
const MSR_RATIO = 0.30;
const SELF_EMPLOYED_HAIRCUT = 0.70; // 30% haircut → 70% recognition
const OTHER_ANNUAL_HAIRCUT = 0.70; // Variable income (commission/bonus/OT): 30% haircut → 70% recognition

/** MAS-style caps depend on applied LTV (75%: 30 yrs / age 65; 55%: 35 yrs / age 75). */
function getMaxAgeForLtv(ltv: 75 | 55 | number | string): number {
  return Number(ltv) === 55 ? 75 : 65;
}

function getMaxTenureForLtv(ltv: 75 | 55 | number | string): number {
  return Number(ltv) === 55 ? 35 : 30;
}

/** HDB flats cap tenure 5 yrs below private / EC: 25 yrs at 75% LTV, 30 yrs at 55% LTV. */
function getMaxHdbTenureForLtv(ltv: 75 | 55 | number | string): number {
  return Number(ltv) === 55 ? 30 : 25;
}
/** MAS minimum cash: 5% at 75% LTV (first loan); 10% at 55% LTV (subsequent). */
function getCashRequirementPercent(ltv: 75 | 55): number {
  return ltv === 55 ? 0.1 : 0.05;
}
const PLEDGED_RECOGNITION = 1.0;
const UNPLEDGED_RECOGNITION = 0.30;
const RECOGNITION_MONTHS = 48;
const MOBILE_SLIDER_INTENT_THRESHOLD_PX = 8;
const PROPERTY_PRICE_MAX = 20000000;
const MONTHLY_INCOME_DEFAULT_MAX = 50000;
const OTHER_ANNUAL_INCOME_DEFAULT_MAX = 200000;
const EXISTING_DEBT_DEFAULT_MAX = 20000;
const INCOME_ABSOLUTE_MAX = 999999999;
const AGE_MAX = 99;

/** Property type column in the Affordability Overview (also what a Reverse Mode target is set on). */
type OverviewType = 'private' | 'ec' | 'hdb';
const OVERVIEW_TYPES: OverviewType[] = ['private', 'ec', 'hdb'];
const OVERVIEW_TYPE_LABELS: Record<OverviewType, string> = { private: 'Private', ec: 'EC', hdb: 'HDB' };

const RELATIONSHIP_LABELS: Record<string, string> = {
  SPOUSE: 'Married Couple',
  INDIVIDUAL: 'Individual',
};

/** Profile DB: 1 / 2 / 3 (or 0 legacy) → ABSD property band */
function propertiesOwnedToPropertyCount(propertiesOwned: number | null | undefined): PropertyCount {
  if (propertiesOwned === 2) return '2nd';
  if (propertiesOwned === 3) return '3rd+';
  return '1st';
}

function propertyCountToPropertiesOwned(count: PropertyCount): number {
  if (count === '2nd') return 2;
  if (count === '3rd+') return 3;
  return 1;
}

/** Snap numeric value to slider step grid within [min, max]. */
function snapToStep(value: number, min: number, max: number, step: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  if (step <= 0) return clamped;
  const n = Math.round((clamped - min) / step);
  let snapped = min + n * step;
  const stepStr = step.toString();
  const dot = stepStr.indexOf('.');
  const decimals = dot === -1 ? 0 : stepStr.length - dot - 1;
  if (decimals > 0) {
    snapped = Math.round(snapped * 10 ** decimals) / 10 ** decimals;
  }
  return Math.min(max, Math.max(min, snapped));
}

function parseFlexibleNumber(s: string): number | null {
  const cleaned = s.replace(/,/g, '').replace(/\s/g, '').trim();
  if (cleaned === '' || cleaned === '-') return null;
  const n = parseFloat(cleaned.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function formatDraftForEditing(v: number, step: number): string {
  if (step < 1 && step > 0) return String(Number(v.toFixed(2)));
  return String(Math.round(v));
}

/** SGD display for manual currency entry (matches fmt() in calculator). */
function formatCurrencyDraftForInput(amount: number): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Label row + edit icon + optional inline TextField; commits snap to step and update slider state. */
const EditableSliderValueRow: React.FC<{
  label: React.ReactNode;
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (v: number) => void;
  formatDisplay: (v: number) => string;
  valueColor: string;
  disabled?: boolean;
  /** When true, the text field shows $ and thousands separators while typing. */
  currencyInput?: boolean;
  commitMax?: number;
}> = ({ label, value, min, max, step, onCommit, formatDisplay, valueColor, disabled = false, currencyInput = false, commitMax }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const skipBlurCommitRef = useRef(false);

  const commit = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    const raw = parseFlexibleNumber(draft);
    if (raw === null) {
      setEditing(false);
      return;
    }
    const snapped = snapToStep(raw, min, commitMax ?? max, step);
    onCommit(snapped);
    setEditing(false);
  };

  const startEdit = () => {
    if (disabled) return;
    skipBlurCommitRef.current = false;
    setDraft(
      currencyInput ? formatCurrencyDraftForInput(Math.round(value)) : formatDraftForEditing(value, step)
    );
    setEditing(true);
  };

  const cancel = () => {
    skipBlurCommitRef.current = true;
    setEditing(false);
  };

  const handleDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currencyInput) {
      const digits = e.target.value.replace(/\D/g, '');
      if (digits === '') {
        setDraft('');
        return;
      }
      const n = parseInt(digits, 10);
      if (!Number.isFinite(n)) return;
      setDraft(formatCurrencyDraftForInput(n));
      return;
    }
    setDraft(e.target.value);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1, flexWrap: 'wrap', opacity: disabled ? 0.6 : 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
      {editing ? (
        <TextField
          size="small"
          value={draft}
          onChange={handleDraftChange}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          autoFocus
          sx={{ width: currencyInput ? { xs: 150, sm: 180 } : { xs: 130, sm: 150 } }}
          inputProps={{ inputMode: currencyInput ? 'numeric' : 'decimal' }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: valueColor }}>
            {formatDisplay(value)}
          </Typography>
          <IconButton size="small" aria-label="Type value manually" onClick={startEdit} disabled={disabled} sx={{ p: 0.35, ml: 0.25 }}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// The guard activates on small viewports (isMobile) and on any coarse-pointer device,
// so tablets are covered at every orientation/width; only touch-originated changes are
// gated, so mouse and keyboard input always passes through.
const IntentGuardedSlider: React.FC<React.ComponentProps<typeof Slider> & { isMobile: boolean }> = ({
  isMobile,
  onChange,
  sx,
  disabled,
  ...props
}) => {
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const guarded = isMobile || isCoarsePointer;
  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    intent: 'pending' | 'horizontal' | 'vertical';
  } | null>(null);
  const [dragReady, setDragReady] = useState(!guarded);

  useEffect(() => {
    setDragReady(!guarded);
    if (!guarded) {
      touchStateRef.current = null;
    }
  }, [guarded]);

  const resetTouch = () => {
    touchStateRef.current = null;
    if (guarded) setDragReady(false);
  };

  const handleTouchStart: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const touch = event.touches[0];
    touchStateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      intent: 'pending',
    };
    setDragReady(false);
  };

  const handleTouchMove: React.TouchEventHandler<HTMLSpanElement> = (event) => {
    if (!guarded || disabled) return;
    const state = touchStateRef.current;
    if (!state) return;

    const touch = event.touches[0];
    const dx = Math.abs(touch.clientX - state.startX);
    const dy = Math.abs(touch.clientY - state.startY);

    if (state.intent === 'pending') {
      if (dx < MOBILE_SLIDER_INTENT_THRESHOLD_PX && dy < MOBILE_SLIDER_INTENT_THRESHOLD_PX) return;
      if (dx > dy) {
        state.intent = 'horizontal';
        setDragReady(true);
      } else {
        state.intent = 'vertical';
        setDragReady(false);
      }
      touchStateRef.current = state;
    }
  };

  const handleChange: NonNullable<React.ComponentProps<typeof Slider>['onChange']> = (
    event,
    value,
    activeThumb
  ) => {
    if (!onChange) return;
    if (!guarded) {
      onChange(event, value, activeThumb);
      return;
    }
    const isTouchEvent = typeof event?.type === 'string' && event.type.startsWith('touch');
    if (isTouchEvent && touchStateRef.current?.intent !== 'horizontal') return;
    onChange(event, value, activeThumb);
  };

  return (
    <Slider
      {...props}
      disabled={disabled}
      onChange={handleChange}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={resetTouch}
      onTouchCancel={resetTouch}
      sx={[
        {
          touchAction: guarded ? 'pan-y' : 'auto',
          '& .MuiSlider-thumb': guarded
            ? {
                transition: 'transform 120ms ease',
                transform: dragReady
                  ? 'translate(-50%, -50%) scale(1.05)'
                  : 'translate(-50%, -50%) scale(1)',
              }
            : undefined,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};

// ─── Calculation Helpers ─────────────────────────────────────────────────────

function calculateMaxLoan(monthlyPayment: number, annualRate: number, tenureYears: number): number {
  if (monthlyPayment <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return monthlyPayment * n;
  const factor = Math.pow(1 + r, n);
  return monthlyPayment * (factor - 1) / (r * factor);
}

function calculateMonthlyPayment(principal: number, annualRate: number, tenureYears: number): number {
  if (principal <= 0 || tenureYears <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = tenureYears * 12;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return principal * (r * factor) / (factor - 1);
}

/**
 * Highest whole-dollar property price P the buyer can close on **today**, given
 * the loan the bank will grant and the cash/CPF already in hand. Mirrors the
 * Purchase Planner's "Affordable Today" column — all three conditions must hold
 * at once:
 *
 *   1. P ≤ maxLoan + cash + CPF                   (absolute resource cap)
 *   2. cashRequirementPercent × P ≤ cash          (cash-only minimum tranche:
 *                                                  5% at 75% LTV, 10% at 55%)
 *   3. Downpayment(P) + BSD(P) + ABSD(P) ≤ cash + CPF
 *      where Downpayment(P) = P − min(maxLoan, ltvRatio × P)
 *
 * Every cost function is monotonically increasing in P, so binary search finds
 * the maximum feasible price.
 */
function calculateAffordableToday(
  maxLoan: number,
  ltvRatio: number,
  cashAvailable: number,
  cpfAvailable: number,
  cashRequirementPercent: number,
  absdRate: number,
): number {
  const totalCapital = cashAvailable + cpfAvailable;
  if (totalCapital <= 0) return 0;

  const satisfies = (p: number): boolean => {
    // Condition 2: cash-only minimum downpayment tranche
    if (cashRequirementPercent * p > cashAvailable) return false;

    // Condition 3: total upfront cost within combined capital
    const loanAmount = Math.min(maxLoan, ltvRatio * p);
    const downpayment = p - loanAmount;
    const bsd = calculateBsd(p);
    const absd = Math.round(p * (absdRate / 100));
    return downpayment + bsd + absd <= totalCapital;
  };

  let lo = 0;
  let hi = Math.round(maxLoan + totalCapital); // absolute upper bound (condition 1)

  while (lo < hi) {
    const mid = Math.floor((lo + hi + 1) / 2);
    if (satisfies(mid)) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

function getRecognisedIncome(borrower: BorrowerState): number {
  const base = borrower.isSelfEmployed
    ? borrower.monthlyIncome * SELF_EMPLOYED_HAIRCUT
    : borrower.monthlyIncome;
  const otherMonthly = (borrower.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;
  return base + otherMonthly;
}

function computeResults(
  inputs: InputState,
  bankRate: number,
  stressTestRate: number,
  absdRate: number = 0,
  /** HDB's own floor for an HDB concessionary loan (Settings); the MAS 4% floor doesn't apply. */
  hdbStressTestRate: number = stressTestRate,
): Results {
  const { primary, includeAdditionalBorrower, additional, tenure, ltv, proposedPropertyPrice } = inputs;
  const availableFunds = inputs.availableFunds ?? createDefaultAvailableFunds();
  const ltvDecimal = ltv / 100;

  // Income calculations
  const primaryRecognised = getRecognisedIncome(primary);
  const additionalRecognised = includeAdditionalBorrower ? getRecognisedIncome(additional) : 0;
  const totalRecognisedIncome = primaryRecognised + additionalRecognised;
  const totalRawIncome = primary.monthlyIncome + primary.otherAnnualIncome / 12
    + (includeAdditionalBorrower ? additional.monthlyIncome + additional.otherAnnualIncome / 12 : 0);

  // Debt
  const totalExistingDebt = primary.existingMonthlyDebt + (includeAdditionalBorrower ? additional.existingMonthlyDebt : 0);

  // IWAA (Income Weighted Average Age)
  const borrowerCount = includeAdditionalBorrower ? 2 : 1;
  let iwaa: number;
  if (!includeAdditionalBorrower) {
    iwaa = primary.age;
  } else {
    const totalIncome = primaryRecognised + additionalRecognised;
    iwaa = totalIncome > 0
      ? Math.ceil((primary.age * primaryRecognised + additional.age * additionalRecognised) / totalIncome)
      : primary.age;
  }

  const effectiveAgeUsed = iwaa;

  // Effective tenure (LTV-specific max tenure and max loan repayment age)
  const maxAgeForLtv = getMaxAgeForLtv(ltv);
  const maxTenureForLtv = getMaxTenureForLtv(ltv);
  const ageBasedCap = Math.max(0, maxAgeForLtv - effectiveAgeUsed);
  const effectiveLoanTenure = Math.min(tenure, ageBasedCap, maxTenureForLtv);

  // TDSR
  const grossTDSR = totalRecognisedIncome * TDSR_RATIO;
  const availableTDSR = Math.max(0, grossTDSR - totalExistingDebt);

  // Max loan from available TDSR (stress test rate, shared calculator setting — not the bank’s offered rate)
  const maxLoan = Math.round(calculateMaxLoan(availableTDSR, stressTestRate, effectiveLoanTenure));
  const maxPropertyPrice = ltvDecimal > 0 ? Math.round(maxLoan / ltvDecimal) : 0;


  // Proposed property
  const requiredLoan = Math.round(proposedPropertyPrice * ltvDecimal);
  const cashRequirementPercent = getCashRequirementPercent(ltv);

  // Funds already in hand → highest price closeable today (loan + cash + CPF,
  // after downpayment, BSD and ABSD).
  const cashAvailable = availableFunds.cash + availableFunds.additionalCash;
  const cpfAvailable = availableFunds.cpf + availableFunds.additionalCpf;
  const affordableToday = calculateAffordableToday(
    maxLoan,
    ltvDecimal,
    cashAvailable,
    cpfAvailable,
    cashRequirementPercent,
    absdRate,
  );

  // MSR (EC / HDB): 30% of full recognised income; TDSR still applies on top. The income
  // ceiling only decides eligibility (gross household income) — it never caps the MSR income.
  const grossMSR = totalRecognisedIncome * MSR_RATIO;
  const bankBasis: LoanBasis = {
    ltv,
    stressRate: stressTestRate,
    stressRateLabel: 'Stress Test Rate',
    interestRate: bankRate,
    cashPercent: cashRequirementPercent,
    maxAge: maxAgeForLtv,
    maxTenure: maxTenureForLtv,
    hdbLoan: false,
  };
  // HDB loan: HDB's terms (75% LTV, 25 yrs, repaid by 65, no cash minimum, 2.6% rate
  // assessed at HDB's floor). Bank loan on an HDB flat: the bank basis with tenure 5 yrs shorter.
  const hdbBasis: LoanBasis = inputs.hdbLoanSource === 'bank'
    ? { ...bankBasis, maxTenure: getMaxHdbTenureForLtv(ltv) }
    : {
        ltv: HDB_LOAN_LTV,
        stressRate: hdbStressTestRate,
        stressRateLabel: 'HDB Interest Rate Floor',
        interestRate: HDB_CONCESSIONARY_INTEREST_RATE,
        cashPercent: 0,
        maxAge: HDB_LOAN_MAX_AGE,
        maxTenure: HDB_LOAN_MAX_TENURE,
        hdbLoan: true,
      };
  const buildMsrColumn = (incomeCeiling: number, loanTenure: number, basis: LoanBasis): MsrColumn => {
    const paymentCap = Math.min(grossMSR, availableTDSR);
    const msrMaxLoan = Math.round(calculateMaxLoan(paymentCap, basis.stressRate, loanTenure));
    const basisLtv = basis.ltv / 100;
    return {
      incomeCeiling,
      aboveCeiling: totalRawIncome > incomeCeiling,
      grossMSR: Math.round(grossMSR),
      paymentCap: Math.round(paymentCap),
      msrBinding: grossMSR < availableTDSR,
      loanTenure,
      maxLoan: msrMaxLoan,
      maxPropertyPrice: basisLtv > 0 ? Math.round(msrMaxLoan / basisLtv) : 0,
      affordableToday: calculateAffordableToday(
        msrMaxLoan,
        basisLtv,
        cashAvailable,
        cpfAvailable,
        basis.cashPercent,
        absdRate,
      ),
    };
  };
  const ec = buildMsrColumn(EC_INCOME_CEILING, effectiveLoanTenure, bankBasis);
  const hdb = buildMsrColumn(
    includeAdditionalBorrower ? HDB_LOAN_INCOME_CEILING_FAMILY : HDB_LOAN_INCOME_CEILING_SINGLE,
    Math.min(tenure, Math.max(0, hdbBasis.maxAge - effectiveAgeUsed), hdbBasis.maxTenure),
    hdbBasis,
  );

  // Selected property against each type's max loan. Income support is assessed at the
  // stress-test rate (same basis as max loan), not the bank's offered rate. Every ratio that
  // applies must pass, so the larger top-up wins — TDSR (55%, net of existing debt) and, for
  // EC / HDB, MSR (30% of income; property loan only). Same rule as the
  // Buying Calculator's extraIncomeNeeded.
  const buildOutcome = (
    typeMaxLoan: number,
    typeMaxPropertyPrice: number,
    loanTenure: number,
    instalmentCap: number,
    grossMSR: number | null,
    typeMinimumCash: number,
    basis: LoanBasis,
  ): TypeOutcome => {
    const typeRequiredLoan = Math.round(proposedPropertyPrice * (basis.ltv / 100));
    const stressInstalment = calculateMonthlyPayment(typeRequiredLoan, basis.stressRate, loanTenure);
    const tdsrExtraIncome = Math.max(0, stressInstalment - availableTDSR) / TDSR_RATIO;
    const msrExtraIncome = grossMSR === null ? 0 : Math.max(0, stressInstalment - grossMSR) / MSR_RATIO;
    const extraMonthlyIncomeNeeded = Math.max(tdsrExtraIncome, msrExtraIncome);
    const pledgedFundsNeeded = Math.round(extraMonthlyIncomeNeeded * RECOGNITION_MONTHS / PLEDGED_RECOGNITION);
    return {
      requiredLoan: typeRequiredLoan,
      maxLoan: typeMaxLoan,
      maxPropertyPrice: typeMaxPropertyPrice,
      loanTenure,
      instalmentCap: Math.round(instalmentCap),
      loanShortfall: Math.max(0, typeRequiredLoan - typeMaxLoan),
      monthlyMortgageRequired: Math.round(calculateMonthlyPayment(typeRequiredLoan, basis.interestRate, loanTenure)),
      stressInstalment: Math.round(stressInstalment),
      tdsrExtraIncome: Math.round(tdsrExtraIncome),
      msrExtraIncome: Math.round(msrExtraIncome),
      extraMonthlyIncomeNeeded: Math.round(extraMonthlyIncomeNeeded),
      supportRule: msrExtraIncome > tdsrExtraIncome ? 'MSR' : 'TDSR',
      pledgedFundsNeeded,
      unpledgedFundsNeeded: UNPLEDGED_RECOGNITION > 0 ? Math.round(pledgedFundsNeeded / UNPLEDGED_RECOGNITION) : 0,
      minimumCashNeeded: typeMinimumCash,
    };
  };
  const minimumCashNeeded = Math.round(proposedPropertyPrice * cashRequirementPercent);
  // HDB resale: the OTP deposit is paid in cash. On a bank loan it counts towards the bank's
  // minimum cash; on an HDB loan it is the only cash needed.
  const hdbOtpDeposit = Math.min(proposedPropertyPrice, HDB_OPTION_FEE + HDB_EXERCISE_FEE);
  const hdbMinimumCash = Math.max(Math.round(proposedPropertyPrice * hdbBasis.cashPercent), hdbOtpDeposit);
  const byType: Record<OverviewType, TypeOutcome> = {
    private: buildOutcome(maxLoan, maxPropertyPrice, effectiveLoanTenure, availableTDSR, null, minimumCashNeeded, bankBasis),
    ec: buildOutcome(ec.maxLoan, ec.maxPropertyPrice, ec.loanTenure, ec.paymentCap, grossMSR, minimumCashNeeded, bankBasis),
    hdb: buildOutcome(hdb.maxLoan, hdb.maxPropertyPrice, hdb.loanTenure, hdb.paymentCap, grossMSR, hdbMinimumCash, hdbBasis),
  };

  return {
    maxPropertyPrice,
    maxLoan,
    appliedLtv: ltv,
    grossTDSR: Math.round(grossTDSR),
    availableTDSR: Math.round(availableTDSR),
    ec,
    hdb,
    basis: { private: bankBasis, ec: bankBasis, hdb: hdbBasis },
    byType,
    totalRecognisedIncome: Math.round(totalRecognisedIncome),
    totalRawIncome: Math.round(totalRawIncome),
    totalExistingDebt: Math.round(totalExistingDebt),
    effectiveAgeUsed,
    borrowerCount,
    iwaa,
    propertyPrice: proposedPropertyPrice,
    requiredLoan,
    loanShortfall: byType.private.loanShortfall,
    pledgedFundsNeeded: byType.private.pledgedFundsNeeded,
    unpledgedFundsNeeded: byType.private.unpledgedFundsNeeded,
    minimumCashNeeded,
    monthlyMortgageRequired: byType.private.monthlyMortgageRequired,
    effectiveLoanTenure,
    ageBasedCap,
    cashAvailable,
    cpfAvailable,
    totalFundsAvailable: cashAvailable + cpfAvailable,
    affordableToday,
  };
}

// ─── Subcomponents (defined outside to preserve stable references) ────────────

/** Header + result cards per column in the Affordability Overview; RuleSection spans this many subgrid rows. */
const RULE_SECTION_ROWS = 7;

/** One property type (Private / EC / HDB) in the Affordability Overview — rows share a subgrid so they line up across columns. */
/** Reasons / advisories listed in a column header. */
const RuleSectionNotice = ({ tone, heading, items }: { tone: 'error' | 'warning'; heading: string; items: string[] }) => (
  <Box
    sx={{
      mt: 1,
      px: { xs: 0.5, sm: 1 },
      py: { xs: 0.5, sm: 0.75 },
      borderRadius: 1,
      bgcolor: (t) => alpha(t.palette[tone].main, 0.08),
      border: 1,
      borderColor: (t) => alpha(t.palette[tone].main, 0.45),
    }}
  >
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: `${tone}.main`, fontSize: { xs: '0.6rem', sm: '0.72rem' } }}>
      {heading}
    </Typography>
    {items.map((item) => (
      <Typography key={item} variant="caption" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35, color: 'text.primary', fontSize: { xs: '0.55rem', sm: '0.7rem' } }}>
        • {item}
      </Typography>
    ))}
  </Box>
);

/** One property type (Private / EC / HDB) in the Affordability Overview — rows share a subgrid so they line up across columns. */
const RuleSection = ({
  title,
  ratioLabel,
  appliesTo,
  accent,
  cardBorder,
  selected,
  onSelect,
  ineligibleReasons = [],
  notes = [],
  headerExtra,
  children,
}: {
  title: string;
  ratioLabel: string;
  appliesTo: string;
  accent: string;
  cardBorder: string;
  /** This column drives the sections below the overview. */
  selected: boolean;
  onSelect: () => void;
  /** Why the buyer can't buy this type — the column gets a ribbon and its figures grey out (unless selected); it stays selectable for reference. */
  ineligibleReasons?: string[];
  /** Conditions the inputs can't verify — shown as advisories; the column stays selectable. */
  notes?: string[];
  /** Extra header controls (the HDB loan toggle) — always active, even on a blocked column. */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const blocked = ineligibleReasons.length > 0;
  return (
  <Box
    role="radio"
    aria-checked={selected}
    aria-label={blocked ? `${title} (not eligible): show calculations below for reference` : `Show ${title} calculations below`}
    tabIndex={0}
    // Tapping anywhere in the column selects it — even an ineligible one, for reference; a result card still opens its working.
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onSelect();
      }
    }}
    sx={{
      position: 'relative',
      overflow: 'hidden',
      border: 1,
      borderColor: selected ? accent : cardBorder,
      borderTop: `4px solid ${blocked ? alpha(accent, 0.4) : accent}`,
      boxShadow: selected ? `0 0 0 1px ${accent}` : 'none',
      bgcolor: selected ? alpha(accent, 0.06) : 'transparent',
      cursor: 'pointer',
      transition: 'background-color 150ms, box-shadow 150ms, border-color 150ms',
      '&:hover': selected ? {} : { borderColor: alpha(accent, 0.6), bgcolor: alpha(accent, 0.03) },
      '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
      // Figures stay on screen for reference, greyed out until the column is picked.
      ...(blocked && !selected ? { '& > :not([data-rule-header]):not([data-rule-ribbon])': { opacity: 0.45 } } : {}),
      borderRadius: 1,
      p: { xs: 0.75, sm: 2 },
      display: 'grid',
      gridRow: `span ${RULE_SECTION_ROWS}`,
      gridTemplateRows: 'subgrid',
      rowGap: { xs: 0.75, sm: 1.5 },
      minWidth: 0,
    }}
  >
    <Box data-rule-header>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', pr: blocked ? { xs: 4.5, sm: 4 } : 0 }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: accent, lineHeight: 1.2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {title}
        </Typography>
        {selected
          ? <CheckCircleIcon sx={{ order: 2, ml: 'auto', fontSize: { xs: '1rem', sm: '1.2rem' }, color: accent }} />
          : <RadioButtonUncheckedIcon sx={{ order: 2, ml: 'auto', fontSize: { xs: '1rem', sm: '1.2rem' }, color: 'text.disabled' }} />}
        <Chip
          label={ratioLabel}
          size="small"
          sx={{
            order: 1,
            height: { xs: 16, sm: 20 },
            fontSize: { xs: '0.55rem', sm: '0.7rem' },
            fontWeight: 700,
            bgcolor: accent,
            color: '#fff',
            '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, pr: blocked ? { xs: 2, sm: 3 } : 0, lineHeight: 1.4, fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
        {appliesTo}
      </Typography>
      {headerExtra && (
        // Header controls must not select the column.
        <Box onClick={(e) => e.stopPropagation()}>{headerExtra}</Box>
      )}
      {blocked && <RuleSectionNotice tone="error" heading="Not eligible" items={ineligibleReasons} />}
      {notes.length > 0 && <RuleSectionNotice tone="warning" heading="Please check" items={notes} />}
    </Box>
    {children}
    {blocked && (
      <Box
        data-rule-ribbon
        aria-hidden
        sx={{
          position: 'absolute',
          // Band centre sits equally far from the top and right edges, so the label clears both.
          top: { xs: 20, sm: 24 },
          right: { xs: -24, sm: -38 },
          width: { xs: 100, sm: 140 },
          transform: 'rotate(45deg)',
          bgcolor: 'error.main',
          color: '#fff',
          textAlign: 'center',
          fontSize: { xs: '0.45rem', sm: '0.62rem' },
          fontWeight: 800,
          letterSpacing: 0.5,
          py: { xs: 0.15, sm: 0.3 },
          boxShadow: 2,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        NOT ELIGIBLE
      </Box>
    )}
  </Box>
  );
};

const ResultCard = ({
  label,
  value,
  highlight,
  cardBg,
  cardBorder,
  formula,
  onEdit,
  editActive,
  helperText,
  dense,
}: {
  label: string;
  value: string;
  highlight?: string;
  cardBg: string;
  cardBorder: string;
  formula?: { title: string; lines: string[] };
  onEdit?: () => void;
  editActive?: boolean;
  helperText?: string;
  /** Tighter padding and type on phones — for the three-up Affordability Overview. */
  dense?: boolean;
}) => {
  const [open, setOpen] = React.useState(false);
  const clickable = !!formula;

  return (
    <>
      <Card
        variant="outlined"
        onClick={clickable ? () => setOpen(true) : undefined}
        sx={{
          p: { xs: dense ? 0.75 : 1.5, sm: 2 },
          minWidth: 0,
          bgcolor: cardBg,
          color: 'text.primary',
          borderColor: editActive ? 'warning.main' : (highlight || cardBorder),
          borderWidth: (highlight || editActive) ? 2 : 1,
          cursor: clickable ? 'pointer' : 'default',
          position: 'relative',
          '&:hover': clickable ? { borderColor: highlight || 'primary.main' } : {},
        }}
      >
        {onEdit && (
          <Box
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              p: { xs: dense ? 0.5 : 1.5, sm: 1.5 },
              cursor: 'pointer',
              borderRadius: '0 4px 0 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: { xs: dense ? 28 : 44, sm: 44 },
              minHeight: { xs: dense ? 28 : 44, sm: 44 },
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <EditIcon sx={{ fontSize: { xs: dense ? '0.85rem' : '1rem', sm: '1rem' }, color: editActive ? 'warning.main' : 'text.secondary' }} />
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: dense ? '0.6rem' : '0.65rem', sm: '0.7rem' },
            lineHeight: 1.3,
            ...(dense ? { display: 'block', pr: { xs: onEdit ? 2.5 : 0, sm: onEdit ? 3 : 0 } } : {}),
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mt: 0.5,
            fontSize: { xs: dense ? 'clamp(0.7rem, 3.1vw, 1rem)' : '1rem', sm: '1.15rem' },
            color: editActive ? 'warning.main' : (highlight || 'text.primary'),
            pr: { xs: onEdit && !dense ? 3 : 0, sm: onEdit ? 3 : 0 },
            overflowWrap: 'anywhere',
          }}
        >
          {value}
        </Typography>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              mt: 0.75,
              display: 'block',
              fontSize: { xs: dense ? '0.6rem' : '0.7rem', sm: '0.75rem' },
              lineHeight: dense ? { xs: 1.4, sm: 1.6 } : 1.6,
              color: 'warning.main',
            }}
          >
            {helperText}
          </Typography>
        )}
      </Card>
      {formula && (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{formula.title}</DialogTitle>
          <DialogContent>
            {formula.lines.map((line, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  lineHeight: 2,
                  fontWeight: line.startsWith('=') ? 700 : 400,
                  color: line.startsWith('=') ? 'primary.main' : 'text.primary',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {line}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const BorrowerSection = ({
  title,
  borrower,
  onUpdate,
  cardBg,
  cardBorder,
  highlightColor,
  isMobile,
  fmt,
  estimatedIncome,
  otherAnnualIncomeCap,
  onIncomeChange,
  reverseHint,
}: {
  title: string;
  borrower: BorrowerState;
  onUpdate: (field: keyof BorrowerState, value: number | boolean | string) => void;
  cardBg: string;
  cardBorder: string;
  highlightColor: string;
  isMobile: boolean;
  fmt: (v: number) => string;
  estimatedIncome?: number;
  otherAnnualIncomeCap?: number;
  onIncomeChange?: (value: number) => void;
  reverseHint?: string;
}) => {
  const isReversed = estimatedIncome !== undefined;
  const displayIncome = isReversed ? estimatedIncome : borrower.monthlyIncome;
  const otherAnnualIncomeReverseCap = Math.max(0, Math.min(OTHER_ANNUAL_INCOME_DEFAULT_MAX, otherAnnualIncomeCap ?? OTHER_ANNUAL_INCOME_DEFAULT_MAX));
  const otherAnnualIncomeCommitMax = isReversed ? otherAnnualIncomeReverseCap : INCOME_ABSOLUTE_MAX;

  // Grow-only slider ceilings: start at the default max and expand upward when
  // the current value exceeds the ceiling. They never shrink back while the
  // component is mounted, so sliding past the default does not reset the max.
  const [monthlyIncomeSliderMax, setMonthlyIncomeSliderMax] = useState<number>(() =>
    Math.max(MONTHLY_INCOME_DEFAULT_MAX, Math.ceil(Math.max(0, displayIncome) / 500) * 500),
  );
  useEffect(() => {
    const needed = Math.ceil(Math.max(0, displayIncome) / 500) * 500;
    if (needed > monthlyIncomeSliderMax) setMonthlyIncomeSliderMax(needed);
  }, [displayIncome, monthlyIncomeSliderMax]);

  const [otherAnnualIncomeSliderMaxState, setOtherAnnualIncomeSliderMaxState] = useState<number>(() =>
    Math.max(OTHER_ANNUAL_INCOME_DEFAULT_MAX, Math.ceil(Math.max(0, borrower.otherAnnualIncome) / 1000) * 1000),
  );
  useEffect(() => {
    if (isReversed) return;
    const needed = Math.ceil(Math.max(0, borrower.otherAnnualIncome) / 1000) * 1000;
    if (needed > otherAnnualIncomeSliderMaxState) setOtherAnnualIncomeSliderMaxState(needed);
  }, [borrower.otherAnnualIncome, otherAnnualIncomeSliderMaxState, isReversed]);

  const otherAnnualIncomeSliderMax = isReversed ? otherAnnualIncomeReverseCap : otherAnnualIncomeSliderMaxState;

  const [existingMonthlyDebtSliderMax, setExistingMonthlyDebtSliderMax] = useState<number>(() =>
    Math.max(EXISTING_DEBT_DEFAULT_MAX, Math.ceil(Math.max(0, borrower.existingMonthlyDebt) / 100) * 100),
  );
  useEffect(() => {
    const needed = Math.ceil(Math.max(0, borrower.existingMonthlyDebt) / 100) * 100;
    if (needed > existingMonthlyDebtSliderMax) setExistingMonthlyDebtSliderMax(needed);
  }, [borrower.existingMonthlyDebt, existingMonthlyDebtSliderMax]);
  const handleIncomeChange = (val: number) => {
    if (onIncomeChange) {
      onIncomeChange(val);
    } else {
      onUpdate('monthlyIncome', val);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: cardBg,
        color: 'text.primary',
        borderColor: isReversed ? 'warning.main' : cardBorder,
        borderWidth: isReversed ? 2 : 1,
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>

      {/* Name — shown in the card header and used when saving as a profile */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography variant="body2" fontWeight={600}>Name</Typography>
        <TextField
          size="small"
          placeholder="e.g. Adam Tan"
          value={borrower.name ?? ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          sx={{ width: 170, '& .MuiInputBase-input': { fontSize: '0.85rem', textAlign: 'right', py: 0.9 } }}
        />
      </Box>

      {/* Age */}
      <Box sx={{ mb: 2 }}>
        <EditableSliderValueRow
          label={<Typography variant="body2" fontWeight={600}>Age</Typography>}
          value={borrower.age}
          min={21}
          max={AGE_MAX}
          step={1}
          onCommit={(v) => onUpdate('age', v)}
          formatDisplay={(v) => `${v} yrs`}
          valueColor={highlightColor}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) 34px', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onUpdate('age', Math.max(21, borrower.age - 1))}
            disabled={borrower.age <= 21}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
            aria-label="Decrease age"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IntentGuardedSlider
            isMobile={isMobile}
            value={borrower.age}
            min={21}
            max={AGE_MAX}
            step={1}
            onChange={(_, val) => onUpdate('age', val as number)}
            sx={{ color: highlightColor, height: 6, display: 'block', minWidth: 0 }}
          />
          <IconButton
            size="small"
            onClick={() => onUpdate('age', Math.min(AGE_MAX, borrower.age + 1))}
            disabled={borrower.age >= AGE_MAX}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
            aria-label="Increase age"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Monthly Income */}
      <Box sx={{ mb: 1 }}>
        <EditableSliderValueRow
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={600}>Monthly Income</Typography>
              {isReversed && (
                <Chip
                  label="Estimated"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
          }
          value={displayIncome}
          min={0}
          max={monthlyIncomeSliderMax}
          commitMax={INCOME_ABSOLUTE_MAX}
          step={1}
          onCommit={(v) => handleIncomeChange(v)}
          formatDisplay={(v) => fmt(v)}
          valueColor={isReversed ? 'warning.main' : highlightColor}
          currencyInput
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleIncomeChange(Math.max(0, displayIncome - 500))}
            disabled={isReversed}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IntentGuardedSlider
            isMobile={isMobile}
            value={displayIncome}
            min={0}
            max={monthlyIncomeSliderMax}
            step={500}
            onChange={(_, val) => handleIncomeChange(val as number)}
            sx={{
              color: isReversed ? 'warning.main' : highlightColor,
              height: 6,
              flex: 1,
              minWidth: 0,
              opacity: isReversed ? 0.85 : 1,
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleIncomeChange(Math.min(INCOME_ABSOLUTE_MAX, displayIncome + 500))}
            disabled={isReversed}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        {isReversed && reverseHint && (
          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5, fontStyle: 'italic' }}>
            {reverseHint}
          </Typography>
        )}
      </Box>

      {/* Other Annual Income */}
      <Box sx={{ mb: 1 }}>
        <EditableSliderValueRow
          label={<Typography variant="body2" fontWeight={600}>Other Annual Income</Typography>}
          value={borrower.otherAnnualIncome}
          min={0}
          max={otherAnnualIncomeSliderMax}
          commitMax={otherAnnualIncomeCommitMax}
          step={1}
          onCommit={(v) => onUpdate('otherAnnualIncome', v)}
          formatDisplay={(v) => fmt(v)}
          valueColor={highlightColor}
          currencyInput
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onUpdate('otherAnnualIncome', Math.max(0, borrower.otherAnnualIncome - 1000))}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IntentGuardedSlider
            isMobile={isMobile}
            value={borrower.otherAnnualIncome}
            min={0}
            max={otherAnnualIncomeSliderMax}
            step={1000}
            onChange={(_, val) => onUpdate('otherAnnualIncome', val as number)}
            sx={{ color: highlightColor, height: 6, flex: 1, minWidth: 0 }}
          />
          <IconButton
            size="small"
            onClick={() => onUpdate('otherAnnualIncome', Math.min(otherAnnualIncomeCommitMax, borrower.otherAnnualIncome + 1000))}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        {isReversed && otherAnnualIncomeSliderMax < OTHER_ANNUAL_INCOME_DEFAULT_MAX && (
          <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 0.5, fontStyle: 'italic' }}>
            Reverse Mode cap: {fmt(otherAnnualIncomeSliderMax)} to keep the estimated monthly income non-negative.
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          Commission/Incentives/Bonus/Overtime – 30% haircut (70% recognised monthly: {fmt((borrower.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT)})
        </Typography>
      </Box>

      {/* Self-employed */}
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={borrower.isSelfEmployed}
              onChange={(e) => onUpdate('isSelfEmployed', e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              Self-employed income{' '}
              {borrower.isSelfEmployed && (
                <Typography component="span" variant="body2" sx={{ color: '#fbbf24', fontWeight: 700 }}>
                  (30% haircut applied)
                </Typography>
              )}
            </Typography>
          }
        />
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 4 }}>
          Recognised income used for calculation:{' '}
          {fmt(
            isReversed
              ? (borrower.isSelfEmployed ? displayIncome * SELF_EMPLOYED_HAIRCUT : displayIncome)
                + (borrower.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT
              : getRecognisedIncome(borrower)
          )}
        </Typography>
      </Box>

      {/* Existing Monthly Debt */}
      <Box sx={{ mb: 0.5 }}>
        <EditableSliderValueRow
          label={<Typography variant="body2" fontWeight={600}>Existing Monthly Debt</Typography>}
          value={borrower.existingMonthlyDebt}
          min={0}
          max={existingMonthlyDebtSliderMax}
          commitMax={INCOME_ABSOLUTE_MAX}
          step={1}
          onCommit={(v) => onUpdate('existingMonthlyDebt', v)}
          formatDisplay={(v) => fmt(v)}
          valueColor={highlightColor}
          currencyInput
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) 34px', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onUpdate('existingMonthlyDebt', Math.max(0, borrower.existingMonthlyDebt - 100))}
            disabled={borrower.existingMonthlyDebt <= 0}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
            aria-label="Decrease existing monthly debt"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <IntentGuardedSlider
            isMobile={isMobile}
            value={borrower.existingMonthlyDebt}
            min={0}
            max={existingMonthlyDebtSliderMax}
            step={100}
            onChange={(_, val) => onUpdate('existingMonthlyDebt', val as number)}
            sx={{ color: highlightColor, height: 6, display: 'block', minWidth: 0 }}
          />
          <IconButton
            size="small"
            onClick={() => onUpdate('existingMonthlyDebt', Math.min(INCOME_ABSOLUTE_MAX, borrower.existingMonthlyDebt + 100))}
            disabled={borrower.existingMonthlyDebt >= INCOME_ABSOLUTE_MAX}
            sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
            aria-label="Increase existing monthly debt"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5, opacity: 0.5 }} />

      {/* This borrower's own funds — Available Funds sums them */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>Cash / Saving</Typography>
          <Typography variant="caption" color="text.secondary">Counts towards Available Funds</Typography>
        </Box>
        <Box sx={{ width: 170, flexShrink: 0 }}>
          <CurrencyTextField value={borrower.cash} onChange={(v) => onUpdate('cash', v)} highlightColor={highlightColor} />
        </Box>
      </Box>
      <Divider sx={{ mb: 1, opacity: 0.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>CPF OA Balance</Typography>
          <Typography variant="caption" color="text.secondary">Counts towards Available Funds</Typography>
        </Box>
        <Box sx={{ width: 170, flexShrink: 0 }}>
          <CurrencyTextField value={borrower.cpf} onChange={(v) => onUpdate('cpf', v)} highlightColor={highlightColor} />
        </Box>
      </Box>
    </Card>
  );
};

// ─── Tutorial ────────────────────────────────────────────────────────────────

/** Step-by-step guide shown by the cap button beside the page title. Each
 *  targetElement matches a `data-tutorial` attribute below, so "Show on Page"
 *  can scroll to and highlight the real control. */
const MAXIMIZER_TUTORIAL_STEPS: TutorialStepDef[] = [
  {
    title: 'Welcome to the Max Affordability Calculator',
    content:
      'The Max Affordability Calculator answers two questions: how much can this buyer borrow, and what is the highest price they can support?\n\n' +
      'Fill in the borrower profile and funds in the Inputs column on the left — every figure on the right recalculates as you type.\n\n' +
      'On any step below, tap "Show on Page" and the calculator will scroll to the control being described and pulse a highlight around it for a few seconds.',
    targetElement: null,
  },
  {
    title: 'Load Example / History',
    content:
      'Start from something rather than a blank form:\n' +
      '• Load Example / History: preset borrower profiles, plus every snapshot you have saved before\n' +
      '• Save to History / Profile: with nothing loaded, choose history only, a new client profile, or both\n' +
      '• With a profile loaded, the same button saves to history and writes your edits back to that profile\n' +
      '• Clear: resets the Max Affordability Calculator to its defaults\n' +
      '• Export PDF: a client-ready summary of the current calculation\n\n' +
      'History is shared across the Super Calculators, so a case started here can be reopened in the Buying Calculator.',
    targetElement: '[data-tutorial="load-example"]',
  },
  {
    title: 'Display Controls',
    content:
      'Two viewing controls, both remembered across calculators:\n' +
      '• A- / A+: shrink or enlarge the text — useful when presenting figures to a client\n' +
      '• 1 Scroll / 2 Scroll (desktop): 1 Scroll moves the page as one column; 2 Scroll lets Inputs and Results scroll independently, so the results stay in view while you adjust an input',
    targetElement: '[data-tutorial="display-controls"]',
  },
  {
    title: 'Primary Borrower',
    content:
      'The main borrower drives both loan tenure and TDSR:\n' +
      '• Name: shown in the card header, and the name used if you save these inputs as a profile\n' +
      '• Age: caps the tenure — the loan must not run past the age limit for the applied LTV\n' +
      '• Monthly Income: gross fixed monthly salary\n' +
      '• Other Annual Income: commission, bonus and overtime — recognised at 70% (30% haircut)\n' +
      '• Self-employed: applies a further 30% haircut to the monthly income\n' +
      '• Existing Monthly Debt: car loans, other mortgages and credit lines, deducted from the TDSR allowance\n' +
      '• Cash / Saving and CPF OA Balance: this borrower\u2019s own funds — Available Funds adds up every borrower\u2019s, and each saves back to that person\u2019s profile\n\n' +
      'Every figure has both a slider and a typed input — tap the value to key in an exact amount.',
    targetElement: '[data-tutorial="primary-borrower"]',
  },
  {
    title: 'Additional Borrower',
    content:
      'Tick "Include additional borrower" for a joint purchase:\n' +
      '• Both incomes and both debts are combined for TDSR\n' +
      '• Loan tenure switches to the Income Weighted Average Age (IWAA), where the higher earner pulls the effective age towards their own\n' +
      '• A second stamp-duty profile appears, together with the Relationship option used for ABSD remission',
    targetElement: '[data-tutorial="additional-borrower"]',
  },
  {
    title: 'Available Funds',
    content:
      'The cash and CPF this buyer can actually put on the table:\n' +
      '• Cash: the sum of every borrower\u2019s own savings — edit it on their card\n' +
      '• Additional Cash: money still to come in, e.g. net sale proceeds\n' +
      '• CPF: the sum of every borrower\u2019s own CPF OA — edit it on their card\n' +
      '• Additional CPF: CPF refunded from a sale, or planned withdrawals\n\n' +
      'Total capital feeds "Affordable Today" in the Affordability Overview — the highest price these funds can complete on after downpayment, BSD and ABSD.',
    targetElement: '[data-tutorial="available-funds"]',
  },
  {
    title: 'Loan Tenure & IWAA',
    content:
      'Two caps apply at once and the calculator uses the tighter one:\n' +
      '• LTV cap: 30 years at 75% LTV, 35 years at 55% LTV\n' +
      '• Age cap: the loan must not run past age 65 at 75% LTV, or age 75 at 55% LTV\n\n' +
      'The line under the slider shows the current cap and the age it was derived from. With a joint borrower the age used is the IWAA — tap the IWAA box to see the working, borrower by borrower.',
    targetElement: '[data-tutorial="loan-tenure"]',
  },
  {
    title: 'Bank Interest Rate',
    content:
      'This is the rate used for the monthly instalment shown in the results.\n\n' +
      'Loan eligibility is a separate matter: it is assessed at the MAS stress-test rate, set once under the left menu → Settings and shared by all the Super Calculators. Moving this slider therefore changes the instalment, not the maximum loan.',
    targetElement: '[data-tutorial="interest-rate"]',
  },
  {
    title: 'Applied LTV',
    content:
      'Applied LTV sets how much of the price the loan can cover:\n' +
      '• 75%: the standard first housing loan — minimum 5% of the price in cash\n' +
      '• 55%: a subsequent housing loan, or a loan that runs past the age/tenure limits — minimum 10% in cash\n\n' +
      'LTV drives Max Property Price (Max Loan ÷ LTV) and the cash portion of the downpayment.',
    targetElement: '[data-tutorial="applied-ltv"]',
  },
  {
    title: 'Purchase & Funding',
    content:
      'Property Price is the specific price you want to test. Set it and the results show what that purchase needs, alongside the maximum the buyer could reach.\n\n' +
      'Income-support funds cover a TDSR shortfall:\n' +
      '• Pledged: deposits locked with the bank for 48 months — recognised at 100%\n' +
      '• Unpledged: assets shown but not locked — recognised at 30%, so roughly 3.3x the pledged amount\n' +
      '• ADJUST BY lets you drive the mix by Total, Unpledge or Pledge; the other two follow automatically',
    targetElement: '[data-tutorial="purchase-funding"]',
  },
  {
    title: 'Stamp Duty Inputs',
    content:
      'Stamp duty is derived from the buyer profile rather than typed in:\n' +
      '• Citizenship: SC / PR / Foreigner / Entity. Nationals of the FTA countries can be marked FTA-eligible and are then charged the Singapore Citizen rate\n' +
      '• Which property: 1st / 2nd / 3rd or more — this picks the ABSD rate\n' +
      '• Relationship (joint purchases only): a married couple with at least one SC spouse may qualify for ABSD remission\n\n' +
      'For a joint purchase without remission, the higher of the two profiles sets the ABSD rate.',
    targetElement: '[data-tutorial="stamp-duty-inputs"]',
  },
  {
    title: 'Affordability Overview',
    content:
      'The headline results, shown side by side for three property types:\n' +
      '• Private — TDSR (55%): 55% of income less existing debt; max tenure 30 years\n' +
      '• EC — MSR (30%): 30% of income, and TDSR still applies, so the lower of the two caps the instalment; max tenure 30 years\n' +
      '• HDB — MSR (30%) as for EC. The HDB Loan | Bank Loan switch in its header picks the loan: an HDB loan is tested at HDB\u2019s interest-rate floor (Settings) with 75% LTV, up to 25 years and age 65, and no cash-only minimum; a bank loan follows the Inputs, with a max tenure of 25 years at 75% LTV\n\n' +
      'Eligibility is checked from the borrower profile (age, citizenship, relationship, properties owned) and gross household income. A type the buyer can\u2019t buy gets a NOT ELIGIBLE ribbon with the reasons; its figures stay greyed out, but the column can still be selected (and exported to PDF) for reference:\n' +
      '• EC: household income up to $16,000; a married couple (one Singapore Citizen, the other Citizen or PR, both 21+) or Joint Singles (all Citizens, 35+). A single can\u2019t buy a new EC alone\n' +
      '• HDB: a single Singapore Citizen aged 35+, a couple with a Citizen (or both PRs), or Joint Singles (all Citizens, 35+); nobody may already own a property\n' +
      '• HDB loan: at least one Singapore Citizen and household income up to $16,000 (family) / $8,000 (single). A buyer who doesn\u2019t qualify is switched to Bank Loan, and HDB Loan is disabled until they do\n\n' +
      'Each column shows Max Property Price (Max Loan ÷ LTV), Max Loan, the Loan Tenure that type allows for this buyer, the monthly instalment cap and Affordable Today (the most the buyer’s actual funds can complete on). Rows line up across the three columns.\n\n' +
      'Tap a column (or use the Private | EC | HDB switch further down) to pick which property type the sections below calculate: Selected Property Summary, Income Support, and Cash & Mortgage all follow it. The ticked column is the one being shown.\n\n' +
      'The pencil icon on Max Loan or Max Property Price — in the Private, EC or HDB column — switches on Reverse Mode: key in the figure you want to reach and the calculator works backwards to the income required. For EC and HDB the income must pass both MSR and TDSR over that column\'s tenure, and a target that needs income above the type\'s ceiling is flagged. A chip appears at the top of the Inputs card; tap its X to leave Reverse Mode.\n\n' +
      'Tap any result card to see the formula behind it.',
    targetElement: '[data-tutorial="affordability-overview"]',
  },
  {
    title: 'Borrower Profile',
    content:
      'How the income was assessed:\n' +
      '• Total Recognised Income vs Total Raw Income — the gap is the self-employed and variable-income haircuts\n' +
      '• Gross TDSR (55%) and Available TDSR after existing debt: the monthly amount left to service the new mortgage\n' +
      '• Effective Age Used and IWAA: the age the tenure was built on\n\n' +
      'Each card opens its own working when tapped.',
    targetElement: '[data-tutorial="borrower-profile"]',
  },
  {
    title: 'Selected Property Summary',
    content:
      'This block is about the price you entered under Purchase & Funding, tested against the property type picked in the Affordability Overview (Private, EC or HDB):\n' +
      '• Property Price: the price being tested\n' +
      '• Required Loan: that price less the funds committed\n' +
      '• Loan Shortfall: how far the required loan exceeds that type\'s max loan — anything above zero is the gap the next block prices\n\n' +
      'Until you set your own price, it follows the selected type\'s Max Property Price.',
    targetElement: '[data-tutorial="selected-property"]',
  },
  {
    title: 'Income Support Needed',
    content:
      'When the required loan runs past what the income supports, this block prices the gap two ways. Private must pass TDSR; EC and HDB must pass both MSR and TDSR, so the ratio needing more income sets the gap:\n' +
      '• Pledged: the deposit to lock for 48 months, recognised at 100%\n' +
      '• Unpledged: the assets to show instead, recognised at 30%\n\n' +
      'Each figure covers 100% of the gap on its own. To plan a mix of the two, use Inputs → Income-support funds; the adjusted split is reflected back here.',
    targetElement: '[data-tutorial="income-support"]',
  },
  {
    title: 'Cash & Mortgage Requirements',
    content:
      'What the buyer must produce, and what they will pay monthly:\n' +
      '• Minimum Cash Needed: the cash-only slice of the downpayment (5% at 75% LTV, 10% at 55%)\n' +
      '• Effective Loan Tenure actually applied after the caps\n' +
      '• Monthly mortgage required for the property price and LTV chosen\n' +
      '• Grand Total Outlay: the downpayment, stamp duty and any pledged/unpledged funds together\n\n' +
      'Note the caveat on the card — Minimum Cash Needed excludes any loan shortfall and any cash tied up in pledging.',
    targetElement: '[data-tutorial="cash-mortgage"]',
  },
  {
    title: 'Stamp Duty Payable',
    content:
      '• BSD: Buyer’s Stamp Duty on the IRAS progressive residential rates (1–6%, on or after 15 Feb 2023)\n' +
      '• ABSD: property price × the rate implied by the buyer profile, with married-couple remission applied where it qualifies\n' +
      '• Total: BSD + ABSD, which is also carried into the Grand Total Outlay\n\n' +
      'Tap either card for the bracket-by-bracket breakdown.',
    targetElement: '[data-tutorial="stamp-duty-payable"]',
  },
  {
    title: 'Saving, Exporting & Resetting',
    content:
      'Wrapping up a case:\n' +
      '• Save changes to "<profile>": appears at the foot of the Inputs card once you edit a loaded profile, writing age, income, debt, citizenship and property count back to it\n' +
      '• Save to History: keeps a snapshot without touching the profile\n' +
      '• Export PDF: a client-ready summary including stamp duty and the income-support figures\n' +
      '• Clear: resets the calculator\n\n' +
      'Your place in this tutorial is remembered — the cap button beside the title is red before you start, amber while in progress and green once finished. Use Restart to run through it again, or the download icon in the header for the full text manual.',
    targetElement: '[data-tutorial="save-profile"]',
  },
];

/** Reference material appended to the downloaded manual, after the steps. */
const MAXIMIZER_MANUAL_SECTIONS = [
  {
    heading: 'Key Rules Applied',
    body:
      'TDSR: 55% of recognised monthly income, less existing monthly debt.\n' +
      'MSR (HDB / EC only): 30% of recognised monthly income. TDSR still applies,\n' +
      '  so the instalment is capped at the lower of MSR and available TDSR.\n' +
      'Income ceilings are eligibility tests, not caps on MSR income: above EC\n' +
      '  $16,000 -> not eligible for a new EC; above HDB loan $16,000 family /\n' +
      '  $8,000 single -> bank loan only. MSR always uses full recognised income.\n' +
      'Eligibility: EC needs a couple (SC + SC/PR, 21+) or Joint Singles (all SC,\n' +
      '  35+). HDB needs a single SC 35+, a couple with an SC (or both PRs), or\n' +
      '  Joint Singles (all SC, 35+), and no property already owned. HDB loan needs\n' +
      '  an SC buyer. Ineligible types are greyed out but selectable for reference.\n' +
      'HDB loan: 2.6%, tested at HDB\'s floor (Settings), 75% LTV, 25 years / age\n' +
      '  65, no cash-only minimum.\n' +
      'Income recognition: self-employed income 70%; variable income (commission /\n' +
      '  bonus / overtime) 70%.\n' +
      'Stress test: maximum loan is assessed at the MAS stress-test rate configured\n' +
      '  under the left menu > Settings, not at the bank rate on the slider.\n' +
      'Tenure caps: 75% LTV -> 30 years and up to age 65; 55% LTV -> 35 years and\n' +
      '  up to age 75. The tighter of the two applies. HDB flats cap 5 years\n' +
      '  lower (25 years at 75% LTV, 30 years at 55% LTV).\n' +
      'Minimum cash: 5% of the price at 75% LTV; 10% at 55% LTV.\n' +
      'Income support: pledged deposits are recognised at 100% over 48 months;\n' +
      '  unpledged assets at 30%.\n' +
      'IWAA: ceil( sum(Age x Recognised Income) / sum(Recognised Income) ).',
  },
  {
    heading: 'Sources',
    body:
      'MAS - Total Debt Servicing Ratio and residential property loan rules.\n' +
      'IRAS - Buyer’s Stamp Duty and Additional Buyer’s Stamp Duty rates\n' +
      '  (residential rates on or after 15 Feb 2023).\n' +
      'CPF Board - Ordinary Account usage for property purchase.',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const AffordabilityCalculator: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { selectedProfile, getSelectedProfiles, getSelectedName, clearSelection, updateProfile } = useProfile();
  const { settings: globalSettings } = useGlobalCalculatorSettings();
  const currentUser = useCurrentUser();

  const [inputs, setInputs] = useState<InputState>(createDefaultInputs);

  const [propertyPriceSliderMax, setPropertyPriceSliderMax] = useState<number>(() =>
    Math.max(PROPERTY_PRICE_MAX, Math.ceil(Math.max(0, 1500000) / 10000) * 10000),
  );
  useEffect(() => {
    const needed = Math.ceil(Math.max(0, inputs.proposedPropertyPrice) / 10000) * 10000;
    if (needed > propertyPriceSliderMax) setPropertyPriceSliderMax(needed);
  }, [inputs.proposedPropertyPrice, propertyPriceSliderMax]);
  // selectedExample now comes from LinkedCalculatorContext
  const [iwaaDialogOpen, setIwaaDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  // historyToast now comes from LinkedCalculatorContext

  // Snapshot of borrower inputs as loaded from profile, for change detection
  const [profileSnapshot, setProfileSnapshot] = useState<{
    primary: BorrowerState;
    additional: BorrowerState;
    includeAdditionalBorrower: boolean;
    primaryCitizenship: CitizenshipType;
    primaryFtaEligible: boolean;
    primaryPropertyCount: PropertyCount;
    additionalCitizenship: CitizenshipType;
    additionalFtaEligible: boolean;
    additionalPropertyCount: PropertyCount;
  } | null>(null);
  const [savingToProfile, setSavingToProfile] = useState(false);

  // ── History (via shared LinkedCalculator context) ──────────────────────
  const {
    activeSelection,
    setActiveSelection,
    selectedExample,
    setSelectedExample,
    saveAllToHistory,
    loadAllFromHistory,
    showToast: showHistoryToast,
    registerStateGetter,
    registerStateSetter,
    registerProfileBuilder,
    setGetSelectedName: setCtxGetSelectedName,
    registerBorrowerExtractor,
    registerBorrowerApplier,
  } = useLinkedCalculator();

  // ── Property Price auto-tracking ─────────────────────────────────────────
  // false = follow maxPropertyPrice; true = user has manually set a value
  const [propertyPriceUserAdjusted, setPropertyPriceUserAdjusted] = useState(false);

  // ── Reverse Mode ─────────────────────────────────────────────────────────
  const [reverseModeType, setReverseModeType] = useState<'loan' | 'propertyPrice' | null>(null);
  const reverseMode = reverseModeType !== null;
  const [targetMaxLoan, setTargetMaxLoan] = useState<number>(0);
  const [targetPropertyPrice, setTargetPropertyPrice] = useState<number>(0);
  // Column the active target belongs to, and the column whose edit dialog is open.
  const [reverseTarget, setReverseTarget] = useState<OverviewType>('private');
  const [editTarget, setEditTarget] = useState<OverviewType>('private');
  // Overview column whose calculations the sections below show.
  const [selectedType, setSelectedType] = useState<OverviewType>('private');
  // PDF export: which property type(s) the report covers.
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfChoice, setPdfChoice] = useState<OverviewType | 'all'>('private');
  const [pdfExporting, setPdfExporting] = useState(false);
  const [editLoanDialogOpen, setEditLoanDialogOpen] = useState(false);
  const [editLoanInput, setEditLoanInput] = useState<string>('');
  const [editPriceDialogOpen, setEditPriceDialogOpen] = useState(false);
  // Explains a derived Available Funds row (Cash / CPF) — see the info icon there.
  const [fundsDialog, setFundsDialog] = useState<{ title: string; lines: string[] } | null>(null);
  const [editPriceInput, setEditPriceInput] = useState<string>('');
  // 0=all additional, 1=all primary (proportion of income for primary borrower)
  const [primaryIncomeRatio, setPrimaryIncomeRatio] = useState(1.0);
  const [incomeOverrideWarning, setIncomeOverrideWarning] = useState<{
    open: boolean;
    target: 'primary' | 'additional';
    newValue: number;
  }>({ open: false, target: 'primary', newValue: 0 });
  // Once user chooses "Lock & Recalculate", skip the dialog for subsequent slider moves
  const [silentLockMode, setSilentLockMode] = useState(false);
  const effectiveInterestRate = inputs.bankInterestRate;
  const effectiveInterestRateLabel = 'Bank Interest Rate';
  const stressTestRate = globalSettings.stressTestRate;
  const hdbStressTestRate = globalSettings.hdbStressTestRate;
  const stressTestRateLabel = 'Stress Test Rate';

  const isBorrowerState = useCallback((value: unknown): value is BorrowerState => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<BorrowerState>;
    return (
      typeof candidate.age === 'number' &&
      typeof candidate.monthlyIncome === 'number' &&
      typeof candidate.isSelfEmployed === 'boolean' &&
      typeof candidate.existingMonthlyDebt === 'number'
    );
  }, []);

  const isInputState = useCallback((value: unknown): value is InputState => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<InputState>;
    return (
      isBorrowerState(candidate.primary) &&
      isBorrowerState(candidate.additional) &&
      typeof candidate.includeAdditionalBorrower === 'boolean' &&
      typeof candidate.tenure === 'number' &&
      typeof candidate.bankInterestRate === 'number' &&
      (candidate.ltv === 75 || candidate.ltv === 55) &&
      typeof candidate.proposedPropertyPrice === 'number'
    );
  }, [isBorrowerState]);

  const getHistoryInputs = useCallback((
    purchasePlannerState: SharedCalculatorActiveSelection['purchasePlannerState']
  ): InputState | null => {
    const raw = purchasePlannerState?.affordabilityState?.inputs;
    if (!isInputState(raw)) return null;
    const savedFunds = normalizeAvailableFunds(raw.availableFunds);
    const primary = normalizeBorrower(raw.primary);
    const additional = normalizeBorrower(raw.additional);
    // Snapshots saved before per-borrower funds existed only carry the totals —
    // park them on the primary borrower so the derived totals match the snapshot.
    const legacyFunds =
      (raw.primary as Partial<BorrowerState> | undefined)?.cash === undefined &&
      (raw.primary as Partial<BorrowerState> | undefined)?.cpf === undefined;
    return {
      primary: legacyFunds ? { ...primary, cash: savedFunds.cash, cpf: savedFunds.cpf } : primary,
      includeAdditionalBorrower: raw.includeAdditionalBorrower,
      additional,
      tenure: raw.tenure,
      bankInterestRate: raw.bankInterestRate,
      ltv: raw.ltv,
      proposedPropertyPrice: raw.proposedPropertyPrice,
      // Snapshots saved before Available Funds existed come back as all-zero.
      availableFunds: savedFunds,
      // Snapshots saved before the HDB loan toggle default to the HDB loan.
      hdbLoanSource: raw.hdbLoanSource === 'bank' ? 'bank' : 'hdb',
    };
  }, [isInputState]);

  const getPurchasePlannerTargetPrice = useCallback(
    (purchasePlannerState: SharedCalculatorActiveSelection['purchasePlannerState']): number | null => {
      const raw = purchasePlannerState?.targetPurchasePrice;
      if (typeof raw !== 'string') return null;
      const parsed = Number.parseFloat(raw.replace(/,/g, ''));
      return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
    },
    []
  );

  // ── ABSD State (per-borrower) ───────────────────────────────────────────
  const [primaryCitizenship, setPrimaryCitizenship] = useState<CitizenshipType>('SC');
  const [primaryPropertyCount, setPrimaryPropertyCount] = useState<PropertyCount>('1st');
  const [additionalCitizenship, setAdditionalCitizenship] = useState<CitizenshipType>('SC');
  const [additionalPropertyCount, setAdditionalPropertyCount] = useState<PropertyCount>('1st');
  const [primaryFtaEligible, setPrimaryFtaEligible] = useState(false);
  const [additionalFtaEligible, setAdditionalFtaEligible] = useState(false);
  const [absdRelationship, setAbsdRelationship] = useState<string>('SPOUSE');

  /** Currency string for hybrid TDSR unpledged row; reset when computed income-support gap changes */
  const [unpledgeInputValue, setUnpledgeInputValue] = useState('');
  // ADJUST BY tab for the income-support funds (Total / Unpledge / Pledge). unpledgeInputValue
  // stays the single source of truth; the other two figures are derived from it.
  const [pledgeMode, setPledgeMode] = useState<'total' | 'unpledge' | 'pledge'>('total');
  // Scroll mode: false = page scrolls as one (default); true = left/right columns scroll independently.
  const [splitScroll, setSplitScroll] = useState(false);

  // Step-by-step tutorial (cap button beside the page title).
  const tutorial = useCalculatorTutorial('maximizerTutorialProgress', MAXIMIZER_TUTORIAL_STEPS.length);
  // Release notes (megaphone beside the tutorial button); pops up on every visit until
  // the agent ticks "Don't show this again".
  const whatsNew = useWhatsNew('maximizer', MAXIMIZER_WHATS_NEW, { untilSuppressed: true });

  const clearCalculator = useCallback(() => {
    setInputs(createDefaultInputs());
    setPropertyPriceSliderMax(PROPERTY_PRICE_MAX);
    setPropertyPriceUserAdjusted(false);
    setProfileSnapshot(null);

    setPrimaryCitizenship('SC');
    setPrimaryPropertyCount('1st');
    setAdditionalCitizenship('SC');
    setAdditionalPropertyCount('1st');
    setPrimaryFtaEligible(false);
    setAdditionalFtaEligible(false);
    setAbsdRelationship('SPOUSE');
    setUnpledgeInputValue('');
    setPledgeMode('total');
    setSplitScroll(false);

    setReverseModeType(null);
    setTargetMaxLoan(0);
    setTargetPropertyPrice(0);
    setPrimaryIncomeRatio(1);
    setSilentLockMode(false);
    setIwaaDialogOpen(false);
    setEditLoanDialogOpen(false);
    setEditLoanInput('');
    setEditPriceDialogOpen(false);
    setEditPriceInput('');
    setIncomeOverrideWarning({ open: false, target: 'primary', newValue: 0 });

    setSelectedExample('');
    setActiveSelection(null);
    // Detach the loaded profile too — the profile itself remains intact.
    clearSelection();
    showHistoryToast('Max Affordability Calculator reset');
    setClearDialogOpen(false);
  }, [setActiveSelection, setSelectedExample, showHistoryToast, clearSelection]);

  // ── Profile Loading ──────────────────────────────────────────────────────

  // Which profile/group the Available Funds rows were last seeded from — see the
  // switch handling inside the effect below.
  const loadedFundsProfileKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedProfile) {
      // Nothing selected (including after Clear): the next selection — even of the
      // same profile — counts as a switch and re-seeds Available Funds.
      loadedFundsProfileKeyRef.current = null;
      return;
    }

    const selectedProfiles =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : selectedProfile.group.profiles || [];

    if (selectedProfiles.length === 0) return;

    // Switching profile re-seeds Available Funds: all four rows are cleared, then Cash
    // and CPF reload from this profile's saved funds, so nothing from the previous client
    // lingers. A plain refresh of the same selection (profile-list refetch, an edit saved
    // in Profile Manager) is not a switch and leaves the entered amounts alone. Claimed
    // before the guards below so the funds in a restored snapshot survive the profile
    // refresh that follows it.
    const fundsProfileKey = `${selectedProfile.type}-${selectedProfile.id}:${selectedProfiles.map((p) => p.Id).join(',')}`;
    const profileSwitched = loadedFundsProfileKeyRef.current !== fundsProfileKey;
    loadedFundsProfileKeyRef.current = fundsProfileKey;

    // A queued Profile Manager load is about to restore a full saved snapshot —
    // don't overwrite it with the profile's default form population.
    if (hasPendingHistoryLoad()) return;

    // A save-back just wrote these same values to the profile — skip the
    // repopulation triggered by the refreshed selectedProfile (it would reset
    // the tenure and clear the active history selection).
    if (isProfileSaveBackInProgress()) return;

    const currentYear = new Date().getFullYear();

    const primaryProfile = selectedProfiles[0];
    const primaryIncome = primaryProfile.MonthlyIncome || 0;
    const primaryOtherAnnual = primaryProfile.OtherAnnualIncome || 0;
    const primaryAge = primaryProfile.BirthYear ? currentYear - primaryProfile.BirthYear : 30;
    const primaryIsSelfEmployed = Boolean(primaryProfile.SelfEmployedInd);
    const primaryDebt =
      (primaryProfile.CarLoan ?? 0) +
      (primaryProfile.HousingLoan ?? 0) +
      (primaryProfile.CreditCard ?? 0) +
      (primaryProfile.OtherLoan ?? 0);

    const newInputs: Partial<InputState> = {
      primary: {
        name: primaryProfile.Name?.trim() || undefined,
        age: primaryAge,
        monthlyIncome: Math.round(primaryIncome),
        otherAnnualIncome: Math.round(primaryOtherAnnual),
        isSelfEmployed: primaryIsSelfEmployed,
        existingMonthlyDebt: Math.round(primaryDebt),
        cash: Math.round(primaryProfile.Saving ?? 0),
        cpf: Math.round(primaryProfile.Cpf ?? 0),
      },
    };

    if (selectedProfiles.length > 1) {
      const addProfile = selectedProfiles[1];
      const addIncome = addProfile.MonthlyIncome || 0;
      const addOtherAnnual = addProfile.OtherAnnualIncome || 0;
      const addAge = addProfile.BirthYear ? currentYear - addProfile.BirthYear : 30;
      const addIsSelfEmployed = Boolean(addProfile.SelfEmployedInd);
      const addDebt =
        (addProfile.CarLoan ?? 0) +
        (addProfile.HousingLoan ?? 0) +
        (addProfile.CreditCard ?? 0) +
        (addProfile.OtherLoan ?? 0);

      newInputs.includeAdditionalBorrower = true;
      newInputs.additional = {
        name: addProfile.Name?.trim() || undefined,
        age: addAge,
        monthlyIncome: Math.round(addIncome),
        otherAnnualIncome: Math.round(addOtherAnnual),
        isSelfEmployed: addIsSelfEmployed,
        existingMonthlyDebt: Math.round(addDebt),
        cash: Math.round(addProfile.Saving ?? 0),
        cpf: Math.round(addProfile.Cpf ?? 0),
      };
    } else {
      newInputs.includeAdditionalBorrower = false;
    }

    // Each member's funds land on their own borrower card above; Available Funds sums
    // them. A profile switch also clears the two "additional" rows, which no profile
    // holds — they only arrive from a Selling handoff or manual entry.
    const hasFundData = selectedProfiles.some((p) => p.Saving != null || p.Cpf != null);
    if (hasFundData || profileSwitched) {
      newInputs.availableFunds = createDefaultAvailableFunds();
    }

    // IWAA uses recognised income (incl. other annual, haircuts)
    const primaryRec = getRecognisedIncome(newInputs.primary!);
    const addRec = newInputs.includeAdditionalBorrower ? getRecognisedIncome(newInputs.additional!) : 0;
    const totalInc = primaryRec + addRec;
    const iwaa = newInputs.includeAdditionalBorrower && totalInc > 0
      ? Math.ceil((newInputs.primary!.age * primaryRec + newInputs.additional!.age * addRec) / totalInc)
      : newInputs.primary!.age;

    setInputs((prev) => {
      const maxAge = getMaxAgeForLtv(prev.ltv);
      const maxTenure = getMaxTenureForLtv(prev.ltv);
      const ageBasedCap = Math.max(0, maxAge - iwaa);
      const effectiveTenure = Math.min(maxTenure, ageBasedCap);
      return {
        ...prev,
        ...newInputs,
        tenure: effectiveTenure,
      };
    });
    // Set ABSD fields from profile (per-borrower)
    const primCitizenship = (primaryProfile.Citizenship as CitizenshipType) || 'SC';
    setPrimaryCitizenship(primCitizenship);
    setPrimaryFtaEligible(Number(primaryProfile.FtaNationalityInd) === 1);

    if (selectedProfiles.length > 1) {
      const addCitizenship = (selectedProfiles[1].Citizenship as CitizenshipType) || 'SC';
      setAdditionalCitizenship(addCitizenship);
      setAdditionalFtaEligible(Number(selectedProfiles[1].FtaNationalityInd) === 1);
    } else {
      setAdditionalFtaEligible(false);
    }

    if (selectedProfile?.type === 'group') {
      const groupRelationship = selectedProfile.group.Relationship || 'SPOUSE';
      setAbsdRelationship(groupRelationship);
    } else {
      setAbsdRelationship('INDIVIDUAL');
    }

    setPrimaryPropertyCount(propertiesOwnedToPropertyCount(primaryProfile.PropertiesOwned));
    if (selectedProfiles.length > 1) {
      setAdditionalPropertyCount(propertiesOwnedToPropertyCount(selectedProfiles[1].PropertiesOwned));
    } else {
      setAdditionalPropertyCount('1st');
    }

    // Snapshot the loaded borrower values for change detection
    const snapPrimCitizenship = (primaryProfile.Citizenship as CitizenshipType) || 'SC';
    const snapPrimFta = Number(primaryProfile.FtaNationalityInd) === 1;
    const snapPrimPropertyCount = propertiesOwnedToPropertyCount(primaryProfile.PropertiesOwned);
    const snapAddCitizenship = selectedProfiles.length > 1 ? ((selectedProfiles[1].Citizenship as CitizenshipType) || 'SC') : 'SC';
    const snapAddFta = selectedProfiles.length > 1 ? Number(selectedProfiles[1].FtaNationalityInd) === 1 : false;
    const snapAddPropertyCount = selectedProfiles.length > 1 ? propertiesOwnedToPropertyCount(selectedProfiles[1].PropertiesOwned) : '1st';
    setProfileSnapshot({
      primary: newInputs.primary!,
      additional: newInputs.additional ?? { age: 30, monthlyIncome: 0, otherAnnualIncome: 0, isSelfEmployed: false, existingMonthlyDebt: 0, ...EMPTY_BORROWER_FUNDS },
      includeAdditionalBorrower: newInputs.includeAdditionalBorrower ?? false,
      primaryCitizenship: snapPrimCitizenship,
      primaryFtaEligible: snapPrimFta,
      primaryPropertyCount: snapPrimPropertyCount,
      additionalCitizenship: snapAddCitizenship,
      additionalFtaEligible: snapAddFta,
      additionalPropertyCount: snapAddPropertyCount,
    });
    setSelectedExample('');
    setActiveSelection(null);
    setReverseModeType(null);
    setTargetMaxLoan(0);
    setTargetPropertyPrice(0);
  }, [selectedProfile, setActiveSelection]);

  // Backfill borrower names from the loaded profile whenever a card has none. The
  // profile-load effect seeds them, but a history snapshot restored afterwards (or
  // one saved before the Name field existed) carries no name and would blank the
  // header — this puts the loaded profile's names back without touching a typed one.
  const selectedMemberNames = useMemo<string[]>(() => {
    if (!selectedProfile) return [];
    const members =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : (selectedProfile.group.profiles || []);
    return members.map((m) => m.Name?.trim() || '');
  }, [selectedProfile]);

  useEffect(() => {
    if (selectedMemberNames.length === 0) return;
    setInputs((prev) => {
      const primary = !prev.primary.name?.trim() && selectedMemberNames[0]
        ? { ...prev.primary, name: selectedMemberNames[0] }
        : prev.primary;
      const additional = !prev.additional.name?.trim() && selectedMemberNames[1]
        ? { ...prev.additional, name: selectedMemberNames[1] }
        : prev.additional;
      return primary === prev.primary && additional === prev.additional
        ? prev
        : { ...prev, primary, additional };
    });
  }, [selectedMemberNames, inputs.primary.name, inputs.additional.name]);

  const mapSharedProfileToBorrower = useCallback((profile: SharedCalculatorProfile): BorrowerState => {
    const isSelfEmployed = profile.selfEmployedInd === 1;
    const incomeBasis = profile.incomeBasis ?? 'assessable';
    const monthlyIncome =
      isSelfEmployed && incomeBasis === 'assessable'
        ? Math.round(profile.monthlyIncome / SELF_EMPLOYED_HAIRCUT)
        : profile.monthlyIncome;

    return {
      age: profile.age,
      monthlyIncome,
      otherAnnualIncome: 0,
      isSelfEmployed,
      existingMonthlyDebt: profile.monthlyDebts ?? 0,
      cash: Math.round(profile.currentCashSavings ?? 0),
      cpf: Math.round(profile.currentCPFOA ?? 0),
    };
  }, []);

  const buildInputsFromSharedProfiles = useCallback(
    (
      profiles: readonly SharedCalculatorProfile[],
      targetPurchasePrice?: number | null
    ): InputState => {
      const primaryProfile = profiles[0] ?? {
        id: '1',
        age: 30,
        monthlyIncome: 5000,
        monthlyDebts: 0,
      };
      const additionalProfile = profiles[1];
      const primary = mapSharedProfileToBorrower(primaryProfile);
      const additional = additionalProfile
        ? mapSharedProfileToBorrower(additionalProfile)
        : { age: primary.age, monthlyIncome: 0, otherAnnualIncome: 0, isSelfEmployed: false, existingMonthlyDebt: 0, ...EMPTY_BORROWER_FUNDS };

      // Shared profiles carry today's balances (Purchase Planner / examples) — those
      // land on the borrower cards above, so Cash / CPF here just mirror their sum.
      // The two "additional" rows reset with the incoming funds, else they are kept.
      const hasFundData = profiles.some(
        (p) => p.currentCashSavings != null || p.currentCPFOA != null,
      );
      const availableFunds: AvailableFundsState = {
        ...(hasFundData ? createDefaultAvailableFunds() : normalizeAvailableFunds(inputs.availableFunds)),
        cash: primary.cash + (additionalProfile ? additional.cash : 0),
        cpf: primary.cpf + (additionalProfile ? additional.cpf : 0),
      };

      const nextInputs: InputState = {
        ...inputs,
        primary,
        includeAdditionalBorrower: !!additionalProfile,
        additional,
        availableFunds,
      };

      const primaryRecognised = getRecognisedIncome(primary);
      const additionalRecognised = nextInputs.includeAdditionalBorrower ? getRecognisedIncome(additional) : 0;
      const totalRecognisedIncome = primaryRecognised + additionalRecognised;
      const iwaa = nextInputs.includeAdditionalBorrower && totalRecognisedIncome > 0
        ? Math.ceil((primary.age * primaryRecognised + additional.age * additionalRecognised) / totalRecognisedIncome)
        : primary.age;
      const maxAge = getMaxAgeForLtv(nextInputs.ltv);
      const maxTenure = getMaxTenureForLtv(nextInputs.ltv);
      const ageBasedCap = Math.max(0, maxAge - iwaa);
      const effectiveTenure = Math.min(maxTenure, ageBasedCap);
      const withTenure = { ...nextInputs, tenure: effectiveTenure };
      const calculated = computeResults(withTenure, effectiveInterestRate, stressTestRate);

      return {
        ...withTenure,
        proposedPropertyPrice:
          targetPurchasePrice && targetPurchasePrice > 0
            ? targetPurchasePrice
            : calculated.maxPropertyPrice,
      };
    },
    [effectiveInterestRate, stressTestRate, inputs, mapSharedProfileToBorrower]
  );

  const applySharedProfiles = useCallback(
    (
      profiles: readonly SharedCalculatorProfile[],
      selectionValue: string,
      targetPurchasePrice?: number | null
    ) => {
      setInputs(buildInputsFromSharedProfiles(profiles, targetPurchasePrice));
      setPropertyPriceUserAdjusted(!!(targetPurchasePrice && targetPurchasePrice > 0));
      setSelectedExample(selectionValue);
      setReverseModeType(null);
      setTargetMaxLoan(0);
      setTargetPropertyPrice(0);
    },
    [buildInputsFromSharedProfiles]
  );

  const lastAppliedSelectionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeSelection?.profiles?.length) {
      lastAppliedSelectionIdRef.current = null;
      return;
    }
    if (activeSelection.id === lastAppliedSelectionIdRef.current) return;
    lastAppliedSelectionIdRef.current = activeSelection.id;
    const savedInputs = getHistoryInputs(activeSelection.purchasePlannerState);
    if (savedInputs) {
      setInputs(savedInputs);
      setPropertyPriceUserAdjusted(true);
      setSelectedExample(activeSelection.id);
      setReverseModeType(null);
      setTargetMaxLoan(0);
      setTargetPropertyPrice(0);
      return;
    }
    applySharedProfiles(
      activeSelection.profiles,
      activeSelection.id,
      getPurchasePlannerTargetPrice(activeSelection.purchasePlannerState)
    );
  }, [activeSelection, applySharedProfiles, getHistoryInputs, getPurchasePlannerTargetPrice]);

  // ── Linked Calculator: register state getter/setter/profile builder ─────
  // Same inputs, but with the monthly incomes Reverse Mode solved from the target
  // loan / property price (see effectiveInputs, assigned further down this render).
  // Everything that persists or exports the borrower reads this — history saves,
  // profile save-back and cross-calculator borrower sync — so the derived income
  // travels instead of the stale pre-reverse slider value.
  const effectiveInputsRef = useRef(inputs);

  useEffect(() => {
    setCtxGetSelectedName(() => getSelectedName() ?? null);
  }, [setCtxGetSelectedName, getSelectedName]);

  useEffect(() => {
    registerStateGetter('maximizer', () => {
      // Reverse-adjusted: a snapshot taken in Reverse Mode stores the solved income,
      // so reloading it reproduces the same affordability without Reverse Mode.
      const curr = effectiveInputsRef.current;
      const tp = curr.proposedPropertyPrice > 0
        ? Math.round(curr.proposedPropertyPrice).toString()
        : undefined;
      return {
        affordabilityState: { inputs: curr },
        targetPurchasePrice: tp,
      };
    });
  }, [registerStateGetter]);

  // Applies a history snapshot pushed by the provider (loads triggered from other
  // faces or Profile Manager's "Maximizer" button) — same path as onHistoryLoad.
  useEffect(() => {
    registerStateSetter('maximizer', (state: unknown) => {
      const savedInputs = getHistoryInputs(
        state as SharedCalculatorActiveSelection['purchasePlannerState'],
      );
      if (!savedInputs) return;
      setInputs(savedInputs);
      setPropertyPriceUserAdjusted(true);
      setReverseModeType(null);
      setTargetMaxLoan(0);
      setTargetPropertyPrice(0);
    });
  }, [registerStateSetter, getHistoryInputs]);

  useEffect(() => {
    registerProfileBuilder('maximizer', () => {
      const curr = effectiveInputsRef.current;
      const profiles: SharedCalculatorProfile[] = [{
        id: '1',
        age: curr.primary.age,
        monthlyIncome: Math.round(getRecognisedIncome(curr.primary)),
        monthlyDebts: curr.primary.existingMonthlyDebt,
        selfEmployedInd: curr.primary.isSelfEmployed ? 1 : 0,
        incomeBasis: 'assessable',
        currentCashSavings: curr.primary.cash,
        currentCPFOA: curr.primary.cpf,
      }];
      if (curr.includeAdditionalBorrower) {
        profiles.push({
          id: '2',
          age: curr.additional.age,
          monthlyIncome: Math.round(getRecognisedIncome(curr.additional)),
          monthlyDebts: curr.additional.existingMonthlyDebt,
          selfEmployedInd: curr.additional.isSelfEmployed ? 1 : 0,
          incomeBasis: 'assessable',
          currentCashSavings: curr.additional.cash,
          currentCPFOA: curr.additional.cpf,
        });
      }
      return profiles;
    });
  }, [registerProfileBuilder]);

  // ── Borrower sync on navigation ─────────────────────────────────────────
  useEffect(() => {
    registerBorrowerExtractor('maximizer', () => ({
      primary: effectiveInputsRef.current.primary,
      additional: effectiveInputsRef.current.additional,
      includeAdditional: effectiveInputsRef.current.includeAdditionalBorrower,
    }));
  }, [registerBorrowerExtractor]);

  useEffect(() => {
    registerBorrowerApplier('maximizer', (data) => {
      setInputs(prev => ({
        ...prev,
        // Spread onto the current borrowers: fields the sender doesn't carry
        // (per-borrower cash/CPF from an older calculator) keep their value.
        primary: { ...prev.primary, ...data.primary },
        additional: { ...prev.additional, ...data.additional },
        includeAdditionalBorrower: data.includeAdditional,
      }));
      setPropertyPriceUserAdjusted(false);
    });
  }, [registerBorrowerApplier]);

  // ── History callbacks for HistoryToolbar ────────────────────────────────

  const onExampleLoad = useCallback(
    (_key: string, profiles: readonly SharedCalculatorProfile[]) => {
      clearSelection();
      setProfileSnapshot(null);
      applySharedProfiles(profiles, _key);
      setActiveSelection({
        id: _key,
        source: 'example',
        profiles,
      });
    },
    [applySharedProfiles, clearSelection, setActiveSelection],
  );

  const onHistoryLoad = useCallback(
    (entry: { profiles: readonly SharedCalculatorProfile[]; propertyType?: unknown; purchasePlannerState?: SharedCalculatorActiveSelection['purchasePlannerState'] }) => {
      // Profile attach/detach on history load is owned by the HistoryToolbar:
      // it switches to the entry's saved profile, or clears an untied one.
      const savedInputs = getHistoryInputs(entry.purchasePlannerState);
      if (savedInputs) {
        setInputs(savedInputs);
        setPropertyPriceUserAdjusted(true);
        setReverseModeType(null);
        setTargetMaxLoan(0);
        setTargetPropertyPrice(0);
      } else if (entry.profiles?.length) {
        applySharedProfiles(
          entry.profiles,
          selectedExample,
          getPurchasePlannerTargetPrice(entry.purchasePlannerState),
        );
      }
    },
    [applySharedProfiles, getHistoryInputs, getPurchasePlannerTargetPrice, selectedExample],
  );

  const onBeforeSave = useCallback(
    () => buildLoadedProfileHistoryLabel(getSelectedName(), activeSelection),
    [getSelectedName, activeSelection],
  );

  // ── Profile change detection & save-back ────────────────────────────────

  const borrowerChanged = (a: BorrowerState, b: BorrowerState) =>
    a.age !== b.age ||
    a.monthlyIncome !== b.monthlyIncome ||
    a.otherAnnualIncome !== b.otherAnnualIncome ||
    a.isSelfEmployed !== b.isSelfEmployed ||
    a.existingMonthlyDebt !== b.existingMonthlyDebt ||
    (a.name ?? '') !== (b.name ?? '') ||
    a.cash !== b.cash ||
    a.cpf !== b.cpf;

  // Keep the itemised debt breakdown when the aggregate is unchanged; the
  // calculator only knows the total, so a changed total is parked under OtherLoan.
  const borrowerDebtFields = (member: CalculatorProfile, totalDebt: number) => {
    const existing = Math.round(
      (member.CarLoan ?? 0) + (member.HousingLoan ?? 0) + (member.CreditCard ?? 0) + (member.OtherLoan ?? 0)
    );
    return existing === Math.round(totalDebt)
      ? {}
      : { CarLoan: 0, HousingLoan: 0, CreditCard: 0, OtherLoan: Math.round(totalDebt) };
  };

  // Core save-back: writes the borrower inputs to the loaded profile. Toast-free
  // so the HistoryToolbar can fold the outcome into its combined save feedback.
  // Reads the reverse-adjusted inputs, so a save made in Reverse Mode stores the
  // income solved from the target loan / property price, not the stale slider value.
  const saveInputsToProfile = async (): Promise<boolean> => {
    if (!selectedProfile) return false;
    const toSave = effectiveInputsRef.current;
    const currentYear = new Date().getFullYear();
    const profiles =
      selectedProfile.type === 'profile'
        ? [selectedProfile.profile]
        : selectedProfile.group.profiles || [];

    if (profiles.length === 0) return false;

    setSavingToProfile(true);
    beginProfileSaveBack();
    try {
      // Save primary borrower
      const primary = profiles[0];
      await updateProfile(primary.Id, {
        Name: toSave.primary.name?.trim() || primary.Name,
        BirthYear: currentYear - toSave.primary.age,
        MonthlyIncome: toSave.primary.monthlyIncome,
        OtherAnnualIncome: toSave.primary.otherAnnualIncome,
        SelfEmployedInd: toSave.primary.isSelfEmployed ? 1 : 0,
        ...borrowerDebtFields(primary, toSave.primary.existingMonthlyDebt),
        Citizenship: primaryCitizenship,
        FtaNationalityInd: primaryFtaEligible ? 1 : 0,
        PropertiesOwned: propertyCountToPropertiesOwned(primaryPropertyCount),
        // Funds are declared per borrower, so each member keeps their own.
        Saving: Math.round(toSave.primary.cash),
        Cpf: Math.round(toSave.primary.cpf),
      });

      // Save additional borrower if present
      if (toSave.includeAdditionalBorrower && profiles.length > 1) {
        const additional = profiles[1];
        await updateProfile(additional.Id, {
          Name: toSave.additional.name?.trim() || additional.Name,
          BirthYear: currentYear - toSave.additional.age,
          MonthlyIncome: toSave.additional.monthlyIncome,
          OtherAnnualIncome: toSave.additional.otherAnnualIncome,
          SelfEmployedInd: toSave.additional.isSelfEmployed ? 1 : 0,
          ...borrowerDebtFields(additional, toSave.additional.existingMonthlyDebt),
          Citizenship: additionalCitizenship,
          FtaNationalityInd: additionalFtaEligible ? 1 : 0,
          PropertiesOwned: propertyCountToPropertiesOwned(additionalPropertyCount),
          Saving: Math.round(toSave.additional.cash),
          Cpf: Math.round(toSave.additional.cpf),
        });
      }

      // Update snapshot to match what was written and the current ABSD state
      setProfileSnapshot({
        primary: { ...toSave.primary },
        additional: { ...toSave.additional },
        includeAdditionalBorrower: toSave.includeAdditionalBorrower,
        primaryCitizenship,
        primaryFtaEligible,
        primaryPropertyCount,
        additionalCitizenship,
        additionalFtaEligible,
        additionalPropertyCount,
      });
      return true;
    } catch (err) {
      console.error('Failed to save profile:', err);
      return false;
    } finally {
      setSavingToProfile(false);
      endProfileSaveBack();
    }
  };

  // Describes the profile(s) "Save to Profile" should create when nothing is loaded:
  // one member per borrower card, named from the card's Name field.
  const buildNewProfilesFromInputs = useCallback((): NewProfileDraft | null => {
    const toSave = effectiveInputsRef.current;
    const currentYear = new Date().getFullYear();
    const member = (
      b: BorrowerState,
      citizenship: CitizenshipType,
      fta: boolean,
      count: PropertyCount,
    ) => ({
      Name: b.name?.trim() || '',
      BirthYear: currentYear - b.age,
      MonthlyIncome: Math.round(b.monthlyIncome),
      OtherAnnualIncome: Math.round(b.otherAnnualIncome),
      Saving: Math.round(b.cash),
      Cpf: Math.round(b.cpf),
      // The calculator only knows the debt total — park it under OtherLoan.
      CarLoan: 0,
      HousingLoan: 0,
      CreditCard: 0,
      OtherLoan: Math.round(b.existingMonthlyDebt),
      SelfEmployedInd: b.isSelfEmployed ? 1 : 0,
      Citizenship: citizenship,
      FtaNationalityInd: fta ? 1 : 0,
      PropertiesOwned: propertyCountToPropertiesOwned(count),
    });

    const members = [member(toSave.primary, primaryCitizenship, primaryFtaEligible, primaryPropertyCount)];
    if (toSave.includeAdditionalBorrower) {
      members.push(member(toSave.additional, additionalCitizenship, additionalFtaEligible, additionalPropertyCount));
    }
    return {
      members,
      groupName: members.map((m) => m.Name).filter(Boolean).join(' & '),
      relationship: absdRelationship === 'SPOUSE' ? 'SPOUSE' : 'INDIVIDUAL',
    };
  }, [
    primaryCitizenship, primaryFtaEligible, primaryPropertyCount,
    additionalCitizenship, additionalFtaEligible, additionalPropertyCount,
    absdRelationship,
  ]);

  const handleSaveToProfile = async () => {
    const ok = await saveInputsToProfile();
    showHistoryToast(ok ? 'Profile updated' : 'Failed to update profile');
  };

  // ── Input Handlers ─────────────────────────────────────────────────────

  const getTenureCapFromInputs = useCallback((nextInputs: InputState): number => {
    const primaryRecognised = getRecognisedIncome(nextInputs.primary);
    const additionalRecognised = nextInputs.includeAdditionalBorrower
      ? getRecognisedIncome(nextInputs.additional)
      : 0;
    const totalRecognisedIncome = primaryRecognised + additionalRecognised;
    const iwaa = nextInputs.includeAdditionalBorrower && totalRecognisedIncome > 0
      ? Math.ceil(
          (nextInputs.primary.age * primaryRecognised + nextInputs.additional.age * additionalRecognised)
          / totalRecognisedIncome
        )
      : nextInputs.primary.age;
    const maxAge = getMaxAgeForLtv(nextInputs.ltv);
    const maxTenure = getMaxTenureForLtv(nextInputs.ltv);
    const ageBasedCap = Math.max(0, maxAge - iwaa);
    return Math.min(maxTenure, ageBasedCap);
  }, []);

  const updatePrimary = (field: keyof BorrowerState, value: number | boolean | string) => {
    setInputs((prev) => {
      const nextInputs = { ...prev, primary: { ...prev.primary, [field]: value } };
      if (field !== 'age') return nextInputs;
      return { ...nextInputs, tenure: getTenureCapFromInputs(nextInputs) };
    });
  };

  const updateAdditional = (field: keyof BorrowerState, value: number | boolean | string) => {
    setInputs((prev) => {
      const nextInputs = { ...prev, additional: { ...prev.additional, [field]: value } };
      if (field !== 'age') return nextInputs;
      return { ...nextInputs, tenure: getTenureCapFromInputs(nextInputs) };
    });
  };

  const updateField = <K extends keyof InputState>(field: K, value: InputState[K]) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const updateAvailableFunds = (field: keyof AvailableFundsState, value: number) => {
    setInputs((prev) => ({
      ...prev,
      availableFunds: {
        ...normalizeAvailableFunds(prev.availableFunds),
        [field]: Math.max(0, Math.round(value)),
      },
    }));
  };

  // ── Reverse Mode Handlers ──────────────────────────────────────────────

  const maxLoanFor = (target: OverviewType) =>
    target === 'private' ? results.maxLoan : results[target].maxLoan;
  const maxPropertyPriceFor = (target: OverviewType) =>
    target === 'private' ? results.maxPropertyPrice : results[target].maxPropertyPrice;

  const handleOpenEditLoan = (target: OverviewType = 'private') => {
    setEditTarget(target);
    const currentLoan = reverseModeType === 'loan' && reverseTarget === target ? targetMaxLoan : maxLoanFor(target);
    setEditLoanInput(Math.round(currentLoan).toLocaleString('en-SG'));
    setEditLoanDialogOpen(true);
  };

  const handleConfirmEditLoan = () => {
    const raw = editLoanInput.replace(/[^0-9.]/g, '');
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
      setTargetMaxLoan(Math.round(val));
      setTargetPropertyPrice(0);
      // Default split based on current recognised income proportions
      if (inputs.includeAdditionalBorrower) {
        const primaryRec = getRecognisedIncome(inputs.primary);
        const additionalRec = getRecognisedIncome(inputs.additional);
        const total = primaryRec + additionalRec;
        const ratio = total > 0 ? primaryRec / total : 0.5;
        setPrimaryIncomeRatio(ratio);
      } else {
        setPrimaryIncomeRatio(1.0);
      }
      setReverseModeType('loan');
      setReverseTarget(editTarget);
      setSelectedType(editTarget);
      setSilentLockMode(false);
      logCalculatorInteraction('affordability', 'mode_switch', { mode: 'target_loan', propertyType: editTarget });
    }
    setEditLoanDialogOpen(false);
  };

  const handleOpenEditPrice = (target: OverviewType = 'private') => {
    setEditTarget(target);
    const currentPrice = reverseModeType === 'propertyPrice' && reverseTarget === target ? targetPropertyPrice : maxPropertyPriceFor(target);
    setEditPriceInput(Math.round(currentPrice).toLocaleString('en-SG'));
    setEditPriceDialogOpen(true);
  };

  const handleConfirmEditPrice = () => {
    const raw = editPriceInput.replace(/[^0-9.]/g, '');
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
      setTargetPropertyPrice(Math.round(val));
      setTargetMaxLoan(0);
      // Default split based on current recognised income proportions
      if (inputs.includeAdditionalBorrower) {
        const primaryRec = getRecognisedIncome(inputs.primary);
        const additionalRec = getRecognisedIncome(inputs.additional);
        const total = primaryRec + additionalRec;
        const ratio = total > 0 ? primaryRec / total : 0.5;
        setPrimaryIncomeRatio(ratio);
      } else {
        setPrimaryIncomeRatio(1.0);
      }
      setReverseModeType('propertyPrice');
      setReverseTarget(editTarget);
      setSelectedType(editTarget);
      setSilentLockMode(false);
      logCalculatorInteraction('affordability', 'mode_switch', { mode: 'target_property_price', propertyType: editTarget });
    }
    setEditPriceDialogOpen(false);
  };

  const exitReverseMode = () => {
    setReverseModeType(null);
    setTargetMaxLoan(0);
    setTargetPropertyPrice(0);
    setSilentLockMode(false);
  };

  // Core: recalculate the split ratio so that one borrower is locked and the other absorbs the rest
  const applyLockAndRecalculate = (target: 'primary' | 'additional', newValue: number) => {
    if (!reverseRequirement) return;
    const totalRequiredRecognised = reverseRequirement.requiredTotalRecognised;

    if (target === 'primary') {
      // newValue is the raw monthly income; total recognised includes other annual income
      const primaryRecognisedFromMonthly = inputs.primary.isSelfEmployed
        ? newValue * SELF_EMPLOYED_HAIRCUT
        : newValue;
      const primaryOtherMonthly = (inputs.primary.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;
      const totalPrimaryRecognised = primaryRecognisedFromMonthly + primaryOtherMonthly;
      setPrimaryIncomeRatio(Math.min(1, Math.max(0, totalPrimaryRecognised / totalRequiredRecognised)));
    } else {
      const additionalRecognisedFromMonthly = inputs.additional.isSelfEmployed
        ? newValue * SELF_EMPLOYED_HAIRCUT
        : newValue;
      const additionalOtherMonthly = (inputs.additional.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;
      const totalAdditionalRecognised = additionalRecognisedFromMonthly + additionalOtherMonthly;
      const additionalShare = Math.min(1, Math.max(0, totalAdditionalRecognised / totalRequiredRecognised));
      setPrimaryIncomeRatio(1 - additionalShare);
    }
  };

  const handleIncomeInteract = (borrowerType: 'primary' | 'additional', newValue: number) => {
    if (reverseMode) {
      if (silentLockMode && inputs.includeAdditionalBorrower) {
        // Already chose "Lock & Recalculate" before — apply directly, no dialog
        applyLockAndRecalculate(borrowerType, newValue);
      } else {
        setIncomeOverrideWarning({ open: true, target: borrowerType, newValue });
      }
    } else {
      if (borrowerType === 'primary') updatePrimary('monthlyIncome', newValue);
      else updateAdditional('monthlyIncome', newValue);
    }
  };

  // Exit reverse mode and apply the user's income directly
  const handleExitAndApplyIncome = () => {
    const { target, newValue } = incomeOverrideWarning;
    setReverseModeType(null);
    setTargetMaxLoan(0);
    setTargetPropertyPrice(0);
    setSilentLockMode(false);
    if (target === 'primary') updatePrimary('monthlyIncome', newValue);
    else updateAdditional('monthlyIncome', newValue);
    setIncomeOverrideWarning({ open: false, target: 'primary', newValue: 0 });
  };

  // Stay in reverse mode, lock this borrower, recalculate the other — and suppress future dialogs
  const handleAdjustOtherBorrower = () => {
    const { target, newValue } = incomeOverrideWarning;
    applyLockAndRecalculate(target, newValue);
    setSilentLockMode(true);
    setIncomeOverrideWarning({ open: false, target: 'primary', newValue: 0 });
  };

  // ── Reverse Calculation ────────────────────────────────────────────────

  // baseResults: always from actual inputs, used for tenure cap (no circularity)
  const baseResults = useMemo(
    () => computeResults(inputs, effectiveInterestRate, stressTestRate, 0, hdbStressTestRate),
    [effectiveInterestRate, stressTestRate, hdbStressTestRate, inputs],
  );

  // Recognised income the target needs. Private: TDSR only. EC / HDB: both MSR (property
  // loan only) and TDSR (net of existing debt) must pass, over that type's tenure. A target
  // needing income past the ceiling is flagged (no new EC / bank loan only), not capped.
  const reverseRequirement = useMemo(() => {
    if (!reverseMode || reverseModeType === null) return null;
    const basis = baseResults.basis[reverseTarget];
    const effectiveLoan = reverseModeType === 'propertyPrice'
      ? Math.round(targetPropertyPrice * (basis.ltv / 100))
      : targetMaxLoan;
    if (effectiveLoan <= 0) return null;
    const tenure = reverseTarget === 'hdb' ? baseResults.hdb.loanTenure : baseResults.effectiveLoanTenure;
    if (tenure <= 0) return null;

    const monthlyPayment = calculateMonthlyPayment(effectiveLoan, basis.stressRate, tenure);
    const totalExistingDebt =
      inputs.primary.existingMonthlyDebt +
      (inputs.includeAdditionalBorrower ? inputs.additional.existingMonthlyDebt : 0);
    const tdsrNeed = (monthlyPayment + totalExistingDebt) / TDSR_RATIO;
    if (reverseTarget === 'private') {
      return { requiredTotalRecognised: tdsrNeed, exceedsCeiling: false, incomeCeiling: 0 };
    }
    const msrNeed = monthlyPayment / MSR_RATIO;
    const incomeCeiling = baseResults[reverseTarget].incomeCeiling;
    const requiredTotalRecognised = Math.max(tdsrNeed, msrNeed);
    return {
      requiredTotalRecognised,
      exceedsCeiling: requiredTotalRecognised > incomeCeiling,
      incomeCeiling,
    };
  }, [
    reverseMode,
    reverseModeType,
    reverseTarget,
    targetMaxLoan,
    targetPropertyPrice,
    inputs.primary.existingMonthlyDebt,
    inputs.includeAdditionalBorrower,
    inputs.additional.existingMonthlyDebt,
    baseResults,
  ]);

  const reverseCalc = useMemo(() => {
    if (!reverseRequirement) return null;
    const { requiredTotalRecognised } = reverseRequirement;

    const ratio = inputs.includeAdditionalBorrower ? primaryIncomeRatio : 1.0;
    const primaryRecognised = Math.max(0, requiredTotalRecognised * ratio);
    const additionalRecognised = Math.max(0, requiredTotalRecognised * (1 - ratio));

    // Subtract other annual income contribution before solving for monthly income
    const primaryOtherMonthly = (inputs.primary.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;
    const primaryRecognisedFromMonthly = Math.max(0, primaryRecognised - primaryOtherMonthly);
    const primaryRaw = inputs.primary.isSelfEmployed
      ? primaryRecognisedFromMonthly / SELF_EMPLOYED_HAIRCUT
      : primaryRecognisedFromMonthly;

    const additionalOtherMonthly = (inputs.additional.otherAnnualIncome / 12) * OTHER_ANNUAL_HAIRCUT;
    const additionalRecognisedFromMonthly = Math.max(0, additionalRecognised - additionalOtherMonthly);
    const additionalRaw = inputs.additional.isSelfEmployed
      ? additionalRecognisedFromMonthly / SELF_EMPLOYED_HAIRCUT
      : additionalRecognisedFromMonthly;

    return {
      primaryEstimatedIncome: Math.max(0, Math.round(primaryRaw)),
      additionalEstimatedIncome: Math.max(0, Math.round(additionalRaw)),
    };
  }, [reverseRequirement, inputs, primaryIncomeRatio]);

  const reverseRecognisedIncomeShares = useMemo(() => {
    if (!reverseRequirement) return null;
    const { requiredTotalRecognised } = reverseRequirement;
    if (requiredTotalRecognised <= 0) return null;

    const ratio = inputs.includeAdditionalBorrower ? primaryIncomeRatio : 1.0;
    return {
      primary: Math.max(0, requiredTotalRecognised * ratio),
      additional: Math.max(0, requiredTotalRecognised * (1 - ratio)),
    };
  }, [reverseRequirement, inputs.includeAdditionalBorrower, primaryIncomeRatio]);

  const primaryOtherAnnualIncomeCap = useMemo(() => {
    if (!reverseRecognisedIncomeShares) return 200000;
    return snapToStep((reverseRecognisedIncomeShares.primary * 12) / OTHER_ANNUAL_HAIRCUT, 0, 200000, 1000);
  }, [reverseRecognisedIncomeShares]);

  const additionalOtherAnnualIncomeCap = useMemo(() => {
    if (!reverseRecognisedIncomeShares || !inputs.includeAdditionalBorrower) return 200000;
    return snapToStep((reverseRecognisedIncomeShares.additional * 12) / OTHER_ANNUAL_HAIRCUT, 0, 200000, 1000);
  }, [reverseRecognisedIncomeShares, inputs.includeAdditionalBorrower]);

  // effectiveInputs: override monthly incomes in reverse mode for display
  const effectiveInputs = useMemo(() => {
    if (!reverseMode || !reverseCalc) return inputs;
    return {
      ...inputs,
      primary: { ...inputs.primary, monthlyIncome: reverseCalc.primaryEstimatedIncome },
      additional: {
        ...inputs.additional,
        monthlyIncome: inputs.includeAdditionalBorrower
          ? reverseCalc.additionalEstimatedIncome
          : inputs.additional.monthlyIncome,
      },
    };
  }, [inputs, reverseMode, reverseCalc]);

  // Keep the borrower-sync ref (declared above) pointing at the reverse-adjusted inputs.
  effectiveInputsRef.current = effectiveInputs;

  // Profile change detection — compares the reverse-adjusted borrower (the values
  // saveInputsToProfile writes), so solving income in Reverse Mode counts as a change.
  // Declared here rather than beside the save-back because it reads effectiveInputs.
  const profileHasChanges = useMemo(() => {
    if (!selectedProfile || !profileSnapshot) return false;
    if (borrowerChanged(effectiveInputs.primary, profileSnapshot.primary)) return true;
    if (effectiveInputs.includeAdditionalBorrower !== profileSnapshot.includeAdditionalBorrower) return true;
    if (effectiveInputs.includeAdditionalBorrower && borrowerChanged(effectiveInputs.additional, profileSnapshot.additional)) return true;
    if (primaryCitizenship !== profileSnapshot.primaryCitizenship) return true;
    if (primaryFtaEligible !== profileSnapshot.primaryFtaEligible) return true;
    if (primaryPropertyCount !== profileSnapshot.primaryPropertyCount) return true;
    if (effectiveInputs.includeAdditionalBorrower && additionalCitizenship !== profileSnapshot.additionalCitizenship) return true;
    if (effectiveInputs.includeAdditionalBorrower && additionalFtaEligible !== profileSnapshot.additionalFtaEligible) return true;
    if (effectiveInputs.includeAdditionalBorrower && additionalPropertyCount !== profileSnapshot.additionalPropertyCount) return true;
    return false;
  }, [
    selectedProfile, profileSnapshot,
    effectiveInputs.primary, effectiveInputs.additional, effectiveInputs.includeAdditionalBorrower,
    primaryCitizenship, primaryFtaEligible, primaryPropertyCount,
    additionalCitizenship, additionalFtaEligible, additionalPropertyCount,
  ]);

  // ── ABSD rate (per-borrower, with married-couple remission) ────────────
  // Derived before the results memo — "Affordable Today" nets ABSD off the
  // buyer's own funds, so the rate is an input to the affordability math.
  const effectivePrimaryCitizenship = useMemo(
    () => getEffectiveCitizenshipForAbsd(primaryCitizenship, primaryFtaEligible),
    [primaryCitizenship, primaryFtaEligible],
  );
  const effectiveAdditionalCitizenship = useMemo(
    () => getEffectiveCitizenshipForAbsd(additionalCitizenship, additionalFtaEligible),
    [additionalCitizenship, additionalFtaEligible],
  );

  const primaryAbsdRate = getAbsdRate(effectivePrimaryCitizenship, primaryPropertyCount);
  const additionalAbsdRate = inputs.includeAdditionalBorrower
    ? getAbsdRate(effectiveAdditionalCitizenship, additionalPropertyCount)
    : 0;
  const isMarriedCouple = inputs.includeAdditionalBorrower && absdRelationship === 'SPOUSE';
  const jointAbsd = inputs.includeAdditionalBorrower
    ? getJointAbsdRate(
        effectivePrimaryCitizenship,
        primaryPropertyCount,
        effectiveAdditionalCitizenship,
        additionalPropertyCount,
        isMarriedCouple,
      )
    : { rate: primaryAbsdRate, remissionApplied: false, rateBeforeRemission: primaryAbsdRate };
  const absdRate = jointAbsd.rate;
  const absdRemissionApplied = jointAbsd.remissionApplied;
  const absdRateBeforeRemission = jointAbsd.rateBeforeRemission;

  // ── Results ────────────────────────────────────────────────────────────

  const results = useMemo(
    () => computeResults(effectiveInputs, effectiveInterestRate, stressTestRate, absdRate, hdbStressTestRate),
    [effectiveInputs, effectiveInterestRate, stressTestRate, absdRate, hdbStressTestRate],
  );

  // ── EC / HDB eligibility ───────────────────────────────────────────────
  // Shared rules (utils/housingEligibility), assessed on the inputs as entered — not the
  // reverse-mode incomes — and on gross household income. A blocked column can't be
  // selected; its figures stay on screen, greyed out, with the reasons.
  const { eligibility, hdbLoanBlockers } = useMemo(() => {
    const input: EligibilityInput = {
      applicants: [
        { label: 'Primary borrower', age: inputs.primary.age, citizenship: primaryCitizenship, propertyCount: primaryPropertyCount },
        ...(inputs.includeAdditionalBorrower
          ? [{ label: 'Additional borrower', age: inputs.additional.age, citizenship: additionalCitizenship, propertyCount: additionalPropertyCount }]
          : []),
      ],
      relationship: absdRelationship === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'SPOUSE',
      grossMonthlyIncome: baseResults.totalRawIncome,
    };
    const ec = assessEcEligibility(input);
    const flat = assessHdbFlatEligibility(input);
    // A buyer who can't take an HDB loan is moved to a bank loan (below), so the HDB
    // loan rules disable that option rather than block the column.
    const loanBlockers = assessHdbLoanEligibility(input).blockers;
    const byType: Record<OverviewType, TypeEligibility> = {
      private: { eligible: true, reasons: [], notes: [] },
      ec: { eligible: ec.eligible, reasons: ec.blockers, notes: ec.notes },
      hdb: { eligible: flat.eligible, reasons: flat.blockers, notes: flat.notes },
    };
    return { eligibility: byType, hdbLoanBlockers: loanBlockers };
  }, [
    inputs.primary.age,
    inputs.additional.age,
    inputs.includeAdditionalBorrower,
    primaryCitizenship,
    primaryPropertyCount,
    additionalCitizenship,
    additionalPropertyCount,
    absdRelationship,
    baseResults.totalRawIncome,
  ]);

  // Not eligible for an HDB loan (e.g. income above the ceiling) → switch the HDB column to a
  // bank loan. The HDB Loan option stays disabled until the buyer qualifies again.
  const hdbLoanAvailable = hdbLoanBlockers.length === 0;
  useEffect(() => {
    if (!hdbLoanAvailable) {
      setInputs((prev) => (prev.hdbLoanSource === 'bank' ? prev : { ...prev, hdbLoanSource: 'bank' }));
    }
  }, [hdbLoanAvailable]);

  // An ineligible type can still be selected for reference, but can't stay the reverse-mode target.
  useEffect(() => {
    if (reverseModeType !== null && !eligibility[reverseTarget].eligible) {
      setReverseModeType(null);
      setTargetMaxLoan(0);
      setTargetPropertyPrice(0);
      setSilentLockMode(false);
    }
  }, [eligibility, reverseModeType, reverseTarget]);

  const setHdbLoanSource = (source: HdbLoanSource) => {
    setInputs((prev) => (prev.hdbLoanSource === source ? prev : { ...prev, hdbLoanSource: source }));
    logCalculatorInteraction('affordability', 'mode_switch', { mode: source === 'hdb' ? 'hdb_loan' : 'bank_loan', propertyType: 'hdb' });
  };

  /** Available Funds as entered — normalised so pre-Available-Funds snapshots read as 0. */
  // Cash and CPF are owned by the borrower cards; Available Funds only holds the
  // two "additional" rows. Both totals stay in `availableFunds` so every
  // downstream calculation keeps reading one shape.
  const borrowerCashTotal = inputs.primary.cash + (inputs.includeAdditionalBorrower ? inputs.additional.cash : 0);
  const borrowerCpfTotal = inputs.primary.cpf + (inputs.includeAdditionalBorrower ? inputs.additional.cpf : 0);
  useEffect(() => {
    setInputs((prev) => {
      const funds = normalizeAvailableFunds(prev.availableFunds);
      if (funds.cash === borrowerCashTotal && funds.cpf === borrowerCpfTotal) return prev;
      return { ...prev, availableFunds: { ...funds, cash: borrowerCashTotal, cpf: borrowerCpfTotal } };
    });
  }, [borrowerCashTotal, borrowerCpfTotal]);

  const availableFunds = useMemo(
    () => normalizeAvailableFunds(inputs.availableFunds),
    [inputs.availableFunds],
  );
  /** Cash-only minimum tranche for a type's basis (MAS 5% at 75% LTV, 10% at 55%; none on an HDB loan). */
  const cashMinimumPercentLabel = (basis: LoanBasis) => `${(basis.cashPercent * 100).toFixed(0)}%`;

  /** Figures for the property type picked in the Affordability Overview. */
  const sel = results.byType[selectedType];
  /** LTV, stress rate and cash minimum the selected type is sized on. */
  const selBasis = results.basis[selectedType];
  const isHdbSelected = selectedType === 'hdb';

  // Auto-sync proposedPropertyPrice → the selected type's max price when user hasn't manually set it
  useEffect(() => {
    if (!propertyPriceUserAdjusted && sel.maxPropertyPrice > 0) {
      setInputs((prev) => (
        prev.proposedPropertyPrice === sel.maxPropertyPrice ? prev : { ...prev, proposedPropertyPrice: sel.maxPropertyPrice }
      ));
    }
  }, [sel.maxPropertyPrice, propertyPriceUserAdjusted]);

  useEffect(() => {
    setUnpledgeInputValue(formatCurrencyDraftForInput(0));
  }, [sel.pledgedFundsNeeded]);

  // ── Stamp duty on the selected property price ──────────────────────────
  const absdAmount = Math.round(results.propertyPrice * (absdRate / 100));
  const absdAmountBeforeRemission = absdRemissionApplied ? Math.round(results.propertyPrice * (absdRateBeforeRemission / 100)) : absdAmount;
  const absdRemissionAmount = absdAmountBeforeRemission - absdAmount;
  const bsdAmount = calculateBsd(results.propertyPrice);
  const totalStampDuty = bsdAmount + absdAmount;

  // ── Format Helpers ─────────────────────────────────────────────────────

  const fullPledgedNeed = sel.pledgedFundsNeeded;
  const fullUnpledgedNeed = sel.unpledgedFundsNeeded;
  const unpledgeParsedForHybrid = parseFlexibleNumber(unpledgeInputValue);
  const effectiveUnpledgedAmount =
    unpledgeParsedForHybrid === null
      ? 0
      : Math.max(0, Math.min(Math.round(unpledgeParsedForHybrid), fullUnpledgedNeed));

  const effectivePledgedAmount =
    fullPledgedNeed <= 0
      ? 0
      : Math.max(
          0,
          Math.min(
            Math.round(fullPledgedNeed - effectiveUnpledgedAmount * UNPLEDGED_RECOGNITION),
            fullPledgedNeed,
          ),
        );

  const adjustedTotalFundsRequired = effectiveUnpledgedAmount + effectivePledgedAmount;

  // ── Initial Outlay ─────────────────────────────────────────────────────
  // Matches the Buying Calculator's Initial Outlay at its default fees: the upfront
  // down payment, stamp duty (BSD always, ABSD only when the buyer profile attracts
  // it) and the closing fees.
  // Private and EC (new launch, Normal Payment Scheme): OTP (Option Fee 5%) + Pay (15%)
  // upfront; the rest of the down payment falls later in the progressive schedule.
  // An HDB resale has none: Option Fee + Exercise Fee on the OTP, then the rest of the
  // down payment at completion — so the whole down payment is upfront. Its closing
  // fees add the caveat (HDB loan only), valuation, HDB admin and agency fees.
  const downPaymentPlan = getDownPaymentPlan(selectedType, results.propertyPrice, sel.requiredLoan, selBasis.ltv);
  const downPaymentStages = downPaymentPlan.stages;
  const upfrontDownPaymentPct = downPaymentPlan.upfrontPct;
  const upfrontDownPayment = downPaymentPlan.upfront;
  const minCashPct = selBasis.cashPercent * 100;
  const upfrontCashOnly = Math.min(upfrontDownPayment, sel.minimumCashNeeded);
  const upfrontFlexible = Math.max(0, upfrontDownPayment - upfrontCashOnly);
  const upfrontFlexiblePct = Math.max(0, upfrontDownPaymentPct - minCashPct);
  // Down payment still owed beyond OTP + Pay (Private / EC), due from Foundation onwards
  // in the progressive schedule.
  const deferredDownPaymentPct = downPaymentPlan.deferredPct;
  const deferredDownPayment = downPaymentPlan.deferred;
  const closingFees = getClosingFees(selectedType, results.propertyPrice, selBasis.hdbLoan);
  const legalFees = closingFees.filter((fee) => fee.group === 'legal');
  const otherFees = closingFees.filter((fee) => fee.group === 'other');
  const totalLegalFees = legalFees.reduce((sum, fee) => sum + fee.amount, 0);
  const totalOtherFees = otherFees.reduce((sum, fee) => sum + fee.amount, 0);
  const initialOutlay = upfrontDownPayment + totalStampDuty + totalLegalFees + totalOtherFees;
  // Pledged / unpledged funds are committed to bridge the loan shortfall, so the
  // Buying Calculator stacks them on top of the outlay as the Grand Total.
  const grandTotalOutlay = initialOutlay + adjustedTotalFundsRequired;

  const unpledgeSliderStep =
    fullUnpledgedNeed <= 0
      ? 1
      : fullUnpledgedNeed > 6_666_667
        ? 50_000
        : fullUnpledgedNeed > 1_666_667
          ? 10_000
          : 5_000;

  const applyUnpledgedAmount = useCallback(
    (n: number) => {
      const clamped = Math.max(0, Math.min(Math.round(n), fullUnpledgedNeed));
      setUnpledgeInputValue(formatCurrencyDraftForInput(clamped));
    },
    [fullUnpledgedNeed],
  );

  // ── Fund-pledging adjustment mode (pledgeMode) ──────────────────────────
  // The user drives ONE of Total / Unpledge / Pledge; the other two are derived.
  // `effectiveUnpledgedAmount` stays the single source of truth; the split always
  // satisfies  P·100% + U·30% = fullPledgedNeed.
  const effectiveTotalAmount = effectivePledgedAmount + effectiveUnpledgedAmount;
  const pledgeStepFor = (maxVal: number): number =>
    maxVal > 6_666_667 ? 50_000 : maxVal > 1_666_667 ? 10_000 : 5_000;
  // Pledge mode: user picks pledged P; unpledged fills the rest → U = (fullPledgedNeed − P) ÷ 30%.
  const applyPledgedAmount = useCallback(
    (p: number) => {
      const clampedP = Math.max(0, Math.min(Math.round(p), fullPledgedNeed));
      applyUnpledgedAmount(UNPLEDGED_RECOGNITION > 0 ? (fullPledgedNeed - clampedP) / UNPLEDGED_RECOGNITION : 0);
    },
    [fullPledgedNeed, applyUnpledgedAmount],
  );
  // Total mode: user picks combined T = P + U. Since P = fullPledgedNeed − 30%·U,
  // T = fullPledgedNeed + (100% − 30%)·U  ⟹  U = (T − fullPledgedNeed) ÷ 70%.
  const applyTotalAmount = useCallback(
    (tVal: number) => {
      const clampedT = Math.max(fullPledgedNeed, Math.min(Math.round(tVal), fullUnpledgedNeed));
      const denom = PLEDGED_RECOGNITION - UNPLEDGED_RECOGNITION;
      applyUnpledgedAmount(denom > 0 ? (clampedT - fullPledgedNeed) / denom : 0);
    },
    [fullPledgedNeed, fullUnpledgedNeed, applyUnpledgedAmount],
  );

  const fmt = (value: number): string =>
    new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  /** Working shown behind each "Affordable Today" card — TDSR and MSR differ only in the loan cap. */
  const buildAffordableTodayLines = (loanCap: number, loanCapLabel: string, price: number, basis: LoanBasis): string[] => {
    const loanDrawn = Math.min(loanCap, Math.round(price * (basis.ltv / 100)));
    const cashPct = cashMinimumPercentLabel(basis);
    return [
      'Highest whole-dollar price P where all three hold:',
      '',
      `1. P <= ${loanCapLabel} + Cash + CPF`,
      `   P <= ${fmt(loanCap)} + ${fmt(results.cashAvailable)} + ${fmt(results.cpfAvailable)} = ${fmt(loanCap + results.totalFundsAvailable)}`,
      '',
      basis.hdbLoan
        ? '2. No cash-only minimum on an HDB loan (CPF can pay the down payment)'
        : `2. ${cashPct} x P <= Cash (cash-only minimum at ${basis.ltv}% LTV)`,
      ...(basis.hdbLoan ? [] : [`   ${cashPct} x P <= ${fmt(results.cashAvailable)}`]),
      '',
      '3. Downpayment(P) + BSD(P) + ABSD(P) <= Cash + CPF',
      `   where Downpayment(P) = P - min(${loanCapLabel}, ${basis.ltv}% x P)`,
      `   and ABSD(P) = P x ${absdRate}%`,
      `   Total capital = ${fmt(results.totalFundsAvailable)}`,
      '',
      `= ${fmt(price)}`,
      '',
      'At that price:',
      `Loan drawn      = ${fmt(loanDrawn)}`,
      `Downpayment     = ${fmt(price - loanDrawn)}`,
      `BSD             = ${fmt(calculateBsd(price))}`,
      `ABSD (${absdRate}%)     = ${fmt(Math.round(price * (absdRate / 100)))}`,
      '',
      'Same basis as the Purchase Planner’s Affordable Today column.',
    ];
  };
  const affordableTodayHelper = (basis: LoanBasis) =>
    results.totalFundsAvailable <= 0
      ? 'Enter your Cash / CPF under Inputs → Available Funds to see what you can buy today.'
      : results.cashAvailable <= 0 && basis.cashPercent > 0
        ? `Cash is $0 — the ${cashMinimumPercentLabel(basis)} cash-only minimum downpayment can't be paid from CPF, so nothing closes today.`
        : undefined;

  const buildMaxLoanLines = (pmtLabel: string, pmt: number, tenureYears: number, loan: number, basis: LoanBasis): string[] => [
    'Max Loan = PMT x [(1+r)^n - 1] / [r x (1+r)^n]',
    '',
    `PMT (${pmtLabel}) = ${fmt(pmt)}`,
    `r (monthly rate from ${basis.stressRateLabel}, Shared Settings) = ${basis.stressRate}% / 12 = ${(basis.stressRate / 12).toFixed(4)}%`,
    `n (months) = ${tenureYears} x 12 = ${tenureYears * 12}`,
    '',
    `= ${fmt(loan)}`,
  ];

  /** Working behind each column's Loan Tenure card — the property type sets its own max tenure. */
  const buildTenureLines = (typeLabel: string, tenureYears: number, basis: LoanBasis): string[] => {
    const ageYears = Math.max(0, basis.maxAge - results.effectiveAgeUsed);
    const maxLabel = basis.hdbLoan ? `${typeLabel} Max (HDB loan)` : `${typeLabel} Max (${basis.ltv}% LTV)`;
    return [
      `Tenure = min(Desired Tenure, Age-Based Years, ${maxLabel})`,
      '',
      `Desired Tenure: ${inputs.tenure} yrs`,
      `Age-Based Years: ${basis.maxAge} - ${results.effectiveAgeUsed} = ${ageYears} yrs`,
      `${maxLabel}: ${basis.maxTenure} yrs`,
      '',
      `= min(${inputs.tenure}, ${ageYears}, ${basis.maxTenure})`,
      `= ${tenureYears} yrs`,
    ];
  };

  // ── Styling ────────────────────────────────────────────────────────────

  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50];
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : theme.palette.divider;
  const highlightColor = isDark ? '#60a5fa' : theme.palette.primary.main;
  const tdsrAccent = highlightColor;
  const msrAccent = isDark ? '#c084fc' : '#7b1fa2';
  const hdbAccent = isDark ? '#2dd4bf' : '#00897b';
  const typeAccent: Record<OverviewType, string> = { private: tdsrAccent, ec: msrAccent, hdb: hdbAccent };
  const selectedAccent = typeAccent[selectedType];
  /** Ratio label for the selected type — EC / HDB must pass MSR as well as TDSR. */
  const selectedRuleLabel = selectedType === 'private' ? 'TDSR' : 'MSR & TDSR';

  const selectType = (type: OverviewType) => {
    if (type === selectedType) return;
    setSelectedType(type);
    logCalculatorInteraction('affordability', 'tab_switch', { tab: type });
  };

  /** Chip in each section header naming the property type its figures are for. */
  const renderTypeChip = () => (
    <Chip
      size="small"
      label={OVERVIEW_TYPE_LABELS[selectedType]}
      sx={{ ml: 1, alignSelf: 'center', height: 22, fontWeight: 700, fontSize: '0.72rem', bgcolor: selectedAccent, color: '#fff' }}
    />
  );

  /** Private | EC | HDB switch below the overview — same selection as tapping a column. */
  const renderTypeSwitch = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Showing calculations for
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={selectedType}
        onChange={(_, v: OverviewType | null) => { if (v) selectType(v); }}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', px: 1.5, py: 0.4 } }}
      >
        {OVERVIEW_TYPES.map((type) => (
          <ToggleButton
            key={type}
            value={type}
            title={eligibility[type].eligible ? undefined : `Not eligible (shown for reference): ${eligibility[type].reasons.join(' ')}`}
            sx={{
              '&.Mui-selected, &.Mui-selected:hover': {
                color: '#fff',
                bgcolor: typeAccent[type],
              },
            }}
          >
            {OVERVIEW_TYPE_LABELS[type]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  const isReverseTarget = (target: OverviewType, type: 'loan' | 'propertyPrice') =>
    reverseModeType === type && reverseTarget === target;
  const privatePriceTarget = isReverseTarget('private', 'propertyPrice');
  const privateLoanTarget = isReverseTarget('private', 'loan');
  /** HDB column: HDB concessionary loan or bank loan, with the basis it sizes the loan on. */
  const renderHdbLoanToggle = (accent: string) => {
    const basis = results.basis.hdb;
    return (
      <Box sx={{ mt: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={inputs.hdbLoanSource}
          onChange={(_, v: HdbLoanSource | null) => { if (v) setHdbLoanSource(v); }}
          aria-label="HDB loan type"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.55rem', sm: '0.72rem' },
              py: { xs: 0.15, sm: 0.3 },
              px: { xs: 0.25, sm: 1 },
              lineHeight: 1.3,
            },
            '& .Mui-selected, & .Mui-selected:hover': { color: '#fff !important', bgcolor: `${accent} !important` },
          }}
        >
          <ToggleButton value="hdb" disabled={!hdbLoanAvailable}>HDB Loan</ToggleButton>
          <ToggleButton value="bank">Bank Loan</ToggleButton>
        </ToggleButtonGroup>
        {!hdbLoanAvailable && hdbLoanBlockers.map((reason) => (
          <Typography key={reason} variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontWeight: 600, lineHeight: 1.35, fontSize: { xs: '0.55rem', sm: '0.7rem' } }}>
            {reason}
          </Typography>
        ))}
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.35, fontSize: { xs: '0.55rem', sm: '0.7rem' } }}>
          {basis.hdbLoan
            ? `${HDB_CONCESSIONARY_INTEREST_RATE}% loan, tested at the ${basis.stressRate}% HDB floor · ${basis.ltv}% LTV · max ${basis.maxTenure} yrs`
            : `Tested at the ${basis.stressRate}% stress rate · ${basis.ltv}% LTV · max ${basis.maxTenure} yrs`}
        </Typography>
      </Box>
    );
  };

  /** What being above each type's income ceiling means — eligibility, never a cap on MSR income. */
  const ceilingConsequence = (target: 'ec' | 'hdb') =>
    target === 'ec' ? 'not eligible for a new EC' : 'bank loan only, no HDB loan';
  const ceilingName = (target: 'ec' | 'hdb') =>
    target === 'ec'
      ? 'EC income ceiling'
      : `HDB loan income ceiling (${inputs.includeAdditionalBorrower ? 'family' : 'single'})`;

  /** Warning under an EC / HDB target that needs income above the type's ceiling. */
  const ceilingTargetWarning = (target: 'ec' | 'hdb') =>
    reverseTarget === target && reverseRequirement?.exceedsCeiling
      ? `Needs income above the ${fmt(reverseRequirement.incomeCeiling)} ceiling — ${ceilingConsequence(target)}`
      : undefined;

  /** EC and HDB: MSR on full income, TDSR on top; HDB also has a shorter tenure. Ceiling is flagged only. */
  const renderMsrSection = (
    target: 'ec' | 'hdb',
    accent: string,
    col: MsrColumn,
  ) => {
    const typeLabel = OVERVIEW_TYPE_LABELS[target];
    const priceTarget = isReverseTarget(target, 'propertyPrice');
    const loanTarget = isReverseTarget(target, 'loan');
    const basis = results.basis[target];
    const { eligible, reasons, notes } = eligibility[target];
    return (
    <RuleSection
      selected={selectedType === target}
      onSelect={() => selectType(target)}
      title={typeLabel}
      ratioLabel="MSR 30%"
      appliesTo="30% of income; TDSR also applies"
      accent={accent}
      cardBorder={cardBorder}
      ineligibleReasons={reasons}
      notes={notes}
      headerExtra={target === 'hdb' ? renderHdbLoanToggle(accent) : undefined}
    >
      <ResultCard
        dense
        cardBg={cardBg}
        cardBorder={cardBorder}
        label={priceTarget ? 'Max Property Price (Target)' : 'Max Property Price'}
        value={fmt(priceTarget ? targetPropertyPrice : col.maxPropertyPrice)}
        onEdit={eligible ? () => handleOpenEditPrice(target) : undefined}
        editActive={priceTarget}
        helperText={priceTarget ? ceilingTargetWarning(target) : undefined}
        formula={priceTarget ? undefined : {
          title: `Max Property Price (${typeLabel})`,
          lines: [
            `Max Property Price = Max Loan (${typeLabel}) / LTV`,
            `${fmt(col.maxLoan)} / ${basis.ltv}%`,
            `= ${fmt(col.maxPropertyPrice)}`,
          ],
        }}
      />
      <ResultCard
        dense
        cardBg={cardBg}
        cardBorder={cardBorder}
        label={loanTarget ? 'Max Loan (Target)' : 'Max Loan'}
        value={fmt(col.maxLoan)}
        onEdit={eligible ? () => handleOpenEditLoan(target) : undefined}
        editActive={loanTarget}
        helperText={loanTarget ? ceilingTargetWarning(target) : undefined}
        formula={{
          title: `Max Loan (${typeLabel})`,
          lines: buildMaxLoanLines('Instalment Cap', col.paymentCap, col.loanTenure, col.maxLoan, basis),
        }}
      />
      <ResultCard
        dense
        cardBg={cardBg}
        cardBorder={cardBorder}
        label="Loan Tenure"
        value={`${col.loanTenure} yrs`}
        formula={{
          title: `Loan Tenure (${typeLabel})`,
          lines: buildTenureLines(typeLabel, col.loanTenure, basis),
        }}
      />
      <ResultCard dense cardBg={cardBg} cardBorder={cardBorder} label="Gross MSR (30% of income)" value={fmt(col.grossMSR)}
        // EC / HDB-loan: being above the ceiling is already a "Not eligible" reason; on a bank
        // loan it is only information.
        helperText={target === 'hdb' && !basis.hdbLoan && col.aboveCeiling ? `Above the ${fmt(col.incomeCeiling)} HDB loan ceiling — bank loan only` : undefined}
        formula={{
          title: `Gross MSR (30%) — ${typeLabel}`,
          lines: [
            'Gross MSR = Recognised Income x 30%',
            `${fmt(results.totalRecognisedIncome)} x 30%`,
            `= ${fmt(col.grossMSR)}`,
            '',
            `${ceilingName(target)}: ${fmt(col.incomeCeiling)}`,
            `Gross household income ${fmt(results.totalRawIncome)} is ${col.aboveCeiling ? 'above' : 'within'} it.`,
            ...(col.aboveCeiling
              ? [
                  target === 'ec'
                    ? 'Above the ceiling the buyer cannot buy a new EC'
                    : 'Above the ceiling the buyer cannot take an HDB loan',
                  target === 'ec'
                    ? 'from the developer. The ceiling is an eligibility'
                    : '— a bank loan still works. The ceiling is an eligibility',
                  'test only: MSR always counts the full income.',
                ]
              : []),
            '',
            'MSR counts property loans only — car loans and',
            'other non-property debt are not deducted here,',
            'but they still reduce Available TDSR.',
          ],
        }}
      />
      <ResultCard dense cardBg={cardBg} cardBorder={cardBorder} label="Monthly Instalment Cap (lower of MSR / TDSR)" value={fmt(col.paymentCap)}
        formula={{
          title: `Monthly Instalment Cap (${typeLabel})`,
          lines: [
            `Both MSR and TDSR apply to ${typeLabel} loans,`,
            'so the lower of the two caps the instalment.',
            '',
            'Cap = min(Gross MSR, Available TDSR)',
            `min(${fmt(col.grossMSR)}, ${fmt(results.availableTDSR)})`,
            `= ${fmt(col.paymentCap)}`,
            '',
            col.msrBinding ? 'MSR is the tighter limit.' : 'TDSR is the tighter limit (existing debt).',
          ],
        }}
      />
      <ResultCard
        dense
        cardBg={cardBg}
        cardBorder={cardBorder}
        label="Affordable Today (with Available Funds)"
        value={fmt(col.affordableToday)}
        highlight={results.totalFundsAvailable > 0 ? accent : undefined}
        helperText={affordableTodayHelper(basis)}
        formula={{
          title: `Affordable Today (${typeLabel})`,
          lines: buildAffordableTodayLines(col.maxLoan, `Max Loan (${typeLabel})`, col.affordableToday, basis),
        }}
      />
    </RuleSection>
    );
  };

  // When split-scroll is on (desktop only), each column gets its own scroll region so the
  // results on the right stay visible while filling in inputs on the left.
  // Column cap: 64px app bar + 24px container bottom padding + 24px breathing room at the
  // top — so at max page scroll the two panels fill nearly the whole viewport.
  const columnScrollSx = (splitScroll && !isMobile)
    ? { maxHeight: 'calc(100vh - 112px)', overflowY: 'auto' as const, pr: 1 }
    : undefined;

  const renderStampDutyMultiBorrowerNote = () => (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: cardBorder,
        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : theme.palette.grey[100],
      }}
    >
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
        More than 2 borrowers?
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.55 }}>
        Select the 2 profiles with the highest ABSD impact:
        <br />
        → Higher-risk citizenship (Foreigner {'>'} PR {'>'} SC)
        <br />
        → Then higher property count
        <br />
        <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
          This ensures a more accurate estimate.
        </Box>
      </Typography>
    </Box>
  );

  // Use baseResults for tenure cap to avoid circularity in reverse mode
  const maxTenureForLtv = getMaxTenureForLtv(inputs.ltv);
  const maxAgeForLtv = getMaxAgeForLtv(inputs.ltv);
  const tenureCap = Math.min(maxTenureForLtv, baseResults.ageBasedCap);

  // ── PDF export ─────────────────────────────────────────────────────────
  /** One property type's figures for the report — the same numbers as its overview column. */
  const buildPdfTypeReport = (type: OverviewType): AffordabilityPdfTypeReport => {
    const outcome = results.byType[type];
    const priceTarget = isReverseTarget(type, 'propertyPrice');
    const col = type === 'private' ? null : results[type];
    return {
      type,
      ...outcome,
      maxPropertyPrice: priceTarget ? targetPropertyPrice : outcome.maxPropertyPrice,
      maxPropertyPriceIsTarget: priceTarget,
      maxTenureForType: results.basis[type].maxTenure,
      affordableToday: col ? col.affordableToday : results.affordableToday,
      basis: results.basis[type],
      eligibility: eligibility[type],
      msr: col && type !== 'private'
        ? {
            grossMSR: col.grossMSR,
            incomeCeiling: col.incomeCeiling,
            aboveCeiling: col.aboveCeiling,
            msrBinding: col.msrBinding,
            ceilingLabel: ceilingName(type),
            ceilingConsequence: ceilingConsequence(type),
          }
        : undefined,
      // The pledged / unpledged mix on screen belongs to the selected type only.
      plannedMix: type === selectedType && effectiveUnpledgedAmount > 0
        ? { unpledged: effectiveUnpledgedAmount, pledged: effectivePledgedAmount }
        : undefined,
    };
  };

  const openPdfDialog = () => {
    setPdfChoice(selectedType);
    setPdfDialogOpen(true);
  };

  const runPdfExport = async () => {
    if (pdfExporting) return;
    setPdfExporting(true);
    try {
      const profiles = getSelectedProfiles();
      logPdfExport('affordability', currentUser);
      await exportAffordabilityToPDF({
        inputs: {
          primary: effectiveInputs.primary,
          includeAdditionalBorrower: effectiveInputs.includeAdditionalBorrower,
          additional: effectiveInputs.additional,
          tenure: inputs.tenure,
          ltv: inputs.ltv,
          bankRate: effectiveInterestRate,
          bankRateLabel: effectiveInterestRateLabel,
          stressTestRate,
          stressTestRateLabel,
        },
        summary: {
          totalRecognisedIncome: results.totalRecognisedIncome,
          totalRawIncome: results.totalRawIncome,
          totalExistingDebt: results.totalExistingDebt,
          grossTDSR: results.grossTDSR,
          availableTDSR: results.availableTDSR,
          iwaa: results.iwaa,
          effectiveAgeUsed: results.effectiveAgeUsed,
          ageBasedCap: results.ageBasedCap,
          propertyPrice: results.propertyPrice,
          requiredLoan: results.requiredLoan,
          cashAvailable: results.cashAvailable,
          cpfAvailable: results.cpfAvailable,
          totalFundsAvailable: results.totalFundsAvailable,
        },
        reports: (pdfChoice === 'all' ? OVERVIEW_TYPES : [pdfChoice]).map(buildPdfTypeReport),
        profileName: selectedProfile ? getSelectedName() ?? undefined : undefined,
        primaryBorrowerName: profiles[0]?.Name ?? undefined,
        additionalBorrowerName: profiles.length > 1 ? profiles[1]?.Name ?? undefined : undefined,
        agentInfo: currentUser ?? undefined,
        stampDuty: {
          bsd: bsdAmount,
          absd: absdAmount,
          total: totalStampDuty,
          propertyPrice: results.propertyPrice,
          absdRatePercent: absdRate,
          absdRemissionApplied,
          absdRateBeforeRemissionPercent: absdRemissionApplied ? absdRateBeforeRemission : undefined,
          absdAmountBeforeRemission: absdRemissionApplied ? absdAmountBeforeRemission : undefined,
        },
      });
      setPdfDialogOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('PDF export failed', error);
    } finally {
      setPdfExporting(false);
    }
  };

  // Auto-clamp tenure to the cap whenever the cap decreases below current tenure
  // (e.g. adding/removing a borrower changes IWAA, which changes the cap)
  useEffect(() => {
    if (tenureCap > 0 && inputs.tenure > tenureCap) {
      setInputs((prev) => ({ ...prev, tenure: tenureCap }));
    }
  }, [inputs.tenure, tenureCap]);

  useEffect(() => {
    if (!reverseMode) return;

    setInputs((prev) => {
      let changed = false;
      const nextPrimaryOtherAnnualIncome = Math.min(prev.primary.otherAnnualIncome, primaryOtherAnnualIncomeCap);
      const nextAdditionalOtherAnnualIncome = Math.min(prev.additional.otherAnnualIncome, additionalOtherAnnualIncomeCap);

      const nextPrimary = nextPrimaryOtherAnnualIncome !== prev.primary.otherAnnualIncome
        ? { ...prev.primary, otherAnnualIncome: nextPrimaryOtherAnnualIncome }
        : prev.primary;
      if (nextPrimary !== prev.primary) changed = true;

      const shouldClampAdditional = prev.includeAdditionalBorrower
        && nextAdditionalOtherAnnualIncome !== prev.additional.otherAnnualIncome;
      const nextAdditional = shouldClampAdditional
        ? { ...prev.additional, otherAnnualIncome: nextAdditionalOtherAnnualIncome }
        : prev.additional;
      if (nextAdditional !== prev.additional) changed = true;

      return changed ? { ...prev, primary: nextPrimary, additional: nextAdditional } : prev;
    });
  }, [reverseMode, primaryOtherAnnualIncomeCap, additionalOtherAnnualIncomeCap]);

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: '100%', maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
          Max Affordability Calculator
        </Typography>
        <TutorialLaunchButton controller={tutorial} label="the Max Affordability Calculator" />
        <WhatsNewLaunchButton controller={whatsNew} label="the Max Affordability Calculator" />
      </Box>

      {/* Load Example / History + Actions */}
      <Box data-tutorial="load-example">
      <HistoryToolbar
        analyticsCalculator="affordability"
        onExampleLoad={onExampleLoad}
        onHistoryLoad={onHistoryLoad}
        onBeforeSave={onBeforeSave}
        onSaveToProfile={saveInputsToProfile}
        onBuildNewProfiles={buildNewProfilesFromInputs}
        onBeforeProfileCreate={beginProfileSaveBack}
        onAfterProfileCreate={endProfileSaveBack}
        onClear={() => {
          clearSelection();
          setProfileSnapshot(null);
        }}
        extraButtons={
          <>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={() => setClearDialogOpen(true)}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={openPdfDialog}
            >
              Export PDF
            </Button>
            {selectedProfile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Profile loaded: <strong>{getSelectedName()}</strong>
                </Typography>
              </Box>
            )}
          </>
        }
      />

      </Box>

      {/* Text size + scroll-mode controls */}
      <Box data-tutorial="display-controls" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        {/* Text size — smaller / larger (shared across calculators) */}
        <TextSizeControl />
        {!isMobile && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={splitScroll ? 'split' : 'single'}
            onChange={(_, v) => { if (v !== null) setSplitScroll(v === 'split'); }}
            sx={{
              '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', px: 1.25, py: 0.5 },
              '& .Mui-selected': { color: '#D97706 !important', bgcolor: 'rgba(217,119,6,0.08) !important' },
            }}
          >
            <Tooltip title="Page scrolls as one column">
              <ToggleButton value="single">1 Scroll</ToggleButton>
            </Tooltip>
            <Tooltip title="Inputs and results scroll independently">
              <ToggleButton value="split">2 Scroll</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Main Layout */}
      <Grid container spacing={3}>
        {/* ── LEFT: Inputs ──────────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Box sx={columnScrollSx}>
          {/* Profile Banner */}
          <ProfileBanner />
          <Card
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderColor: cardBorder,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Inputs
              </Typography>
              {reverseMode && (
                <Tooltip
                  title={
                    reverseModeType === 'propertyPrice'
                      ? `Reverse via ${OVERVIEW_TYPE_LABELS[reverseTarget]} Property Price: ${new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(targetPropertyPrice)}`
                      : `Reverse via ${OVERVIEW_TYPE_LABELS[reverseTarget]} Max Loan: ${new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(targetMaxLoan)}`
                  }
                >
                  <Chip
                    label={
                      reverseModeType === 'propertyPrice'
                        ? `Reverse: ${OVERVIEW_TYPE_LABELS[reverseTarget]} Property Price → Income`
                        : `Reverse: ${OVERVIEW_TYPE_LABELS[reverseTarget]} Max Loan → Income`
                    }
                    onDelete={exitReverseMode}
                    deleteIcon={<CloseIcon />}
                    size="small"
                    sx={{
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      maxWidth: { xs: 'min(100%, 220px)', sm: 'none' },
                      '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center', py: 0.25 },
                      '& .MuiChip-deleteIcon': { color: 'warning.contrastText' },
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Primary Borrower */}
            <Box data-tutorial="primary-borrower">
            <BorrowerSection
              title={inputs.primary.name?.trim() ? `Primary Borrower - ${inputs.primary.name.trim()}` : 'Primary Borrower'}
              borrower={inputs.primary}
              onUpdate={updatePrimary}
              cardBg={cardBg}
              cardBorder={cardBorder}
              highlightColor={highlightColor}
              isMobile={isMobile}
              fmt={fmt}
              estimatedIncome={reverseMode && reverseCalc ? reverseCalc.primaryEstimatedIncome : undefined}
              otherAnnualIncomeCap={reverseMode ? primaryOtherAnnualIncomeCap : undefined}
              onIncomeChange={(val) => handleIncomeInteract('primary', val)}
              reverseHint={
                silentLockMode && inputs.includeAdditionalBorrower
                  ? 'Slide to set — Additional borrower adjusts automatically to keep the target loan.'
                  : inputs.includeAdditionalBorrower
                    ? 'Tap the slider to choose how to distribute income between borrowers.'
                    : 'Tap the slider to manually adjust — this will exit Reverse Mode.'
              }
            />

            </Box>

            {/* Additional Borrower Toggle */}
            <Box data-tutorial="additional-borrower" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Additional Borrower
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inputs.includeAdditionalBorrower}
                    onChange={(e) => updateField('includeAdditionalBorrower', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Include additional borrower</Typography>}
              />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                When added, loan tenure uses <strong>Income Weighted Average Age (IWAA)</strong>, where
                the higher-income borrower has a larger impact on the effective age.
              </Typography>
            </Box>

            {/* Additional Borrower Fields */}
            {inputs.includeAdditionalBorrower && (
              <>
                <BorrowerSection
                  title={inputs.additional.name?.trim() ? `Additional Borrower - ${inputs.additional.name.trim()}` : 'Additional Borrower'}
                  borrower={inputs.additional}
                  onUpdate={updateAdditional}
                  cardBg={cardBg}
                  cardBorder={cardBorder}
                  highlightColor={highlightColor}
                  isMobile={isMobile}
                  fmt={fmt}
                  estimatedIncome={reverseMode && reverseCalc ? reverseCalc.additionalEstimatedIncome : undefined}
                  otherAnnualIncomeCap={reverseMode ? additionalOtherAnnualIncomeCap : undefined}
                  onIncomeChange={(val) => handleIncomeInteract('additional', val)}
                  reverseHint={
                    silentLockMode
                      ? 'Slide to set — Primary borrower adjusts automatically to keep the target loan.'
                      : 'Tap the slider to choose how to distribute income between borrowers.'
                  }
                />

                {/* Income Split Slider (only in reverse mode, joint borrower) */}
                {reverseMode && reverseCalc && (
                  <Card
                    variant="outlined"
                    sx={{ p: 2, bgcolor: cardBg, borderColor: 'warning.main', borderWidth: 2, mb: 2 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: 'warning.main' }}>
                      Income Split
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                      Drag to redistribute the estimated income between borrowers. Total stays fixed to the target loan.
                    </Typography>
                    <EditableSliderValueRow
                      label={<Typography variant="body2" fontWeight={600}>Primary income share</Typography>}
                      value={Math.round(primaryIncomeRatio * 100)}
                      min={0}
                      max={100}
                      step={1}
                      onCommit={(v) => setPrimaryIncomeRatio(v / 100)}
                      formatDisplay={(v) => `${v}%`}
                      valueColor="warning.main"
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                      Primary: {fmt(reverseCalc.primaryEstimatedIncome)} · Additional: {fmt(reverseCalc.additionalEstimatedIncome)}
                    </Typography>
                    <IntentGuardedSlider
                      isMobile={isMobile}
                      value={Math.round(primaryIncomeRatio * 100)}
                      min={0}
                      max={100}
                      step={5}
                      onChange={(_, val) => setPrimaryIncomeRatio((val as number) / 100)}
                      sx={{ color: 'warning.main', height: 6 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>All Additional</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>All Primary</Typography>
                    </Box>
                  </Card>
                )}
              </>
            )}

            {/* ── Available Funds (drives Affordable Today) ──────────────── */}
            <Box data-tutorial="available-funds" sx={{
              p: 2,
              mb: 2,
              bgcolor: cardBg,
              borderRadius: 2,
              border: 1,
              borderColor: cardBorder,
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: highlightColor }}>
                Available Funds
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                Cash and CPF you can commit today. Drives <strong>Affordable Today</strong> under
                Affordability Overview — the highest price your funds cover after downpayment, BSD and ABSD.
              </Typography>

              {([
                { field: 'cash' as const, label: 'Cash', hint: 'From each borrower\u2019s Cash / Saving' },
                { field: 'additionalCash' as const, label: 'Additional Cash', hint: 'Sales proceed / other cash coming in' },
                { field: 'cpf' as const, label: 'CPF', hint: 'From each borrower\u2019s CPF OA Balance' },
                { field: 'additionalCpf' as const, label: 'Additional CPF', hint: 'From CPF withdrawal / sales proceed' },
              ]).map(({ field, label, hint }, i, arr) => {
                // Cash and CPF are entered on the borrower cards — shown here as a
                // read-only total, broken down per borrower when there are two.
                const derived = field === 'cash' || field === 'cpf';
                const people = derived
                  ? [
                      { name: inputs.primary.name?.trim() || 'Primary borrower', amount: inputs.primary[field] },
                      ...(inputs.includeAdditionalBorrower
                        ? [{ name: inputs.additional.name?.trim() || 'Additional borrower', amount: inputs.additional[field] }]
                        : []),
                    ]
                  : [];
                return (
                  <React.Fragment key={field}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight={600}>{label}</Typography>
                          {derived && (
                            <InfoOutlinedIcon
                              onClick={() => setFundsDialog({
                                title: `${label} Available`,
                                lines: [
                                  ...people.map((p, idx) => `${idx === 0 ? '' : '+ '}${p.name}: ${fmt(p.amount)}`),
                                  `= Total ${label}: ${fmt(availableFunds[field])}`,
                                ],
                              })}
                              sx={{ fontSize: 15, color: 'primary.main', cursor: 'pointer', ml: 0.5 }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">{hint}</Typography>
                      </Box>
                      {derived ? (
                        <Typography variant="body2" fontWeight={700} sx={{ width: 170, flexShrink: 0, textAlign: 'right' }}>
                          {fmt(availableFunds[field])}
                        </Typography>
                      ) : (
                        <Box sx={{ width: 170, flexShrink: 0 }}>
                          <CurrencyTextField
                            value={availableFunds[field]}
                            onChange={(v) => updateAvailableFunds(field, v)}
                            highlightColor={highlightColor}
                          />
                        </Box>
                      )}
                    </Box>
                    {i < arr.length - 1 && <Divider sx={{ mb: 1, opacity: 0.5 }} />}
                  </React.Fragment>
                );
              })}

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Total cash</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt(results.cashAvailable)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Total CPF</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt(results.cpfAvailable)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={700}>Total capital</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: highlightColor }}>
                  {fmt(results.totalFundsAvailable)}
                </Typography>
              </Box>
            </Box>

            {/* Loan Tenure */}
            <Box data-tutorial="loan-tenure" sx={{ mb: 2 }}>
              <EditableSliderValueRow
                label={<Typography variant="body2" fontWeight={700}>Loan Tenure</Typography>}
                value={inputs.tenure}
                min={1}
                max={Math.max(1, tenureCap)}
                step={1}
                onCommit={(v) => updateField('tenure', v)}
                formatDisplay={(v) => `${v} yrs`}
                valueColor={highlightColor}
              />
              <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                <IntentGuardedSlider
                  isMobile={isMobile}
                  value={inputs.tenure}
                  min={1}
                  max={tenureCap}
                  step={1}
                  onChange={(_, val) => updateField('tenure', val as number)}
                  sx={{ color: highlightColor, height: 6, display: 'block' }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cap: {tenureCap} yrs
                </Typography>
              </Box>

              {/* Age basis: show IWAA when joint, plain age when single */}
              {inputs.includeAdditionalBorrower ? (
                <Box
                  onClick={() => setIwaaDialogOpen(true)}
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: cardBg,
                    border: 1,
                    borderColor: cardBorder,
                    cursor: 'pointer',
                    '&:hover': { borderColor: highlightColor },
                  }}
                >
                  <Typography variant="body2" fontWeight={700} sx={{ color: highlightColor }}>
                    IWAA: {results.iwaa} years old
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Tap to see calculation
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                  Based on borrower age: <strong>{inputs.primary.age}</strong>.
                </Typography>
              )}

              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                Applied LTV is <strong style={{ color: highlightColor }}>{inputs.ltv}%</strong>.
                Loan tenure is capped at <strong>{maxTenureForLtv} years</strong> and must not go beyond{' '}
                <strong style={{ color: highlightColor }}>age {maxAgeForLtv}</strong>.
                {inputs.includeAdditionalBorrower ? (
                  <> Current cap: <strong>{tenureCap} years</strong>.</>
                ) : (
                  <>
                    {' '}
                    Current age: <strong style={{ color: highlightColor }}>{inputs.primary.age}</strong>.
                    Current cap: <strong>{tenureCap} years</strong>.
                  </>
                )}
              </Typography>
            </Box>

            {/* IWAA Calculation Dialog */}
            <Dialog open={iwaaDialogOpen} onClose={() => setIwaaDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: 700 }}>
                IWAA Calculation
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Income Weighted Average Age (IWAA) gives the higher-income borrower
                  a larger impact on the effective age used for tenure calculation.
                </Typography>

                <Box sx={{ bgcolor: cardBg, p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Formula</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    IWAA = ceil( Sum(Age_i x Income_i) / Sum(Income_i) )
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Borrower 1 (Primary)</Typography>
                  <Typography variant="body2">
                    Age: {inputs.primary.age} yrs | Recognised Income: {fmt(getRecognisedIncome(inputs.primary))}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Borrower 2 (Additional)</Typography>
                  <Typography variant="body2">
                    Age: {inputs.additional.age} yrs | Recognised Income: {fmt(getRecognisedIncome(inputs.additional))}
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: cardBg, p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Calculation</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', lineHeight: 2 }}>
                    = ceil( ({inputs.primary.age} x {fmt(getRecognisedIncome(inputs.primary))} + {inputs.additional.age} x {fmt(getRecognisedIncome(inputs.additional))}) / ({fmt(getRecognisedIncome(inputs.primary))} + {fmt(getRecognisedIncome(inputs.additional))}) )
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', lineHeight: 2 }}>
                    = ceil( {fmt(inputs.primary.age * getRecognisedIncome(inputs.primary) + inputs.additional.age * getRecognisedIncome(inputs.additional))} / {fmt(getRecognisedIncome(inputs.primary) + getRecognisedIncome(inputs.additional))} )
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', lineHeight: 2, fontWeight: 700, color: highlightColor }}>
                    = {results.iwaa} years
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: cardBg, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Tenure Cap</Typography>
                  <Typography variant="body2">
                    Max Age ({maxAgeForLtv}) - IWAA ({results.iwaa}) ={' '}
                    <strong>{results.ageBasedCap} years</strong>
                    {' '}(min with {maxTenureForLtv}-yr LTV cap → <strong>{Math.min(maxTenureForLtv, results.ageBasedCap)} years</strong>)
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIwaaDialogOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Bank interest rate */}
            <Box data-tutorial="interest-rate" sx={{ mb: 2 }}>
              <EditableSliderValueRow
                label={<Typography variant="body2" fontWeight={700}>Bank Interest Rate</Typography>}
                value={inputs.bankInterestRate}
                min={0.5}
                max={6.0}
                step={0.01}
                onCommit={(v) => updateField('bankInterestRate', v)}
                formatDisplay={(v) => `${v.toFixed(2)}%`}
                valueColor={highlightColor}
              />
              <Box sx={{ pl: 1.5, pr: 3, overflow: 'hidden' }}>
                <IntentGuardedSlider
                  isMobile={isMobile}
                  value={inputs.bankInterestRate}
                  min={0.5}
                  max={6.0}
                  step={0.05}
                  onChange={(_, val) => updateField('bankInterestRate', val as number)}
                  sx={{ color: highlightColor, height: 6, display: 'block' }}
                />
              </Box>
            </Box>

            {/* Applied LTV */}
            <Box data-tutorial="applied-ltv" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Applied LTV
              </Typography>
              <RadioGroup
                row
                value={Number(inputs.ltv) === 55 ? '55' : '75'}
                onChange={(e) => {
                  const v = e.target.value === '55' ? 55 : 75;
                  updateField('ltv', v);
                }}
              >
                <FormControlLabel value="75" control={<Radio size="small" />} label="75%" />
                <FormControlLabel value="55" control={<Radio size="small" />} label="55%" />
              </RadioGroup>
            </Box>

            {/* ── Property Price & income-support mix (unpledged) ───── */}
            <Box data-tutorial="purchase-funding" sx={{
              p: 2,
              mb: 2,
              bgcolor: cardBg,
              borderRadius: 2,
              border: 1,
              borderColor: cardBorder,
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, color: highlightColor }}>
                Purchase & Funding
              </Typography>

              {/* Property Price */}
              <Box sx={{ mb: 2 }}>
                <EditableSliderValueRow
                  label={<Typography variant="body2" fontWeight={700}>Property Price</Typography>}
                  value={inputs.proposedPropertyPrice}
                  min={0}
                  max={propertyPriceSliderMax}
                  commitMax={INCOME_ABSOLUTE_MAX}
                  step={1}
                  onCommit={(v) => { setPropertyPriceUserAdjusted(true); updateField('proposedPropertyPrice', v); }}
                  formatDisplay={(v) => fmt(v)}
                  valueColor={highlightColor}
                  currencyInput
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => { setPropertyPriceUserAdjusted(true); updateField('proposedPropertyPrice', Math.max(0, inputs.proposedPropertyPrice - 50000)); }}
                    sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <IntentGuardedSlider
                    isMobile={isMobile}
                    value={inputs.proposedPropertyPrice}
                    min={0}
                    max={propertyPriceSliderMax}
                    step={10000}
                    onChange={(_, val) => { setPropertyPriceUserAdjusted(true); updateField('proposedPropertyPrice', val as number); }}
                    sx={{ color: highlightColor, height: 6, flex: 1, mx: 2 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => { setPropertyPriceUserAdjusted(true); updateField('proposedPropertyPrice', Math.min(INCOME_ABSOLUTE_MAX, inputs.proposedPropertyPrice + 50000)); }}
                    sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Income-support funds (pledged / unpledged mix) */}
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Income-support funds
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                Bridge the {selectedRuleLabel} income-support gap for <strong>{OVERVIEW_TYPE_LABELS[selectedType]}</strong> with pledged (100% recognised, locked 48 months) or unpledged
                (30% recognised) assets. Full amounts are shown under <strong>Income Support Needed to Pass {selectedRuleLabel}</strong> on the right.
              </Typography>
              {fullPledgedNeed <= 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  No additional income support gap at current settings.
                </Typography>
              ) : (() => {
                const modeCfg = pledgeMode === 'pledge'
                  ? { label: 'Amount you will pledge', value: effectivePledgedAmount, min: 0, max: fullPledgedNeed,
                      step: pledgeStepFor(fullPledgedNeed), commit: applyPledgedAmount,
                      hint: 'You set the pledged amount — the unpledged amount fills the rest.' }
                  : pledgeMode === 'total'
                  ? { label: 'Total assets you will commit', value: effectiveTotalAmount, min: fullPledgedNeed, max: fullUnpledgedNeed,
                      step: unpledgeSliderStep, commit: applyTotalAmount,
                      hint: 'You set the total — it is split into pledged & unpledged automatically.' }
                  : { label: 'Amount you will leave unpledged', value: effectiveUnpledgedAmount, min: 0, max: fullUnpledgedNeed,
                      step: unpledgeSliderStep, commit: applyUnpledgedAmount,
                      hint: 'You set the unpledged amount — the pledged amount fills the rest.' };
                return (
                  <>
                    {/* Adjustment mode — pick which figure you drive */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em' }}>ADJUST BY</Typography>
                      <ToggleButtonGroup
                        size="small" exclusive value={pledgeMode}
                        onChange={(_, v) => { if (v !== null) setPledgeMode(v); }}
                        sx={{
                          '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', px: 1.1, py: 0.3 },
                          '& .Mui-selected': { color: '#D97706 !important', bgcolor: 'rgba(217,119,6,0.08) !important' },
                        }}
                      >
                        <ToggleButton value="total">Total</ToggleButton>
                        <ToggleButton value="unpledge">Unpledge</ToggleButton>
                        <ToggleButton value="pledge">Pledge</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <EditableSliderValueRow
                      label={<Typography variant="body2">{modeCfg.label}</Typography>}
                      value={modeCfg.value}
                      min={modeCfg.min}
                      max={modeCfg.max}
                      step={modeCfg.step}
                      onCommit={modeCfg.commit}
                      formatDisplay={(v) => fmt(v)}
                      valueColor={highlightColor}
                      currencyInput
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => modeCfg.commit(modeCfg.value - modeCfg.step)}
                        sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
                        aria-label="Decrease"
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <IntentGuardedSlider
                        isMobile={isMobile}
                        value={modeCfg.value}
                        min={modeCfg.min}
                        max={modeCfg.max}
                        step={modeCfg.step}
                        onChange={(_, val) => modeCfg.commit(val as number)}
                        sx={{ color: highlightColor, height: 6, flex: 1 }}
                        aria-label="Income-support funds amount"
                      />
                      <IconButton
                        size="small"
                        onClick={() => modeCfg.commit(modeCfg.value + modeCfg.step)}
                        sx={{ border: 1, borderColor: cardBorder, borderRadius: 1 }}
                        aria-label="Increase"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
                      {modeCfg.hint}
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Pledged needed (adjusted)</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmt(effectivePledgedAmount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Unpledged planned</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmt(effectiveUnpledgedAmount)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight={700}>Total (pledged + unpledged)</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: highlightColor }}>{fmt(effectiveTotalAmount)}</Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>

            {/* ── Stamp Duty Inputs ─────────────────────────────────────── */}
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
              Stamp Duty Payable
            </Typography>
            {renderStampDutyMultiBorrowerNote()}

            {/* Borrower 1 */}
            <Box data-tutorial="stamp-duty-inputs" sx={{ mb: 2, p: 1.5, bgcolor: cardBg, borderRadius: 1, border: 1, borderColor: cardBorder }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                Borrower 1 (Primary)
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Citizenship</Typography>
              <RadioGroup
                row
                value={primaryCitizenship}
                onChange={(e) => {
                  const v = e.target.value as CitizenshipType;
                  setPrimaryCitizenship(v);
                  if (v === 'SC') setPrimaryFtaEligible(false);
                }}
              >
                {(Object.keys(CITIZENSHIP_LABELS) as CitizenshipType[]).map((key) => (
                  <FormControlLabel
                    key={key}
                    value={key}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{CITIZENSHIP_LABELS[key]}</Typography>}
                  />
                ))}
              </RadioGroup>
              {(primaryCitizenship === 'SPR' || primaryCitizenship === 'FOREIGNER') && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2">Eligible for FTA ABSD remission (SC-equivalent rates)?</Typography>
                    <Tooltip
                      title={
                        <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 360, whiteSpace: 'pre-line' }}>
                          {FTA_ABSD_INFO_TOOLTIP}
                        </Typography>
                      }
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <IconButton size="small" aria-label="FTA nationality requirements for ABSD remission" sx={{ p: 0.25 }}>
                        <InfoOutlinedIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <RadioGroup
                    row
                    value={primaryFtaEligible ? 'yes' : 'no'}
                    onChange={(e) => setPrimaryFtaEligible(e.target.value === 'yes')}
                  >
                    <FormControlLabel value="no" control={<Radio size="small" />} label={<Typography variant="body2">No</Typography>} />
                    <FormControlLabel value="yes" control={<Radio size="small" />} label={<Typography variant="body2">Yes</Typography>} />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                    Yes applies SC ABSD rates in this calculator if you meet the nationality rules in the info button. See{' '}
                    <Link href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/foreigners-eligible-for-absd-remission-under-free-trade-agreements-(ftas)" target="_blank" rel="noopener noreferrer" variant="inherit">
                      IRAS
                    </Link>{' '}
                    for how to apply.
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ mb: 0.5, mt: 1 }}>Which Property</Typography>
              <RadioGroup
                row
                value={primaryPropertyCount}
                onChange={(e) => setPrimaryPropertyCount(e.target.value as PropertyCount)}
              >
                {PROPERTY_COUNT_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Box>

            {/* Borrower 2 (only when additional borrower is enabled) */}
            {inputs.includeAdditionalBorrower && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: cardBg, borderRadius: 1, border: 1, borderColor: cardBorder }}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  Borrower 2 (Additional)
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Citizenship</Typography>
                <RadioGroup
                  row
                  value={additionalCitizenship}
                  onChange={(e) => {
                    const v = e.target.value as CitizenshipType;
                    setAdditionalCitizenship(v);
                    if (v === 'SC') setAdditionalFtaEligible(false);
                  }}
                >
                  {(Object.keys(CITIZENSHIP_LABELS) as CitizenshipType[]).map((key) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{CITIZENSHIP_LABELS[key]}</Typography>}
                    />
                  ))}
                </RadioGroup>
                {(additionalCitizenship === 'SPR' || additionalCitizenship === 'FOREIGNER') && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="body2">Eligible for FTA ABSD remission (SC-equivalent rates)?</Typography>
                      <Tooltip
                        title={
                          <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 360, whiteSpace: 'pre-line' }}>
                            {FTA_ABSD_INFO_TOOLTIP}
                          </Typography>
                        }
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                      >
                        <IconButton size="small" aria-label="FTA nationality requirements for ABSD remission" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <RadioGroup
                      row
                      value={additionalFtaEligible ? 'yes' : 'no'}
                      onChange={(e) => setAdditionalFtaEligible(e.target.value === 'yes')}
                    >
                      <FormControlLabel value="no" control={<Radio size="small" />} label={<Typography variant="body2">No</Typography>} />
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={<Typography variant="body2">Yes</Typography>} />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                      Yes applies SC ABSD rates in this calculator if you meet the nationality rules in the info button. See{' '}
                      <Link href="https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/foreigners-eligible-for-absd-remission-under-free-trade-agreements-(ftas)" target="_blank" rel="noopener noreferrer" variant="inherit">
                        IRAS
                      </Link>{' '}
                      for how to apply.
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" sx={{ mb: 0.5, mt: 1 }}>Which Property</Typography>
                <RadioGroup
                  row
                  value={additionalPropertyCount}
                  onChange={(e) => setAdditionalPropertyCount(e.target.value as PropertyCount)}
                >
                  {PROPERTY_COUNT_OPTIONS.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{opt.label}</Typography>}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}

            {/* Relationship (only shown for joint borrowers) */}
            {inputs.includeAdditionalBorrower && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  Relationship
                </Typography>
                <RadioGroup
                  row
                  value={absdRelationship}
                  onChange={(e) => setAbsdRelationship(e.target.value)}
                >
                  {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => (
                    <FormControlLabel
                      key={key}
                      value={key}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                  ))}
                </RadioGroup>
                <Typography variant="caption" sx={{ display: 'block', color: absdRemissionApplied ? (isDark ? '#4ade80' : '#2e7d32') : 'text.secondary', mt: 0.5 }}>
                  {isMarriedCouple
                    ? absdRemissionApplied
                      ? `Married couple remission applied — ABSD reduced from ${absdRateBeforeRemission}% to ${absdRate}% (saving ${fmt(absdRemissionAmount)}).`
                      : 'Married joint purchase — ABSD follows IRAS joint-purchase tables for married couples (based on each spouse’s profile), not only the SC spouse’s rate.'
                    : 'For joint purchases, ABSD is based on the buyer profile with the higher rate.'}
                </Typography>
              </Box>
            )}

            {/* Save Changes to Profile */}
            {profileHasChanges && (
              <Box data-tutorial="save-profile" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: cardBorder }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SaveIcon />}
                  disabled={savingToProfile}
                  onClick={handleSaveToProfile}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {savingToProfile ? 'Saving…' : `Save changes to "${getSelectedName()}"`}
                </Button>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, textAlign: 'center' }}>
                  Updates the profile with current age, income, debt, citizenship, and property values
                </Typography>
              </Box>
            )}
          </Card>
          </Box>
        </Grid>

        {/* ── RIGHT: Results ────────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...(columnScrollSx ?? {}) }}>

            {/* Affordability Overview */}
            <Accordion data-tutorial="affordability-overview" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Affordability Overview
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              {/* Shared basis — every column uses the same income, LTV and stress rate; tenure varies by property type */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {results.basis.hdb.hdbLoan ? 'Basis:' : 'Same basis for all:'}
                </Typography>
                <Chip size="small" variant="outlined" label={`Income ${fmt(results.totalRecognisedIncome)}/mth`} />
                <Chip size="small" variant="outlined" label={`${results.basis.hdb.hdbLoan ? 'Private & EC: ' : ''}Applied LTV ${results.appliedLtv}%`} />
                <Chip size="small" variant="outlined" label={`${stressTestRateLabel} ${stressTestRate}%`} />
                {results.basis.hdb.hdbLoan && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`HDB loan: LTV ${results.basis.hdb.ltv}% · HDB floor ${results.basis.hdb.stressRate}%`}
                    sx={{ borderColor: hdbAccent, color: hdbAccent }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  mb: 1.5,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: alpha(selectedAccent, 0.08),
                  borderLeft: `3px solid ${selectedAccent}`,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: '1rem', mt: '2px', color: selectedAccent }} />
                <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
                  <strong>Tap Private, EC or HDB</strong> to see its full calculation below — property summary, income
                  support, cash &amp; mortgage. Now showing: <strong style={{ color: selectedAccent }}>{OVERVIEW_TYPE_LABELS[selectedType]}</strong>.
                  {' '}Tap any figure for its working.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  columnGap: { xs: 0.75, sm: 2 },
                }}
              >
                {/* ── Private: TDSR ── */}
                <RuleSection
                  selected={selectedType === 'private'}
                  onSelect={() => selectType('private')}
                  title="Private"
                  ratioLabel="TDSR 55%"
                  appliesTo="55% of income less existing debt"
                  accent={tdsrAccent}
                  cardBorder={cardBorder}
                >
                  <ResultCard
                    dense
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label={privatePriceTarget ? 'Max Property Price (Target)' : 'Max Property Price'}
                    value={fmt(privatePriceTarget ? targetPropertyPrice : results.maxPropertyPrice)}
                    onEdit={() => handleOpenEditPrice('private')}
                    editActive={privatePriceTarget}
                    formula={privatePriceTarget ? undefined : {
                      title: 'Max Property Price (Private)',
                      lines: [
                        'Max Property Price = Max Loan / LTV',
                        `${fmt(results.maxLoan)} / ${inputs.ltv}%`,
                        `= ${fmt(results.maxPropertyPrice)}`,
                      ],
                    }}
                  />
                  <ResultCard
                    dense
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label={privateLoanTarget ? 'Max Loan (Target)' : 'Max Loan'}
                    value={fmt(results.maxLoan)}
                    onEdit={() => handleOpenEditLoan('private')}
                    editActive={privateLoanTarget}
                    formula={{
                      title: 'Max Loan (Private)',
                      lines: buildMaxLoanLines('Available TDSR', results.availableTDSR, results.effectiveLoanTenure, results.maxLoan, results.basis.private),
                    }}
                  />
                  <ResultCard
                    dense
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Loan Tenure"
                    value={`${results.effectiveLoanTenure} yrs`}
                    formula={{
                      title: 'Loan Tenure (Private)',
                      lines: buildTenureLines('Private', results.effectiveLoanTenure, results.basis.private),
                    }}
                  />
                  <ResultCard dense cardBg={cardBg} cardBorder={cardBorder} label="Gross TDSR (55% of income)" value={fmt(results.grossTDSR)}
                    formula={{
                      title: 'Gross TDSR (55%)',
                      lines: [
                        'Gross TDSR = Recognised Income x 55%',
                        `${fmt(results.totalRecognisedIncome)} x 55%`,
                        `= ${fmt(results.grossTDSR)}`,
                      ],
                    }}
                  />
                  <ResultCard dense cardBg={cardBg} cardBorder={cardBorder} label="Monthly Instalment Cap (− Existing Debt)" value={fmt(results.availableTDSR)}
                    formula={{
                      title: 'Available TDSR',
                      lines: [
                        'Available TDSR = Gross TDSR - Existing Debt',
                        `${fmt(results.grossTDSR)} - ${fmt(results.totalExistingDebt)}`,
                        `= ${fmt(results.availableTDSR)}`,
                      ],
                    }}
                  />
                  <ResultCard
                    dense
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Affordable Today (with Available Funds)"
                    value={fmt(results.affordableToday)}
                    highlight={results.totalFundsAvailable > 0 ? tdsrAccent : undefined}
                    helperText={affordableTodayHelper(results.basis.private)}
                    formula={{
                      title: 'Affordable Today (Private)',
                      lines: buildAffordableTodayLines(results.maxLoan, 'Max Loan', results.affordableToday, results.basis.private),
                    }}
                  />
                </RuleSection>

                {/* ── EC / HDB: MSR (TDSR also applies) ── */}
                {renderMsrSection('ec', msrAccent, results.ec)}
                {renderMsrSection('hdb', hdbAccent, results.hdb)}
              </Box>
              </AccordionDetails>
            </Accordion>

            {/* Borrower Profile */}
            <Accordion data-tutorial="borrower-profile" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Borrower Profile
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Total Recognised Income" value={fmt(results.totalRecognisedIncome)}
                    formula={{
                      title: 'Total Recognised Income',
                      lines: [
                        inputs.primary.isSelfEmployed
                          ? `Primary: ${fmt(inputs.primary.monthlyIncome)} x 70% = ${fmt(getRecognisedIncome(inputs.primary))}`
                          : `Primary: ${fmt(inputs.primary.monthlyIncome)} (100%)`,
                        ...(inputs.includeAdditionalBorrower
                          ? [inputs.additional.isSelfEmployed
                              ? `Additional: ${fmt(inputs.additional.monthlyIncome)} x 70% = ${fmt(getRecognisedIncome(inputs.additional))}`
                              : `Additional: ${fmt(inputs.additional.monthlyIncome)} (100%)`]
                          : []),
                        '',
                        `= ${fmt(results.totalRecognisedIncome)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Total Raw Income" value={fmt(results.totalRawIncome)}
                    formula={{
                      title: 'Total Raw Income',
                      lines: [
                        `Primary: ${fmt(inputs.primary.monthlyIncome)}`,
                        ...(inputs.includeAdditionalBorrower
                          ? [`Additional: ${fmt(inputs.additional.monthlyIncome)}`]
                          : []),
                        '',
                        `= ${fmt(results.totalRawIncome)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Total Existing Debt" value={fmt(results.totalExistingDebt)}
                    formula={{
                      title: 'Total Existing Debt',
                      lines: [
                        `Primary: ${fmt(inputs.primary.existingMonthlyDebt)}`,
                        ...(inputs.includeAdditionalBorrower
                          ? [`Additional: ${fmt(inputs.additional.existingMonthlyDebt)}`]
                          : []),
                        '',
                        `= ${fmt(results.totalExistingDebt)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Effective Age Used" value={`${results.effectiveAgeUsed} yrs`}
                    formula={{
                      title: 'Effective Age Used',
                      lines: inputs.includeAdditionalBorrower
                        ? [
                            'Uses IWAA for joint borrowers:',
                            `ceil( (${inputs.primary.age} x ${fmt(getRecognisedIncome(inputs.primary))} + ${inputs.additional.age} x ${fmt(getRecognisedIncome(inputs.additional))}) / (${fmt(getRecognisedIncome(inputs.primary))} + ${fmt(getRecognisedIncome(inputs.additional))}) )`,
                            `= ${results.effectiveAgeUsed} yrs`,
                          ]
                        : [
                            'Single borrower: uses primary age.',
                            `= ${results.effectiveAgeUsed} yrs`,
                          ],
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Borrower Count" value={`${results.borrowerCount}`} />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="IWAA" value={`${results.iwaa} yrs`}
                    formula={{
                      title: 'IWAA (Income Weighted Average Age)',
                      lines: inputs.includeAdditionalBorrower
                        ? [
                            'IWAA = ceil( Sum(Age_i x Income_i) / Sum(Income_i) )',
                            '',
                            `Primary: age ${inputs.primary.age}, income ${fmt(getRecognisedIncome(inputs.primary))}`,
                            `Additional: age ${inputs.additional.age}, income ${fmt(getRecognisedIncome(inputs.additional))}`,
                            '',
                            `ceil( (${inputs.primary.age} x ${fmt(getRecognisedIncome(inputs.primary))} + ${inputs.additional.age} x ${fmt(getRecognisedIncome(inputs.additional))}) / ${fmt(results.totalRecognisedIncome)} )`,
                            `= ${results.iwaa} yrs`,
                          ]
                        : [
                            'Single borrower: IWAA equals primary age.',
                            `= ${results.iwaa} yrs`,
                          ],
                    }}
                  />
                </Grid>
              </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Selected Property Summary */}
            <Accordion data-tutorial="selected-property" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Selected Property Summary
                </Typography>
                {renderTypeChip()}
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              {renderTypeSwitch()}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Property Price" value={fmt(results.propertyPrice)}
                    helperText={propertyPriceUserAdjusted ? undefined : `Follows the ${OVERVIEW_TYPE_LABELS[selectedType]} max price — set your own under Inputs → Purchase & Funding.`}
                    formula={{
                      title: 'Property Price',
                      lines: propertyPriceUserAdjusted
                        ? [
                            'Set under Inputs → Purchase & Funding.',
                            '',
                            `= ${fmt(results.propertyPrice)}`,
                          ]
                        : [
                            `Follows the ${OVERVIEW_TYPE_LABELS[selectedType]} Max Property Price until you set your own`,
                            'under Inputs → Purchase & Funding.',
                            '',
                            `= ${fmt(results.propertyPrice)}`,
                          ],
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Required Loan" value={fmt(sel.requiredLoan)}
                    formula={{
                      title: 'Required Loan',
                      lines: [
                        `Required Loan = Property Price x LTV${selBasis.hdbLoan ? ' (HDB loan)' : ''}`,
                        `${fmt(results.propertyPrice)} x ${selBasis.ltv}%`,
                        `= ${fmt(sel.requiredLoan)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label={`Loan Shortfall (vs ${OVERVIEW_TYPE_LABELS[selectedType]} Max Loan ${fmt(sel.maxLoan)})`}
                    value={fmt(sel.loanShortfall)}
                    highlight={sel.loanShortfall > 0 ? (isDark ? '#f87171' : '#c62828') : undefined}
                    helperText={sel.loanShortfall > 0
                      ? `Bridge it with more cash, or with the income support below (${selectedRuleLabel}).`
                      : undefined}
                    formula={{
                      title: `Loan Shortfall (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                      lines: [
                        `Loan Shortfall = max(0, Required Loan - Max Loan (${OVERVIEW_TYPE_LABELS[selectedType]}))`,
                        `max(0, ${fmt(sel.requiredLoan)} - ${fmt(sel.maxLoan)})`,
                        `= ${fmt(sel.loanShortfall)}`,
                        '',
                        selectedType === 'private'
                          ? `Max Loan (Private) is sized on Available TDSR ${fmt(sel.instalmentCap)}/mth over ${sel.loanTenure} yrs.`
                          : `Max Loan (${OVERVIEW_TYPE_LABELS[selectedType]}) is sized on the lower of MSR / TDSR, ${fmt(sel.instalmentCap)}/mth, over ${sel.loanTenure} yrs.`,
                      ],
                    }}
                  />
                </Grid>
              </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Income Support Needed to Pass TDSR */}
            <Accordion data-tutorial="income-support" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Income Support Needed to Pass {selectedRuleLabel}
                </Typography>
                {renderTypeChip()}
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2, lineHeight: 1.5 }}>
                Full amounts for each way to cover the income-support gap: unpledged assets (30% recognition) or pledged deposits (100% over 48 months).
                Model a mix under <strong>Inputs → Income-support funds</strong>.
                {selectedType !== 'private' && (
                  <> {OVERVIEW_TYPE_LABELS[selectedType]} loans must pass both MSR (30%) and TDSR (55%), so the larger top-up applies.</>
                )}
              </Typography>

              {/* Instalment on the selected property vs the extra income each ratio needs */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={selectedType === 'private' ? 6 : 4}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Instalment at stress rate"
                    value={fmt(sel.stressInstalment)}
                    formula={{
                      title: 'Instalment Assessed (stress test)',
                      lines: [
                        'PMT = P x [r(1+r)^n] / [(1+r)^n - 1]',
                        '',
                        `P (Required Loan) = ${fmt(sel.requiredLoan)}`,
                        `r (${selBasis.stressRateLabel}) = ${selBasis.stressRate}% / 12`,
                        `n (${OVERVIEW_TYPE_LABELS[selectedType]} tenure) = ${sel.loanTenure} x 12 = ${sel.loanTenure * 12}`,
                        '',
                        `= ${fmt(sel.stressInstalment)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={selectedType === 'private' ? 6 : 4}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Extra income (TDSR)"
                    value={fmt(sel.tdsrExtraIncome)}
                    highlight={sel.supportRule === 'TDSR' && sel.extraMonthlyIncomeNeeded > 0 ? selectedAccent : undefined}
                    formula={{
                      title: 'Extra Income Needed — TDSR',
                      lines: [
                        'Extra Income = max(0, Instalment - Available TDSR) / 55%',
                        `max(0, ${fmt(sel.stressInstalment)} - ${fmt(results.availableTDSR)}) / 55%`,
                        `= ${fmt(sel.tdsrExtraIncome)} / month`,
                      ],
                    }}
                  />
                </Grid>
                {selectedType !== 'private' && (
                  <Grid item xs={4}>
                    <ResultCard
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      label="Extra income (MSR)"
                      value={fmt(sel.msrExtraIncome)}
                      highlight={sel.supportRule === 'MSR' && sel.extraMonthlyIncomeNeeded > 0 ? selectedAccent : undefined}
                      formula={{
                        title: 'Extra Income Needed — MSR',
                        lines: [
                          'Extra Income = max(0, Instalment - Gross MSR) / 30%',
                          `max(0, ${fmt(sel.stressInstalment)} - ${fmt(results[selectedType].grossMSR)}) / 30%`,
                          `= ${fmt(sel.msrExtraIncome)} / month`,
                          '',
                          `Gross MSR = 30% x ${fmt(results.totalRecognisedIncome)}`,
                        ],
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary' }}>
                Full (either route covers 100% of the gap)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
                <Grid item xs={12} md={5}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Full Unpledged Funds Needed"
                    value={fmt(sel.unpledgedFundsNeeded)}
                    highlight={sel.unpledgedFundsNeeded > 0 ? (isDark ? '#fbbf24' : '#e65100') : undefined}
                    formula={{
                      title: `Full Unpledged Funds Needed (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                      lines: [
                        'Unpledged assets: 30% recognition after haircut.',
                        `Derived from the same ${sel.supportRule} gap as full pledged (monthly pay at shared Stress Test rate, like max loan).`,
                        '',
                        'Unpledged Funds = Pledged Funds / 30%',
                        `${fmt(sel.pledgedFundsNeeded)} / 30%`,
                        `= ${fmt(sel.unpledgedFundsNeeded)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: 'auto', md: 80 },
                  }}
                >
                  <Typography variant="body1" fontWeight={700} color="text.secondary">
                    or
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Full Pledged Funds Needed"
                    value={fmt(sel.pledgedFundsNeeded)}
                    highlight={sel.pledgedFundsNeeded > 0 ? (isDark ? '#4ade80' : '#2e7d32') : undefined}
                    formula={{
                      title: `Full Pledged Funds Needed (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                      lines: [
                        'Pledged deposits: 100% recognition over 48 months.',
                        `Extra payment is the loan installment at the shared Stress Test rate (${selectedRuleLabel} test), not the bank’s offered rate.`,
                        '',
                        ...(selectedType === 'private'
                          ? [`Extra Monthly Income Needed (TDSR) = ${fmt(sel.tdsrExtraIncome)}`]
                          : [
                              `Extra Monthly Income Needed = max(TDSR ${fmt(sel.tdsrExtraIncome)}, MSR ${fmt(sel.msrExtraIncome)})`,
                              `= ${fmt(sel.extraMonthlyIncomeNeeded)} (${sel.supportRule} needs more)`,
                            ]),
                        'Pledged Funds = Extra Monthly Income x 48 months',
                        `${fmt(sel.extraMonthlyIncomeNeeded)} x 48`,
                        '',
                        `= ${fmt(sel.pledgedFundsNeeded)}`,
                      ],
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary' }}>
                Adjusted (your unpledged amount from inputs)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Adjusted Unpledged Fund"
                    value={fmt(effectiveUnpledgedAmount)}
                    highlight={effectiveUnpledgedAmount > 0 ? (isDark ? '#fbbf24' : '#e65100') : undefined}
                    formula={{
                      title: 'Adjusted Unpledged Fund',
                      lines: [
                        'Amount you plan to cover with unpledged assets (Inputs → Unpledged funds), capped at full unpledged need.',
                        '',
                        `Full unpledged need = ${fmt(sel.unpledgedFundsNeeded)}`,
                        `Adjusted unpledged = ${fmt(effectiveUnpledgedAmount)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Implied Pledged Fund"
                    value={fmt(effectivePledgedAmount)}
                    highlight={effectivePledgedAmount > 0 ? (isDark ? '#4ade80' : '#2e7d32') : undefined}
                    formula={{
                      title: 'Implied Pledged Fund',
                      lines: [
                        'Pledged deposits implied after applying 30% recognition on your adjusted unpledged amount.',
                        '',
                        `Recognised from unpledged = ${fmt(effectiveUnpledgedAmount)} × ${UNPLEDGED_RECOGNITION * 100}% = ${fmt(Math.round(effectiveUnpledgedAmount * UNPLEDGED_RECOGNITION))}`,
                        `Implied pledge = Full pledged need − recognised portion (rounded, capped)`,
                        `= ${fmt(effectivePledgedAmount)}`,
                      ],
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label="Total funds required (adjusted mix)"
                    value={fmt(adjustedTotalFundsRequired)}
                    highlight={adjustedTotalFundsRequired > 0 ? 'primary.main' : undefined}
                    formula={{
                      title: 'Total funds required (adjusted mix)',
                      lines: [
                        'Total capital you model using unpledged assets plus the implied top-up in pledged form.',
                        '',
                        `Adjusted unpledged + Implied pledge`,
                        `${fmt(effectiveUnpledgedAmount)} + ${fmt(effectivePledgedAmount)}`,
                        `= ${fmt(adjustedTotalFundsRequired)}`,
                      ],
                    }}
                  />
                </Grid>
              </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Cash & Mortgage Requirements */}
            <Accordion data-tutorial="cash-mortgage" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Cash & Mortgage Requirements
                </Typography>
                {renderTypeChip()}
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              {/* Two independent columns: a tall card (helper text) in one column no
                  longer leaves dead space beside a short card in the other. */}
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ResultCard
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      label="Minimum Cash Needed"
                      value={fmt(sel.minimumCashNeeded)}
                      helperText="This figure does not yet include any loan shortfall, any cash required for pledging / unpledging, or other funding gaps arising from bank assessment or loan structuring."
                      formula={{
                        title: `Minimum Cash Needed (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                        lines: [
                          ...(selBasis.hdbLoan
                            ? [
                                'HDB loan: no cash-only minimum — CPF can pay the down payment.',
                                `Only the OTP deposit (Option ${fmt(HDB_OPTION_FEE)} + Exercise ${fmt(HDB_EXERCISE_FEE)}) is paid in cash.`,
                              ]
                            : [
                                selBasis.ltv === 55
                                  ? 'First 10% of property price must be in cash (MAS, 55% LTV / subsequent loan).'
                                  : 'First 5% of property price must be in cash (MAS).',
                                '',
                                `${fmt(results.propertyPrice)} x ${minCashPct}% = ${fmt(Math.round(results.propertyPrice * selBasis.cashPercent))}`,
                                ...(isHdbSelected
                                  ? [
                                      `HDB resale: the OTP deposit (Option ${fmt(HDB_OPTION_FEE)} + Exercise ${fmt(HDB_EXERCISE_FEE)})`,
                                      'is paid in cash and counts towards it — the higher of the two applies.',
                                    ]
                                  : []),
                              ]),
                          '',
                          `= ${fmt(sel.minimumCashNeeded)}`,
                        ],
                      }}
                    />
                    <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Effective Loan Tenure" value={`${sel.loanTenure} yrs`}
                      formula={{
                        title: `Effective Loan Tenure (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                        lines: buildTenureLines(OVERVIEW_TYPE_LABELS[selectedType], sel.loanTenure, selBasis),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Monthly mortgage (required based on property price & LTV)" value={fmt(sel.monthlyMortgageRequired)}
                      formula={{
                        title: `Monthly Mortgage Payment (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                        lines: [
                          'PMT = P x [r(1+r)^n] / [(1+r)^n - 1]',
                          '',
                          `P (Required Loan) = ${fmt(sel.requiredLoan)}`,
                          `r (monthly rate from ${selBasis.hdbLoan ? 'HDB Concessionary Rate' : effectiveInterestRateLabel}) = ${selBasis.interestRate}% / 12 = ${(selBasis.interestRate / 12).toFixed(4)}%`,
                          `n (months) = ${sel.loanTenure} x 12 = ${sel.loanTenure * 12}`,
                          '',
                          `= ${fmt(sel.monthlyMortgageRequired)}`,
                        ],
                      }}
                    />
                    {/* Single bottom-line card: the grand total when pledged / unpledged
                        funds are in play, otherwise the initial outlay itself. Tapping it
                        opens the full itemised working. */}
                    <ResultCard
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      label={adjustedTotalFundsRequired > 0 ? 'Grand Total Outlay (incl. fund pledging)' : 'Total Initial Outlay'}
                      value={fmt(grandTotalOutlay)}
                      highlight="primary.main"
                      helperText={[
                        isHdbSelected
                          ? `Down payment ${upfrontDownPaymentPct}% (Option Fee + Exercise Fee + balance at completion) + BSD${absdAmount > 0 ? ' + ABSD' : ''} + legal and other fees${adjustedTotalFundsRequired > 0 ? ', plus the pledged / unpledged funds bridging the loan shortfall' : ''}.`
                          : `Down payment ${upfrontDownPaymentPct}% (OTP ${OTP_STAGE_PCT}% + Pay ${SP_STAGE_PCT}%) + BSD${absdAmount > 0 ? ' + ABSD' : ''} + conveyancing${adjustedTotalFundsRequired > 0 ? ', plus the pledged / unpledged funds bridging the loan shortfall' : ''}.`,
                        ...(deferredDownPaymentPct > 0
                          ? [`Excludes the remaining ${deferredDownPaymentPct}% down payment due later in the progressive schedule.`]
                          : []),
                        'Tap for the full breakdown.',
                      ].join(' ')}
                      formula={{
                        title: `${adjustedTotalFundsRequired > 0 ? 'Grand Total Outlay' : 'Total Initial Outlay'} (${OVERVIEW_TYPE_LABELS[selectedType]})`,
                        lines: [
                          `Cash and CPF payable by the buyer: the ${upfrontDownPaymentPct}% down payment, stamp duty and fees (Buying Calculator defaults).`,
                          '',
                          `DOWN PAYMENT (${upfrontDownPaymentPct}%${isHdbSelected ? ', HDB resale' : selectedType === 'ec' ? ', EC Normal Payment Scheme' : ''})`,
                          ...downPaymentStages.map((stage) => `${stage.label} = ${fmt(stage.amount)}`),
                          `Subtotal = ${fmt(upfrontDownPayment)}`,
                          '',
                          'STAMP DUTY',
                          `BSD = ${fmt(bsdAmount)}`,
                          ...(absdAmount > 0
                            ? [`ABSD (${absdRate}%) = ${fmt(absdAmount)}`]
                            : ['ABSD = not applicable']),
                          `Subtotal = ${fmt(totalStampDuty)}`,
                          '',
                          'LEGAL FEE',
                          ...legalFees.map((fee) => `${fee.label} = ${fmt(fee.amount)}`),
                          ...(legalFees.length > 1 ? [`Subtotal = ${fmt(totalLegalFees)}`] : []),
                          ...(otherFees.length > 0
                            ? [
                                '',
                                'OTHER FEES',
                                ...otherFees.map((fee) => `${fee.label} = ${fmt(fee.amount)}`),
                                `Subtotal = ${fmt(totalOtherFees)}`,
                              ]
                            : []),
                          '',
                          `= Total Initial Outlay  ${fmt(initialOutlay)}`,
                          ...(adjustedTotalFundsRequired > 0
                            ? [
                                '',
                                'ADDITIONAL FUNDS REQUIRED',
                                ...(effectiveUnpledgedAmount > 0
                                  ? [`Unpledged Amount = ${fmt(effectiveUnpledgedAmount)}`]
                                  : []),
                                ...(effectivePledgedAmount > 0
                                  ? [`Pledged Amount = ${fmt(effectivePledgedAmount)}`]
                                  : []),
                                '',
                                `= Grand Total Outlay  ${fmt(grandTotalOutlay)}`,
                              ]
                            : []),
                          '',
                          `Funding split of the ${upfrontDownPaymentPct}% down payment:`,
                          `Cash only (min ${minCashPct}%) = ${fmt(upfrontCashOnly)}`,
                          ...(upfrontFlexible > 0
                            ? [`CPF / Cash (${upfrontFlexiblePct}%) = ${fmt(upfrontFlexible)}`]
                            : []),
                          ...(deferredDownPayment > 0
                            ? [
                                '',
                                `Excludes the remaining ${deferredDownPaymentPct}% down payment (${fmt(deferredDownPayment)}), due later in the progressive schedule once the bank starts disbursing.`,
                              ]
                            : []),
                          '',
                          'Excludes HPS and fire insurance premiums. Fees use the Buying Calculator defaults.',
                        ],
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Stamp Duty Payable */}
            <Accordion data-tutorial="stamp-duty-payable" defaultExpanded variant="outlined" sx={{ bgcolor: 'background.paper', color: 'text.primary', borderColor: cardBorder, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight={700}>
                  Stamp Duty Payable
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              {renderStampDutyMultiBorrowerNote()}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="BSD" value={fmt(bsdAmount)}
                    formula={{
                      title: "Buyer's Stamp Duty (BSD)",
                      lines: (() => {
                        const breakdown = getBsdBreakdown(results.propertyPrice);
                        return [
                          'Progressive rates (IRAS, effective 15 Feb 2023):',
                          ...breakdown.map((t) => `• ${t.bracket}`),
                          '',
                          `Property Price: ${fmt(results.propertyPrice)}`,
                          `= ${breakdown.map((t) => `${fmt(t.taxable)} × ${Math.round(t.rate * 100)}%`).join(' + ')}`,
                          `= ${breakdown.map((t) => fmt(t.duty)).join(' + ')}`,
                          `= ${fmt(bsdAmount)}`,
                        ];
                      })(),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ResultCard
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    label={`ABSD (${absdRate}%)${absdRemissionApplied ? ' ✓ Remission' : ''}`}
                    value={fmt(absdAmount)}
                    highlight={absdRemissionApplied ? (isDark ? '#4ade80' : '#2e7d32') : absdAmount > 0 ? 'error.main' : undefined}
                    formula={{
                      title: 'Additional Buyer\'s Stamp Duty (ABSD)',
                      lines: (() => {
                        const lines = [
                          'ABSD Rates (effective 27 Apr 2023, IRAS):',
                          '',
                          'Singapore Citizen:  1st 0% | 2nd 20% | 3rd+ 30%',
                          'Singapore PR:       1st 5% | 2nd 30% | 3rd+ 35%',
                          'Foreigner:          1st 60% | 2nd 60% | 3rd+ 60%',
                          '',
                          `Borrower 1: ${CITIZENSHIP_LABELS[primaryCitizenship]}${primaryFtaEligible ? ' (FTA → SC ABSD rates)' : ''}, ${primaryPropertyCount === '3rd+' ? '≥3rd' : primaryPropertyCount} Property → ${primaryAbsdRate}%`,
                        ];
                        if (primaryFtaEligible || additionalFtaEligible) {
                          lines.push(
                            '',
                            'FTA: Where indicated, SC-equivalent ABSD rates apply after qualifying for an FTA remission (subject to IRAS approval).',
                          );
                        }
                        if (inputs.includeAdditionalBorrower) {
                          lines.push(`Borrower 2: ${CITIZENSHIP_LABELS[additionalCitizenship]}${additionalFtaEligible ? ' (FTA → SC ABSD rates)' : ''}, ${additionalPropertyCount === '3rd+' ? '≥3rd' : additionalPropertyCount} Property → ${additionalAbsdRate}%`);
                          if (absdRemissionApplied) {
                            lines.push(
                              '',
                              '── Married Couple Remission ──',
                              'Where at least one spouse is a Singapore Citizen,',
                              'ABSD is based on the SC spouse\'s rate.',
                              '',
                              `Before remission: ${absdRateBeforeRemission}% → ${fmt(absdAmountBeforeRemission)}`,
                              `After remission:  ${absdRate}% → ${fmt(absdAmount)}`,
                              `Remission savings: ${fmt(absdRemissionAmount)}`,
                            );
                          } else if (isMarriedCouple) {
                            lines.push('', `Married couple — no remission applicable (both rates equal or no SC spouse).`);
                            lines.push(`Applied Rate: ${absdRate}%`);
                          } else {
                            lines.push('', `Applied Rate: higher of ${primaryAbsdRate}% and ${additionalAbsdRate}% = ${absdRate}%`);
                          }
                        } else {
                          lines.push(`ABSD Rate: ${absdRate}%`);
                        }
                        lines.push('', `= ${fmt(results.propertyPrice)} × ${absdRate}%`, `= ${fmt(absdAmount)}`);
                        return lines;
                      })(),
                    }}
                  />
                </Grid>
                {absdRemissionApplied && (
                  <Grid item xs={6}>
                    <ResultCard
                      cardBg={cardBg}
                      cardBorder={cardBorder}
                      label="ABSD Remission (Married Couple)"
                      value={`-${fmt(absdRemissionAmount)}`}
                      highlight={isDark ? '#4ade80' : '#2e7d32'}
                      formula={{
                        title: 'ABSD Remission for Married Couple',
                        lines: [
                          'Eligible married couples (at least one SC spouse)',
                          'can apply for ABSD remission.',
                          '',
                          `Without remission: ${absdRateBeforeRemission}% × ${fmt(results.propertyPrice)} = ${fmt(absdAmountBeforeRemission)}`,
                          `With remission:    ${absdRate}% × ${fmt(results.propertyPrice)} = ${fmt(absdAmount)}`,
                          '',
                          `Remission: ${fmt(absdRemissionAmount)}`,
                          '',
                          'Source: IRAS — Remission of ABSD for a Married Couple',
                        ],
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <ResultCard cardBg={cardBg} cardBorder={cardBorder} label="Total Stamp Duty" value={fmt(totalStampDuty)}
                    formula={{
                      title: 'Total Stamp Duty',
                      lines: (() => {
                        const lines = [
                          'Total Stamp Duty = BSD + ABSD',
                          '',
                          `BSD: ${fmt(bsdAmount)}`,
                          `ABSD: ${fmt(absdAmount)}`,
                        ];
                        if (absdRemissionApplied) {
                          lines.push(`(After married couple remission of ${fmt(absdRemissionAmount)})`);
                        }
                        lines.push('', `= ${fmt(bsdAmount)} + ${fmt(absdAmount)}`, `= ${fmt(totalStampDuty)}`);
                        return lines;
                      })(),
                    }}
                  />
                </Grid>
              </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Footer */}
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1.7, fontSize: '0.8rem' }}
            >
              {inputs.includeAdditionalBorrower
                ? `Joint borrower mode is enabled. Loan tenure is based on the Income Weighted Average Age (IWAA) of ${results.iwaa} years. TDSR uses the combined recognised income and debt of both borrowers.`
                : `Single borrower mode is enabled. Loan tenure is based on the primary borrower's age. TDSR uses the primary borrower's recognised income and debt only.`}
              {(inputs.primary.isSelfEmployed || (inputs.includeAdditionalBorrower && inputs.additional.isSelfEmployed))
                ? ' Self-employed income applies a 30% haircut when selected.'
                : ''}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.7rem', fontStyle: 'italic' }}
            >
              Disclaimer: This calculator is provided for general guidance and planning purposes only.
              All figures are estimates and subject to change. Final loan eligibility, tenure, and amounts
              are subject to verification and approval by qualified financial institutions.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ── Tutorial ── */}
      <CalculatorTutorial
        controller={tutorial}
        title="Max Affordability Calculator Tutorial"
        manualName="Max Affordability Calculator"
        steps={MAXIMIZER_TUTORIAL_STEPS}
        manualExtraSections={MAXIMIZER_MANUAL_SECTIONS}
      />

      {/* ── What's New ── */}
      <WhatsNewDialog controller={whatsNew} />

      {/* ── Clear Calculator Confirmation Dialog ── */}
      {/* Where a derived Available Funds row came from */}
      <Dialog open={!!fundsDialog} onClose={() => setFundsDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{fundsDialog?.title}</DialogTitle>
        <DialogContent>
          {(fundsDialog?.lines ?? []).map((line, i) => (
            <Typography
              key={i}
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                lineHeight: 2,
                fontWeight: line.startsWith('=') ? 700 : 400,
                color: line.startsWith('=') ? 'primary.main' : 'text.primary',
                whiteSpace: 'pre-wrap',
              }}
            >
              {line}
            </Typography>
          ))}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5, lineHeight: 1.6 }}>
            Entered on each borrower&rsquo;s card — edit it there.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFundsDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartAltIcon color="error" />
          Reset Max Affordability Calculator?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will reset all Max Affordability Calculator values to their original defaults and clear the selected example or history entry.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved history entries will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button onClick={clearCalculator} variant="contained" color="error">
            Clear &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Export PDF: pick the property type(s) ───────────────────────── */}
      <Dialog open={pdfDialogOpen} onClose={() => { if (!pdfExporting) setPdfDialogOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Export PDF report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Which property type should the report cover?
          </Typography>
          <Box role="radiogroup" aria-label="Report property type" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {([...OVERVIEW_TYPES, 'all'] as const).map((choice) => {
              const active = pdfChoice === choice;
              const accent = choice === 'all' ? highlightColor : typeAccent[choice];
              const price = choice === 'all' ? null : buildPdfTypeReport(choice).maxPropertyPrice;
              // An ineligible type can still be exported on its own; the report stamps it "Not eligible".
              const ineligible = choice !== 'all' && !eligibility[choice].eligible;
              return (
                <Box
                  key={choice}
                  role="radio"
                  aria-checked={active}
                  tabIndex={0}
                  onClick={() => setPdfChoice(choice)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPdfChoice(choice); } }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: active ? accent : cardBorder,
                    borderLeft: `4px solid ${accent}`,
                    bgcolor: active ? alpha(accent, 0.08) : 'transparent',
                    boxShadow: active ? `0 0 0 1px ${accent}` : 'none',
                    cursor: 'pointer',
                    transition: 'background-color 150ms, box-shadow 150ms',
                    '&:hover': active ? {} : { bgcolor: alpha(accent, 0.04) },
                    '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                  }}
                >
                  {active
                    ? <CheckCircleIcon sx={{ color: accent }} />
                    : <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
                      {choice === 'all' ? 'All three — side by side' : OVERVIEW_TYPE_LABELS[choice]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {choice === 'all'
                        ? 'Private, EC and HDB compared, with a page for each'
                        : ineligible
                          ? `Not eligible — for reference · max price ${fmt(price ?? 0)}`
                          : `${choice === 'private' ? 'TDSR 55%' : 'MSR 30% + TDSR'} · max price ${fmt(price ?? 0)}`}
                    </Typography>
                  </Box>
                  {choice === selectedType && (
                    <Chip size="small" label="On screen" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                </Box>
              );
            })}
          </Box>
          {pdfExporting && <LinearProgress sx={{ mt: 2, height: 4, borderRadius: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfDialogOpen(false)} disabled={pdfExporting}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => void runPdfExport()}
            disabled={pdfExporting}
          >
            {pdfExporting ? 'Exporting…' : 'Export PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Max Loan Dialog ────────────────────────────────────────── */}
      <Dialog open={editLoanDialogOpen} onClose={() => setEditLoanDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Set Target Max Loan ({OVERVIEW_TYPE_LABELS[editTarget]})
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Enter your desired max loan amount. The calculator will back-calculate the monthly income needed to qualify
            {editTarget === 'private' ? ' under TDSR.' : ` for ${OVERVIEW_TYPE_LABELS[editTarget]} — both MSR and TDSR must pass, over the ${editTarget === 'hdb' ? results.hdb.loanTenure : results.effectiveLoanTenure}-year ${OVERVIEW_TYPE_LABELS[editTarget]} tenure.`}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Target Max Loan (SGD)"
            value={editLoanInput}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              setEditLoanInput(digits ? Number(digits).toLocaleString('en-SG') : '');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmEditLoan(); }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
            helperText={editLoanInput ? ' ' : 'e.g. 1,000,000'}
            inputProps={{ inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLoanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmEditLoan}>
            Calculate Income
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Max Property Price Dialog ─────────────────────────────── */}
      <Dialog open={editPriceDialogOpen} onClose={() => setEditPriceDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Set Target Max Property Price ({OVERVIEW_TYPE_LABELS[editTarget]})
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Enter your desired property price. The calculator will derive the required loan ({inputs.ltv}% LTV) and back-calculate the monthly income needed to qualify
            {editTarget === 'private' ? ' under TDSR.' : ` for ${OVERVIEW_TYPE_LABELS[editTarget]} — both MSR and TDSR must pass.`}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Target Property Price (SGD)"
            value={editPriceInput}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              setEditPriceInput(digits ? Number(digits).toLocaleString('en-SG') : '');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmEditPrice(); }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
            helperText={
              editPriceInput
                ? `Implied loan at ${inputs.ltv}% LTV: ${fmt(Math.round(Number(editPriceInput.replace(/[^0-9]/g, '')) * inputs.ltv / 100))}`
                : 'e.g. 1,500,000'
            }
            inputProps={{ inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPriceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmEditPrice}>
            Calculate Income
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Income Override Warning Dialog ───────────────────────────────── */}
      <Dialog open={incomeOverrideWarning.open} onClose={() => setIncomeOverrideWarning((p) => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Adjust Income?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            You're in <strong>Reverse Mode ({OVERVIEW_TYPE_LABELS[reverseTarget]} {reverseModeType === 'propertyPrice' ? 'Property Price → Income' : 'Max Loan → Income'})</strong>
            {' — '}
            {reverseModeType === 'propertyPrice'
              ? <>target Property Price: <strong>{new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(targetPropertyPrice)}</strong></>
              : <>target Max Loan: <strong>{new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(targetMaxLoan)}</strong></>
            }.
          </Typography>
          {inputs.includeAdditionalBorrower ? (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                What would you like to do?
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Lock{' '}
                  {incomeOverrideWarning.target === 'primary' ? 'Primary' : 'Additional'} income &amp; recalculate the other
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Stays in Reverse Mode. The{' '}
                  {incomeOverrideWarning.target === 'primary' ? 'Additional' : 'Primary'}{' '}
                  borrower's income adjusts automatically to keep the target loan.
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Exit Reverse Mode
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Uses your adjusted income directly and recalculates Max Loan from scratch.
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Adjusting income will <strong>exit Reverse Mode</strong> and Max Loan will be recalculated based on your new income.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 2, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setIncomeOverrideWarning((p) => ({ ...p, open: false }))}
            variant="outlined"
          >
            Stay in Estimated Mode
          </Button>
          {inputs.includeAdditionalBorrower && (
            <Button fullWidth variant="contained" color="warning" onClick={handleAdjustOtherBorrower}>
              Lock &amp; Recalculate{' '}
              {incomeOverrideWarning.target === 'primary' ? 'Additional' : 'Primary'} Borrower
            </Button>
          )}
          <Button fullWidth variant="contained" color="primary" onClick={handleExitAndApplyIncome}>
            Exit Reverse Mode
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AffordabilityCalculator;
